import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Boxes,
  CalendarRange,
  CheckCircle2,
  Download,
  RefreshCw,
  Search,
  TrendingUp,
} from 'lucide-react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import Header from './Header';
import TabIcon from './TabIcon';
import ModuleHelp from './ModuleHelp';
import './DemandForecastDashboard.css';
import { fmtDateTimeSecVN } from './timeZone';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';

const API_BASE = process.env.REACT_APP_DEMAND_FORECAST_API_BASE
  || '';

const FILTER_DEFAULTS = {
  horizonWeeks: 12,
  branches: [],
  channels: [],
  productCollections: [],
  productCategories: [],
  productTypes: [],
  frameModels: [],
  skus: [],
};

const SKU_DETAIL_LIMIT = 500;

// Values the code also compares or keys on — statuses, decisions and actions, some worked out on this screen, some
// sent by the server. The code keeps the English value; tv() (in the component) looks up only the label drawn on
// screen. A value not listed here (free text from the server, a model name) is shown as it is.
const VALUE_KEYS = {
  All: 'all',
  Review: 'review',
  Watch: 'watch',
  OK: 'ok',
  'Keep current': 'keepCurrent',
  'Increase forecast': 'increaseForecast',
  'Decrease forecast': 'decreaseForecast',
  Aligned: 'aligned',
  High: 'high',
  Medium: 'medium',
  Low: 'low',
  Helps: 'helps',
  Hurts: 'hurts',
  Neutral: 'neutral',
  Unknown: 'unknown',
  'Unknown reason': 'unknownReason',
  'Unknown risk': 'unknownRisk',
  'Review manually': 'reviewManually',
  'Needs data': 'needsData',
  'Keep baseline': 'keepBaseline',
  'Pilot candidate': 'pilotCandidate',
  'Review candidate': 'reviewCandidate',
  Hold: 'hold',
  'Low volume watch': 'lowVolumeWatch',
  'Review new demand': 'reviewNewDemand',
  'Review dropped demand': 'reviewDroppedDemand',
  'Manual review': 'manualReview',
  'Accept movement': 'acceptMovement',
  Pass: 'pass',
  'No run loaded': 'noRunLoaded',
  Ready: 'ready',
  Missing: 'missing',
  Done: 'done',
  Waiting: 'waiting',
  'Review first': 'reviewFirst',
  Proceed: 'proceed',
  'Policy ready': 'policyReady',
  'Rolling stable': 'rollingStable',
  'Not stable yet': 'notStableYet',
  'High overfit risk': 'highOverfitRisk',
  Promising: 'promising',
  'Promote review': 'promoteReview',
  'Watch only': 'watchOnly',
  'Production baseline': 'productionBaseline',
  'Rule wins': 'ruleWins',
  Close: 'close',
  'Feature helps': 'featureHelps',
  'Feature hurts': 'featureHurts',
  'Low volume': 'lowVolume',
  'No feature signal': 'noFeatureSignal',
  'Strong candidate': 'strongCandidate',
  'V3 beats current': 'v3Beats',
  'V3 worse than current': 'v3Worse',
};
const LEVEL_KEYS = ['total', 'branch_channel', 'sku'];

const nf = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const nf1 = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });

const formatQty = (value, digits = 0) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '-';
  return digits ? nf1.format(number) : nf.format(number);
};

const formatPercent = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '-';
  return `${(number * 100).toFixed(1)}%`;
};

const formatDateTime = (value) => fmtDateTimeSecVN(value, '-');

const weekLabel = (row) => `W${String(row?.target_week ?? row?.targetWeek ?? '').padStart(2, '0')}`;
const modelLabel = (value) => String(value || '-').replaceAll('_', ' ');

const csvValue = (value) => {
  const text = value === null || value === undefined ? '' : String(value);
  return `"${text.replaceAll('"', '""')}"`;
};

const downloadCsv = (fileName, columns, rows) => {
  const csv = [
    columns.map((column) => csvValue(column.label)).join(','),
    ...rows.map((row) => columns.map((column) => csvValue(column.value(row))).join(',')),
  ].join('\n');
  // The byte-order mark makes Excel read the file as UTF-8, so translated headings are not garbled.
  const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const fetchForecastJson = async (endpoint, body, signal) => {
  const response = await fetch(`${API_BASE}/api/demand-forecast/${endpoint}`, {
    method: body ? 'POST' : 'GET',
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });
  const text = await response.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch (error) {
    throw new Error(i18n.t('xDemand.msg.cannotParse', { endpoint }));
  }
  if (!response.ok) {
    throw new Error(data?.detail || data?.error || i18n.t('xDemand.msg.cannotLoad', { endpoint }));
  }
  return data;
};

const Stat = ({ icon: Icon, label, value, note }) => (
  <div className="df-stat">
    <div className="df-stat-icon"><Icon size={18} /></div>
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
      {note ? <em>{note}</em> : null}
    </div>
  </div>
);

const MultiSelect = ({ label, options = [], value = [], onChange, searchable = false }) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [query, setQuery] = useState('');
  const selected = value.length ? (value.length > 2 ? `${value.slice(0, 2).join(', ')} +${value.length - 2}` : value.join(', ')) : t('xDemand.v.all');
  const normalizedQuery = query.trim().toLowerCase();
  const visibleOptions = normalizedQuery
    ? options.filter((option) => String(option).toLowerCase().includes(normalizedQuery)).slice(0, 80)
    : options.slice(0, 80);

  useEffect(() => {
    const close = (event) => {
      if (ref.current?.open && !ref.current.contains(event.target)) {
        ref.current.removeAttribute('open');
        setQuery('');
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const toggle = (option) => {
    if (value.includes(option)) {
      onChange(value.filter((item) => item !== option));
    } else {
      onChange([...value, option]);
    }
  };

  return (
    <label className="df-field">
      <span>{label}</span>
      <details className="df-multi" ref={ref}>
        <summary title={selected}>{selected}</summary>
        <div className="df-multi-panel">
          {searchable && (
            <div className="df-search-row">
              <Search size={15} />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={label}
                type="search"
              />
            </div>
          )}
          <button type="button" className="df-option" onClick={() => onChange([])}>
            <input type="checkbox" checked={!value.length} readOnly />
            <span>{t('xDemand.v.all')}</span>
          </button>
          <div className="df-option-list">
            {visibleOptions.map((option) => (
              <button type="button" className="df-option" key={option} onClick={() => toggle(option)}>
                <input type="checkbox" checked={value.includes(option)} readOnly />
                <span>{option}</span>
              </button>
            ))}
            {!visibleOptions.length && <div className="df-empty">{t('xDemand.f.noMatches')}</div>}
          </div>
        </div>
      </details>
    </label>
  );
};

/* THE GUIDE (Max 01/11: every module has one). It walks the Forecast tab, the one the screen opens on; the tour switches
   back to it first. */
const TOUR = [
  { selector: '.df-actions', title: 'tourDemand.refreshT', body: 'tourDemand.refreshB' },
  { selector: '.df-filters', title: 'tourDemand.filtersT', body: 'tourDemand.filtersB' },
  { selector: '.df-stats', title: 'tourDemand.statsT', body: 'tourDemand.statsB' },
  { selector: '.df-tabs', title: 'tourDemand.tabsT', body: 'tourDemand.tabsB' },
  { selector: '.df-grid .df-chart', title: 'tourDemand.chartT', body: 'tourDemand.chartB' },
];

const DemandForecastDashboard = () => {
  const { t } = useTranslation();
  // The label of a value the code compares (see VALUE_KEYS); anything else is drawn as it is.
  const tv = (value) => (Object.prototype.hasOwnProperty.call(VALUE_KEYS, value) ? t(`xDemand.v.${VALUE_KEYS[value]}`) : value);
  const pts = (n) => t('xDemand.u.pts', { n });
  const levelLabel = (level) => (LEVEL_KEYS.includes(level) ? t(`xDemand.level.${level}`) : String(level || '').replace('_', ' '));
  const [filtersMeta, setFiltersMeta] = useState(null);
  const [filters, setFilters] = useState(FILTER_DEFAULTS);
  const [summary, setSummary] = useState(null);
  const [detail, setDetail] = useState(null);
  const [backtest, setBacktest] = useState(null);
  const [featureBacktest, setFeatureBacktest] = useState(null);
  const [runHistory, setRunHistory] = useState(null);
  const [activeTab, setActiveTab] = useState('forecast');
  const [candidateStatusFilter, setCandidateStatusFilter] = useState('All');
  const [candidateSkuStatusFilter, setCandidateSkuStatusFilter] = useState('All');
  const [recommendedOutputStatusFilter, setRecommendedOutputStatusFilter] = useState('All');
  const [recommendedOutputDirectionFilter, setRecommendedOutputDirectionFilter] = useState('All');
  const [recommendedOutputAlignmentFilter, setRecommendedOutputAlignmentFilter] = useState('All');
  const [recommendedOutputGroupFilter, setRecommendedOutputGroupFilter] = useState(null);
  const [recommendedOutputReasonFilter, setRecommendedOutputReasonFilter] = useState(null);
  const [featureReviewPriorityFilter, setFeatureReviewPriorityFilter] = useState('All');
  const [featureReviewRiskFilter, setFeatureReviewRiskFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshingData, setRefreshingData] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState('');
  const [error, setError] = useState('');
  const latestLoadRef = useRef(0);

  const payload = useMemo(() => ({
    horizonWeeks: filters.horizonWeeks,
    historyWeeks: 26,
    branches: filters.branches,
    channels: filters.channels,
    productCollections: filters.productCollections,
    productCategories: filters.productCategories,
    productTypes: filters.productTypes,
    frameModels: filters.frameModels,
    skus: filters.skus,
  }), [filters]);
  const payloadKey = useMemo(() => JSON.stringify(payload), [payload]);

  useEffect(() => {
    setCandidateStatusFilter('All');
    setCandidateSkuStatusFilter('All');
    setRecommendedOutputStatusFilter('All');
    setRecommendedOutputDirectionFilter('All');
    setRecommendedOutputAlignmentFilter('All');
    setRecommendedOutputGroupFilter(null);
    setRecommendedOutputReasonFilter(null);
    setFeatureReviewPriorityFilter('All');
    setFeatureReviewRiskFilter('All');
  }, [payloadKey]);

  const loadForecast = useCallback(async (signal) => {
    const loadId = latestLoadRef.current + 1;
    latestLoadRef.current = loadId;
    setError('');
    setSummary(null);
    setDetail(null);
    setBacktest(null);
    setFeatureBacktest(null);
    setRunHistory(null);
    const summaryData = await fetchForecastJson('summary', payload, signal);
    if (signal?.aborted || loadId !== latestLoadRef.current) return;
    setSummary(summaryData);

    const [detailResult, backtestResult, featureBacktestResult, runHistoryResult] = await Promise.allSettled([
      fetchForecastJson('detail', { ...payload, limit: SKU_DETAIL_LIMIT }, signal),
      fetchForecastJson('backtest', {
        branches: payload.branches,
        channels: payload.channels,
        skus: payload.skus,
      }, signal),
      fetchForecastJson('feature-backtest', payload, signal),
      fetchForecastJson('run-history', payload, signal),
    ]);
    if (signal?.aborted || loadId !== latestLoadRef.current) return;
    if (detailResult.status === 'fulfilled') {
      setDetail(detailResult.value);
    } else {
      console.warn('Demand forecast detail skipped:', detailResult.reason);
      setDetail({ meta: { totalRows: 0 }, rows: [], recommendedOutput: [], candidateSkuImpact: [] });
    }
    if (backtestResult.status === 'fulfilled') {
      setBacktest(backtestResult.value);
    } else {
      console.warn('Demand forecast backtest skipped:', backtestResult.reason);
      setBacktest({ accuracy: [], byWeek: [], modelComparison: [], modelComparisonByWeek: [] });
    }
    if (featureBacktestResult.status === 'fulfilled') {
      setFeatureBacktest(featureBacktestResult.value);
    } else {
      console.warn('Demand forecast feature backtest skipped:', featureBacktestResult.reason);
      setFeatureBacktest({ summary: [], byWeek: [] });
    }
    if (runHistoryResult.status === 'fulfilled') {
      setRunHistory(runHistoryResult.value);
    } else {
      console.warn('Demand forecast run history skipped:', runHistoryResult.reason);
      setRunHistory({ runs: [], comparison: {}, branchChannel: [], skuMovers: [] });
    }
  }, [payload]);

  const refreshForecastData = useCallback(async () => {
    setError('');
    setRefreshMessage(t('xDemand.msg.refreshRunning'));
    setRefreshingData(true);
    try {
      const result = await fetchForecastJson('refresh', {
        mart: true,
        forecast: true,
        backtest: true,
        horizonWeeks: Math.max(filters.horizonWeeks || 12, 26),
        testWeeks: 12,
      });
      const meta = await fetchForecastJson('filters');
      setFiltersMeta(meta);
      await loadForecast();
      setRefreshMessage(t('xDemand.msg.refreshed', {
        steps: (result?.steps || []).join(', ') || t('xDemand.msg.done'),
        time: String(result?.refreshedAt || '').slice(0, 19).replace('T', ' '),
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setRefreshingData(false);
    }
  }, [filters.horizonWeeks, loadForecast, t]);

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      try {
        setLoading(true);
        const meta = await fetchForecastJson('filters', null, controller.signal);
        const horizonOptions = meta?.horizons?.length ? meta.horizons : [4, 8, 12];
        setFiltersMeta(meta);
        setFilters((current) => ({
          ...current,
          horizonWeeks: horizonOptions.includes(current.horizonWeeks)
            ? current.horizonWeeks
            : (meta?.defaults?.horizonWeeks || horizonOptions[horizonOptions.length - 1]),
        }));
      } catch (err) {
        if (err.name !== 'AbortError') setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!filtersMeta) return undefined;
    const horizonOptions = filtersMeta?.horizons?.length ? filtersMeta.horizons : [4, 8, 12];
    if (!horizonOptions.includes(filters.horizonWeeks)) {
      setFilters((current) => ({
        ...current,
        horizonWeeks: horizonOptions[horizonOptions.length - 1],
      }));
      return undefined;
    }
    const controller = new AbortController();
    const load = async () => {
      try {
        setRefreshing(true);
        await loadForecast(controller.signal);
      } catch (err) {
        if (err.name !== 'AbortError') setError(err.message);
      } finally {
        setRefreshing(false);
      }
    };
    load();
    return () => controller.abort();
  }, [filters.horizonWeeks, filtersMeta, payloadKey, loadForecast]);

  const setListFilter = (key) => (value) => setFilters((current) => ({ ...current, [key]: value }));

  const candidateTrendByWeek = new Map((summary?.candidate?.trend || []).map((row) => [`${row.target_year}-${row.target_week}`, row]));
  const trendRows = (summary?.trend || []).map((row) => ({
    ...row,
    label: weekLabel(row),
    rowType: 'forecast',
    forecastQty: Number(row.forecastQty) || 0,
    lowerQty: Number(row.lowerQty) || 0,
    upperQty: Number(row.upperQty) || 0,
    candidateQty: Number(candidateTrendByWeek.get(`${row.target_year}-${row.target_week}`)?.candidateQty) || 0,
  }));
  const historyRows = (summary?.history || []).map((row) => ({
    ...row,
    label: weekLabel(row),
    rowType: 'actual',
    actualQty: Number(row.actualQty) || 0,
  }));
  const combinedTrendRows = [...historyRows, ...trendRows];
  const forecastWeekCount = summary?.meta?.availableForecastWeeks || trendRows.length || filtersMeta?.forecast?.forecastWeeks || filters.horizonWeeks;
  const backtestSummary = backtest?.summary || [];
  const wapeRow = backtestSummary.find((row) => row.level === 'total')
    || backtestSummary.find((row) => row.level === 'branch_channel')
    || backtestSummary[0];

  const branchRows = summary?.branchChannel || [];
  const candidateTotal = summary?.candidate?.total || {};
  const candidateBranchRows = summary?.candidate?.branchChannel || [];
  const candidateVisibleBranchRows = candidateStatusFilter === 'All'
    ? candidateBranchRows
    : candidateBranchRows.filter((row) => row.diagnosticStatus === candidateStatusFilter);
  const candidateStatusCounts = candidateBranchRows.reduce((acc, row) => {
    const key = row.diagnosticStatus || 'Unknown';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const confidenceRows = summary?.confidence || [];
  const detailRows = detail?.rows || [];
  const candidateV3Summary = detail?.candidateV3 || {};
  const recommendedOutputSummary = detail?.recommendedOutputSummary || {};
  const branchChannelModelRows = backtest?.branchChannelModelRecommendation || [];
  const branchChannelModelMap = branchChannelModelRows.reduce((acc, row) => {
    const key = `${String(row.branch || '-').trim().toLowerCase()}|${String(row.channel || '-').trim().toLowerCase()}`;
    acc[key] = row;
    return acc;
  }, {});
  const recommendedOutputRows = (detail?.recommendedOutput || []).map((row) => {
    const key = `${String(row.branch || '-').trim().toLowerCase()}|${String(row.channel || '-').trim().toLowerCase()}`;
    const bcModel = branchChannelModelMap[key] || null;
    const bcRecommendedModel = bcModel?.recommendedModel || null;
    const outputModel = row.recommendedModel || null;
    const modelAligned = !bcRecommendedModel
      || !outputModel
      || outputModel === bcRecommendedModel;
    const deltaQtyAbs = Math.abs(Number(row.deltaQty) || 0);
    const deltaPctAbs = Math.abs(Number(row.deltaPct) || 0);
    const outputStatus = row.outputStatus || 'Unknown';
    const priorityScore = deltaQtyAbs
      + (deltaPctAbs * 1000)
      + (outputStatus === 'Review' ? 2000 : outputStatus === 'Watch' ? 800 : 0)
      + (!modelAligned ? 1500 : 0);
    const reviewPriority = outputStatus === 'Review' && (!modelAligned || deltaQtyAbs >= 1000 || deltaPctAbs >= 0.5)
      ? 'P1'
      : outputStatus === 'Review' || outputStatus === 'Watch' || deltaQtyAbs >= 500 || deltaPctAbs >= 0.2
        ? 'P2'
        : 'P3';
    return {
      ...row,
      bcRecommendedModel,
      bcModelStatus: bcModel?.recommendationStatus || null,
      bcModelImprovement: bcModel?.improvementVsCurrent ?? null,
      modelAligned,
      reviewPriority,
      priorityScore,
    };
  });
  const recommendedOutputStatusCounts = recommendedOutputRows.reduce((acc, row) => {
    const key = row.outputStatus || 'Unknown';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const recommendedOutputDirectionCounts = recommendedOutputRows.reduce((acc, row) => {
    const key = row.outputDirection || 'Unknown';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const recommendedOutputModelAlignmentCounts = recommendedOutputRows.reduce((acc, row) => {
    const key = row.modelAligned ? 'Aligned' : 'Review';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const recommendedOutputPriorityCounts = recommendedOutputRows.reduce((acc, row) => {
    const key = row.reviewPriority || 'P3';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const recommendedVisibleOutputRows = recommendedOutputRows.filter((row) => (
    (recommendedOutputStatusFilter === 'All' || row.outputStatus === recommendedOutputStatusFilter)
    && (recommendedOutputDirectionFilter === 'All' || row.outputDirection === recommendedOutputDirectionFilter)
    && (recommendedOutputAlignmentFilter === 'All' || (row.modelAligned ? 'Aligned' : 'Review') === recommendedOutputAlignmentFilter)
    && (!recommendedOutputGroupFilter || `${row.branch || '-'}|${row.channel || '-'}` === recommendedOutputGroupFilter)
    && (!recommendedOutputReasonFilter || (row.outputReason || 'Unknown reason') === recommendedOutputReasonFilter)
  ));
  const recommendedPriorityRows = [...recommendedVisibleOutputRows]
    .sort((a, b) => (
      Number(b.priorityScore || 0) - Number(a.priorityScore || 0)
      || Math.abs(Number(b.deltaQty) || 0) - Math.abs(Number(a.deltaQty) || 0)
      || String(a.sku || '').localeCompare(String(b.sku || ''))
    ))
    .slice(0, 8);
  const recommendedOutputGroupRows = Object.values(recommendedVisibleOutputRows.reduce((acc, row) => {
    const key = `${row.branch || '-'}|${row.channel || '-'}`;
    if (!acc[key]) {
      acc[key] = {
        branch: row.branch || '-',
        channel: row.channel || '-',
        rows: 0,
        reviewRows: 0,
        watchRows: 0,
        keepCurrentRows: 0,
        increaseRows: 0,
        decreaseRows: 0,
        keepDirectionRows: 0,
        currentQty: 0,
        recommendedQty: 0,
        deltaQty: 0,
      };
    }
    acc[key].rows += 1;
    if (row.outputStatus === 'Review') acc[key].reviewRows += 1;
    if (row.outputStatus === 'Watch') acc[key].watchRows += 1;
    if (row.outputStatus === 'Keep current') acc[key].keepCurrentRows += 1;
    if (row.outputDirection === 'Increase forecast') acc[key].increaseRows += 1;
    if (row.outputDirection === 'Decrease forecast') acc[key].decreaseRows += 1;
    if (row.outputDirection === 'Keep current') acc[key].keepDirectionRows += 1;
    acc[key].currentQty += Number(row.currentForecastQty) || 0;
    acc[key].recommendedQty += Number(row.recommendedForecastQty) || 0;
    acc[key].deltaQty += Number(row.deltaQty) || 0;
    return acc;
  }, {}))
    .map((row) => ({
      ...row,
      deltaPct: row.currentQty ? row.deltaQty / row.currentQty : 0,
      priority: row.reviewRows >= 10 || Math.abs(row.deltaQty) >= 5000
        ? 'High'
        : row.reviewRows >= 3 || row.watchRows >= 5 || Math.abs(row.deltaQty) >= 1500
          ? 'Medium'
          : 'Low',
    }))
    .sort((a, b) => (
      Math.abs(b.deltaQty) - Math.abs(a.deltaQty)
      || b.reviewRows - a.reviewRows
      || `${a.branch} ${a.channel}`.localeCompare(`${b.branch} ${b.channel}`)
    ));
  const recommendedReasonRows = Object.values(recommendedVisibleOutputRows.reduce((acc, row) => {
    const key = row.outputReason || 'Unknown reason';
    if (!acc[key]) {
      acc[key] = {
        reason: key,
        rows: 0,
        reviewRows: 0,
        watchRows: 0,
        increaseRows: 0,
        decreaseRows: 0,
        currentQty: 0,
        recommendedQty: 0,
        deltaQty: 0,
      };
    }
    acc[key].rows += 1;
    if (row.outputStatus === 'Review') acc[key].reviewRows += 1;
    if (row.outputStatus === 'Watch') acc[key].watchRows += 1;
    if (row.outputDirection === 'Increase forecast') acc[key].increaseRows += 1;
    if (row.outputDirection === 'Decrease forecast') acc[key].decreaseRows += 1;
    acc[key].currentQty += Number(row.currentForecastQty) || 0;
    acc[key].recommendedQty += Number(row.recommendedForecastQty) || 0;
    acc[key].deltaQty += Number(row.deltaQty) || 0;
    return acc;
  }, {}))
    .map((row) => ({
      ...row,
      deltaPct: row.currentQty ? row.deltaQty / row.currentQty : 0,
      priority: row.reviewRows >= 50 || Math.abs(row.deltaQty) >= 10000
        ? 'High'
        : row.reviewRows >= 10 || row.watchRows >= 20 || Math.abs(row.deltaQty) >= 3000
          ? 'Medium'
          : 'Low',
    }))
    .sort((a, b) => (
      Math.abs(b.deltaQty) - Math.abs(a.deltaQty)
      || b.rows - a.rows
      || a.reason.localeCompare(b.reason)
    ));
  const candidateSkuRows = detail?.candidateSkuImpact || [];
  const candidateSkuStatus = (row) => {
    const deltaPct = Math.abs(Number(row.deltaPct) || 0);
    const deltaQty = Math.abs(Number(row.deltaQty) || 0);
    if (deltaPct >= 0.5 || deltaQty >= 1000) return 'Review';
    if (deltaPct >= 0.2 || deltaQty >= 500) return 'Watch';
    return 'OK';
  };
  const candidateSkuReason = (row) => {
    const deltaPct = Math.abs(Number(row.deltaPct) || 0);
    const deltaQty = Math.abs(Number(row.deltaQty) || 0);
    if (deltaPct >= 0.5) return t('xDemand.csr.largePct');
    if (deltaQty >= 1000) return t('xDemand.csr.largeQty');
    if (deltaPct >= 0.2) return t('xDemand.csr.moderatePct');
    if (deltaQty >= 500) return t('xDemand.csr.moderateQty');
    return t('xDemand.cr.broadlyAligned');
  };
  const candidateSkuReviewRows = candidateSkuRows.map((row) => ({
    ...row,
    diagnosticStatus: candidateSkuStatus(row),
    diagnosticReason: candidateSkuReason(row),
  }));
  const candidateVisibleSkuRows = candidateSkuStatusFilter === 'All'
    ? candidateSkuReviewRows
    : candidateSkuReviewRows.filter((row) => row.diagnosticStatus === candidateSkuStatusFilter);
  const candidateSkuStatusCounts = candidateSkuReviewRows.reduce((acc, row) => {
    const key = row.diagnosticStatus || 'Unknown';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const candidateReviewSummaryRows = Object.values(candidateSkuReviewRows
    .filter((row) => ['Review', 'Watch'].includes(row.diagnosticStatus))
    .reduce((acc, row) => {
      const key = `${row.branch || '-'}|${row.channel || '-'}`;
      if (!acc[key]) {
        acc[key] = {
          branch: row.branch || '-',
          channel: row.channel || '-',
          reviewSkuCount: 0,
          watchSkuCount: 0,
          candidateQty: 0,
          currentQty: 0,
          candidateV3Qty: 0,
          deltaQty: 0,
          v3DeltaQty: 0,
        };
      }
      if (row.diagnosticStatus === 'Review') acc[key].reviewSkuCount += 1;
      if (row.diagnosticStatus === 'Watch') acc[key].watchSkuCount += 1;
      acc[key].candidateQty += Number(row.candidateForecastQty) || 0;
      acc[key].candidateV3Qty += Number(row.candidateV3ForecastQty) || 0;
      acc[key].currentQty += Number(row.currentForecastQty) || 0;
      acc[key].deltaQty += Number(row.deltaQty) || 0;
      acc[key].v3DeltaQty += Number(row.v3DeltaVsCurrentQty) || 0;
      return acc;
    }, {}))
    .map((row) => {
      const currentQty = Number(row.currentQty) || 0;
      const deltaPct = currentQty ? row.deltaQty / currentQty : 0;
      const priority = row.reviewSkuCount >= 5 || Math.abs(row.deltaQty) >= 5000
        ? 'High'
        : row.reviewSkuCount >= 2 || row.watchSkuCount >= 3 || Math.abs(row.deltaQty) >= 1500
          ? 'Medium'
          : 'Low';
      return { ...row, deltaPct, priority };
    })
    .sort((a, b) => (
      b.reviewSkuCount - a.reviewSkuCount
      || Math.abs(b.deltaQty) - Math.abs(a.deltaQty)
      || b.watchSkuCount - a.watchSkuCount
      || `${a.branch} ${a.channel}`.localeCompare(`${b.branch} ${b.channel}`)
    ));
  const exportCandidateSkuReview = () => {
    const safeStatus = String(candidateSkuStatusFilter || 'All').toLowerCase();
    downloadCsv(`demand_forecast_candidate_sku_${safeStatus}.csv`, [
      { label: 'SKU', value: (row) => row.sku },
      { label: t('xDemand.col.productName'), value: (row) => row.productDescription || row.sku },
      { label: t('xDemand.col.branch'), value: (row) => row.branch },
      { label: t('xDemand.col.channel'), value: (row) => row.channel },
      { label: t('xDemand.col.collection'), value: (row) => row.productCollection },
      { label: t('xDemand.col.category'), value: (row) => row.productCategory },
      { label: t('xDemand.col.candidateQty'), value: (row) => Number(row.candidateForecastQty || 0).toFixed(3) },
      { label: t('xDemand.col.candidateV3Qty'), value: (row) => Number(row.candidateV3ForecastQty || 0).toFixed(3) },
      { label: t('xDemand.col.currentQty'), value: (row) => Number(row.currentForecastQty || 0).toFixed(3) },
      { label: t('xDemand.col.deltaQty'), value: (row) => Number(row.deltaQty || 0).toFixed(3) },
      { label: t('xDemand.col.deltaPctTxt'), value: (row) => Number(row.deltaPct || 0).toFixed(6) },
      { label: t('xDemand.col.v3DeltaVsCurrentQty'), value: (row) => Number(row.v3DeltaVsCurrentQty || 0).toFixed(3) },
      { label: t('xDemand.col.v3DeltaVsCurrentPct'), value: (row) => Number(row.v3DeltaVsCurrentPct || 0).toFixed(6) },
      { label: t('xDemand.col.v3DeltaVsV2Qty'), value: (row) => Number(row.v3DeltaVsV2Qty || 0).toFixed(3) },
      { label: t('xDemand.col.status'), value: (row) => row.diagnosticStatus },
      { label: t('xDemand.col.reason'), value: (row) => row.diagnosticReason },
      { label: t('xDemand.col.dataSignal'), value: (row) => row.dataSignal },
      { label: t('xDemand.col.candidateV3Rule'), value: (row) => row.candidateV3Rule },
      { label: t('xDemand.col.recent12Qty'), value: (row) => Number(row.recent12ActualQty || 0).toFixed(3) },
      { label: t('xDemand.col.recent4Qty'), value: (row) => Number(row.recent4ActualQty || 0).toFixed(3) },
      { label: t('xDemand.col.recent12ActiveWeeks'), value: (row) => row.recent12ActiveWeeks || 0 },
      { label: t('xDemand.col.priorYearHorizonQty'), value: (row) => Number(row.priorYearSameHorizonQty || 0).toFixed(3) },
    ], candidateVisibleSkuRows);
  };
  const exportRecommendedOutput = () => {
    const safeStatus = String(recommendedOutputStatusFilter || 'All').toLowerCase().replace(/\s+/g, '_');
    const safeDirection = String(recommendedOutputDirectionFilter || 'All').toLowerCase().replace(/\s+/g, '_');
    const safeAlignment = String(recommendedOutputAlignmentFilter || 'All').toLowerCase().replace(/\s+/g, '_');
    downloadCsv(`demand_forecast_recommended_output_${safeStatus}_${safeDirection}_${safeAlignment}.csv`, [
      { label: 'SKU', value: (row) => row.sku },
      { label: t('xDemand.col.productName'), value: (row) => row.productDescription || row.sku },
      { label: t('xDemand.col.branch'), value: (row) => row.branch },
      { label: t('xDemand.col.channel'), value: (row) => row.channel },
      { label: t('xDemand.col.collection'), value: (row) => row.productCollection },
      { label: t('xDemand.col.category'), value: (row) => row.productCategory },
      { label: t('xDemand.col.currentForecast'), value: (row) => Number(row.currentForecastQty || 0).toFixed(3) },
      { label: t('xDemand.col.candidateV3Forecast'), value: (row) => Number(row.candidateV3ForecastQty || 0).toFixed(3) },
      { label: t('xDemand.col.recommendedForecast'), value: (row) => Number(row.recommendedForecastQty || 0).toFixed(3) },
      { label: t('xDemand.col.deltaQty'), value: (row) => Number(row.deltaQty || 0).toFixed(3) },
      { label: t('xDemand.col.deltaPct'), value: (row) => Number(row.deltaPct || 0).toFixed(6) },
      { label: t('xDemand.col.recommendedModel'), value: (row) => row.recommendedModel },
      { label: t('xDemand.col.bcRecommendedModel'), value: (row) => row.bcRecommendedModel || '' },
      { label: t('xDemand.col.modelAlignment'), value: (row) => (row.modelAligned ? 'Aligned' : 'Review') },
      { label: t('xDemand.col.reviewPriority'), value: (row) => row.reviewPriority },
      { label: t('xDemand.col.bcModelStatus'), value: (row) => row.bcModelStatus || '' },
      { label: t('xDemand.col.bcModelImprovement'), value: (row) => Number(row.bcModelImprovement || 0).toFixed(6) },
      { label: t('xDemand.col.status'), value: (row) => row.outputStatus },
      { label: t('xDemand.col.direction'), value: (row) => row.outputDirection },
      { label: t('xDemand.col.reason'), value: (row) => row.outputReason },
      { label: t('xDemand.col.recent12Actual'), value: (row) => Number(row.recent12ActualQty || 0).toFixed(3) },
      { label: t('xDemand.col.recent4Actual'), value: (row) => Number(row.recent4ActualQty || 0).toFixed(3) },
      { label: t('xDemand.col.recent12ActiveWeeks'), value: (row) => row.recent12ActiveWeeks },
      { label: t('xDemand.col.priorYearHorizon'), value: (row) => Number(row.priorYearSameHorizonQty || 0).toFixed(3) },
      { label: t('xDemand.col.dataSignal'), value: (row) => row.dataSignal },
    ], recommendedVisibleOutputRows);
  };
  const exportPlanningOutput = () => {
    const actualsThrough = filtersMeta?.forecast?.source_max_week_end
      ? String(filtersMeta.forecast.source_max_week_end).slice(0, 10)
      : '';
    const safeStatus = String(recommendedOutputStatusFilter || 'All').toLowerCase().replace(/\s+/g, '_');
    const safeDirection = String(recommendedOutputDirectionFilter || 'All').toLowerCase().replace(/\s+/g, '_');
    downloadCsv(`demand_forecast_planning_output_${safeStatus}_${safeDirection}_${filters.horizonWeeks}w.csv`, [
      { label: t('xDemand.col.actualsThrough'), value: () => actualsThrough },
      { label: t('xDemand.col.horizonWeeks'), value: () => filters.horizonWeeks },
      { label: t('xDemand.col.branch'), value: (row) => row.branch },
      { label: t('xDemand.col.channel'), value: (row) => row.channel },
      { label: 'SKU', value: (row) => row.sku },
      { label: t('xDemand.col.productName'), value: (row) => row.productDescription || row.sku },
      { label: t('xDemand.col.collection'), value: (row) => row.productCollection },
      { label: t('xDemand.col.category'), value: (row) => row.productCategory },
      { label: t('xDemand.col.currentProductionForecast'), value: (row) => Number(row.currentForecastQty || 0).toFixed(3) },
      { label: t('xDemand.col.recommendedPlanningForecast'), value: (row) => Number(row.recommendedForecastQty || 0).toFixed(3) },
      { label: t('xDemand.col.plannerOverride'), value: () => '' },
      { label: t('xDemand.col.finalApproved'), value: () => '' },
      { label: t('xDemand.col.deltaQty'), value: (row) => Number(row.deltaQty || 0).toFixed(3) },
      { label: t('xDemand.col.deltaPct'), value: (row) => Number(row.deltaPct || 0).toFixed(6) },
      { label: t('xDemand.col.direction'), value: (row) => row.outputDirection },
      { label: t('xDemand.col.reviewStatus'), value: (row) => row.outputStatus },
      { label: t('xDemand.col.reviewPriority'), value: (row) => row.reviewPriority },
      { label: t('xDemand.col.modelAlignment'), value: (row) => (row.modelAligned ? 'Aligned' : 'Review') },
      { label: t('xDemand.col.recommendedModel'), value: (row) => modelLabel(row.recommendedModel) },
      { label: t('xDemand.col.bcModel'), value: (row) => modelLabel(row.bcRecommendedModel) },
      { label: t('xDemand.col.reason'), value: (row) => row.outputReason },
      { label: t('xDemand.col.plannerComment'), value: () => '' },
      { label: t('xDemand.col.owner'), value: () => '' },
      { label: t('xDemand.col.decision'), value: () => '' },
      { label: t('xDemand.col.recent12Actual'), value: (row) => Number(row.recent12ActualQty || 0).toFixed(3) },
      { label: t('xDemand.col.recent4Actual'), value: (row) => Number(row.recent4ActualQty || 0).toFixed(3) },
      { label: t('xDemand.col.recent12ActiveWeeks'), value: (row) => row.recent12ActiveWeeks },
      { label: t('xDemand.col.priorYearHorizon'), value: (row) => Number(row.priorYearSameHorizonQty || 0).toFixed(3) },
      { label: t('xDemand.col.dataSignal'), value: (row) => row.dataSignal },
    ], recommendedVisibleOutputRows);
  };
  const detailTotalRows = Number(detail?.meta?.totalRows || detailRows.length || 0);
  const confidenceTotalQty = confidenceRows.reduce((sum, row) => sum + (Number(row.forecastQty) || 0), 0);
  const confidenceDescriptions = {
    High: t('xDemand.conf.high'),
    Medium: t('xDemand.conf.medium'),
    Low: t('xDemand.conf.low'),
  };
  const backtestWeeks = (backtest?.byWeek || []).map((row) => ({
    ...row,
    label: weekLabel(row),
    wapePct: (Number(row.wape) || 0) * 100,
    biasPct: (Number(row.bias) || 0) * 100,
  }));
  const backtestModelName = backtest?.meta?.modelName
    || backtestWeeks.find((row) => row.modelName)?.modelName
    || backtestSummary.find((row) => row.modelName)?.modelName
    || '-';
  const backtestModelLabel = modelLabel(backtestModelName);
  const modelComparisonRows = backtest?.modelComparison || [];
  const featureBacktestRows = featureBacktest?.summary || [];
  const featureBacktestByWeekRows = featureBacktest?.byWeek || [];
  const featureBranchChannelRows = featureBacktest?.branchChannel || [];
  const featureReviewQueueRows = featureBacktest?.reviewQueue || [];
  const featureCurrentModelName = 'hierarchical_branch_channel_recent12_sku_mix';
  const featureCandidateModelName = featureBacktestRows.find((row) => row.modelName !== featureCurrentModelName)?.modelName
    || featureBacktestRows[0]?.modelName
    || 'candidate_v7_feature_guarded';
  const featureWeeklyChartRows = Object.values(featureBacktestByWeekRows.reduce((acc, row) => {
    const key = `${row.targetYear || row.target_year || ''}-${row.targetWeek || row.target_week || ''}`;
    if (!acc[key]) {
      acc[key] = {
        key,
        targetYear: row.targetYear || row.target_year,
        targetWeek: row.targetWeek || row.target_week,
        label: weekLabel(row),
      };
    }
    if (row.modelName === featureCurrentModelName) {
      acc[key].actualQty = Number(row.actualQty) || 0;
      acc[key].currentForecastQty = Number(row.forecastQty) || 0;
      acc[key].currentWapePct = (Number(row.wape) || 0) * 100;
    }
    if (row.modelName === featureCandidateModelName) {
      acc[key].featureForecastQty = Number(row.forecastQty) || 0;
      acc[key].featureWapePct = (Number(row.wape) || 0) * 100;
      acc[key].adjustmentPct = (Number(row.totalAdjustmentPct) || 0) * 100;
      acc[key].adjustmentReason = row.adjustmentReason || '-';
    }
    return acc;
  }, {})).sort((a, b) => (
    Number(a.targetYear || 0) - Number(b.targetYear || 0)
    || Number(a.targetWeek || 0) - Number(b.targetWeek || 0)
  ));
  const featureWeeklyVerdictRows = featureWeeklyChartRows
    .filter((row) => Number.isFinite(Number(row.currentWapePct)) && Number.isFinite(Number(row.featureWapePct)))
    .map((row) => {
      const improvementPts = Number(row.currentWapePct) - Number(row.featureWapePct);
      const verdict = improvementPts >= 1
        ? 'Helps'
        : improvementPts <= -1
          ? 'Hurts'
          : 'Neutral';
      return {
        ...row,
        improvementPts,
        verdict,
      };
    });
  const featureWeeklyVerdictCounts = featureWeeklyVerdictRows.reduce((acc, row) => {
    acc[row.verdict] = (acc[row.verdict] || 0) + 1;
    return acc;
  }, {});
  const featureWeeklyAvgImprovement = featureWeeklyVerdictRows.length
    ? featureWeeklyVerdictRows.reduce((sum, row) => sum + row.improvementPts, 0) / featureWeeklyVerdictRows.length
    : null;
  const featureWeeklyBestWeek = featureWeeklyVerdictRows.slice().sort((a, b) => b.improvementPts - a.improvementPts)[0] || null;
  const featureWeeklyWorstWeek = featureWeeklyVerdictRows.slice().sort((a, b) => a.improvementPts - b.improvementPts)[0] || null;
  const featureReviewPriorityCounts = featureReviewQueueRows.reduce((acc, row) => {
    const key = row.reviewPriority || 'Unknown';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const featureReviewRiskCounts = featureReviewQueueRows.reduce((acc, row) => {
    const key = row.reviewReason || 'Unknown';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const featureReviewTopRisk = Object.entries(featureReviewRiskCounts)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0];
  const featureReviewRiskOptions = ['All', ...Object.keys(featureReviewRiskCounts).sort()];
  const featureVisibleReviewQueueRows = featureReviewQueueRows.filter((row) => (
    (featureReviewPriorityFilter === 'All' || row.reviewPriority === featureReviewPriorityFilter)
    && (featureReviewRiskFilter === 'All' || row.reviewReason === featureReviewRiskFilter)
  ));
  const featureReviewBranchRows = Object.values(featureVisibleReviewQueueRows.reduce((acc, row) => {
    const key = `${row.branch || '-'}|${row.channel || '-'}`;
    if (!acc[key]) {
      acc[key] = {
        branch: row.branch || '-',
        channel: row.channel || '-',
        rows: 0,
        highRows: 0,
        mediumRows: 0,
        forecastQty: 0,
        recentQty: 0,
      };
    }
    acc[key].rows += 1;
    acc[key].highRows += row.reviewPriority === 'High' ? 1 : 0;
    acc[key].mediumRows += row.reviewPriority === 'Medium' ? 1 : 0;
    acc[key].forecastQty += Number(row.forecastQty) || 0;
    acc[key].recentQty += Number(row.recentQty) || 0;
    return acc;
  }, {})).sort((a, b) => (
    b.highRows - a.highRows
    || b.rows - a.rows
    || b.forecastQty - a.forecastQty
    || `${a.branch} ${a.channel}`.localeCompare(`${b.branch} ${b.channel}`)
  ));
  const featureExceptionRows = Object.values(featureVisibleReviewQueueRows.reduce((acc, row) => {
    const risk = row.reviewReason || 'Unknown risk';
    const action = row.recommendedAction || 'Review manually';
    const key = `${risk}|${action}`;
    if (!acc[key]) {
      acc[key] = {
        risk,
        action,
        rows: 0,
        highRows: 0,
        mediumRows: 0,
        forecastQty: 0,
        recentQty: 0,
        lowerQty: 0,
        upperQty: 0,
      };
    }
    acc[key].rows += 1;
    acc[key].highRows += row.reviewPriority === 'High' ? 1 : 0;
    acc[key].mediumRows += row.reviewPriority === 'Medium' ? 1 : 0;
    acc[key].forecastQty += Number(row.forecastQty) || 0;
    acc[key].recentQty += Number(row.recentQty) || 0;
    acc[key].lowerQty += Number(row.lowerQty) || 0;
    acc[key].upperQty += Number(row.upperQty) || 0;
    return acc;
  }, {})).sort((a, b) => (
    b.highRows - a.highRows
    || b.forecastQty - a.forecastQty
    || b.rows - a.rows
    || a.risk.localeCompare(b.risk)
  ));
  const featureExceptionTop = featureExceptionRows[0] || null;
  const featureExceptionForecastQty = featureExceptionRows.reduce((sum, row) => sum + row.forecastQty, 0);
  const featureExceptionHighForecastQty = featureExceptionRows.reduce((sum, row) => sum + (row.highRows ? row.forecastQty : 0), 0);
  const featureSignalDefinitions = [
    {
      key: 'promotion_price',
      label: t('xDemand.sig.promo'),
      note: t('xDemand.sig.promoNote'),
      match: (row) => (
        (Number(row.promoQtyShare) || 0) >= 0.10
        || (Number(row.highDiscountQtyShare) || 0) >= 0.10
        || (Number(row.priceDropQtyShare) || 0) >= 0.10
        || /promotion|price|discount/i.test(row.reviewReason || '')
      ),
      metricLabel: t('xDemand.sig.promoMetric'),
      metric: (row) => Math.max(
        Number(row.promoQtyShare) || 0,
        Number(row.highDiscountQtyShare) || 0,
        Number(row.priceDropQtyShare) || 0,
      ),
      formatMetric: (value) => formatPercent(value),
    },
    {
      key: 'inventory_stockout',
      label: t('xDemand.sig.stock'),
      note: t('xDemand.sig.stockNote'),
      match: (row) => (
        (Number(row.stockoutQtyShare) || 0) >= 0.10
        || (Number.isFinite(Number(row.inventoryToSalesRatio)) && Number(row.inventoryToSalesRatio) < 1)
        || /stockout|inventory/i.test(row.reviewReason || '')
      ),
      metricLabel: t('xDemand.sig.stockMetric'),
      metric: (row) => Number(row.stockoutQtyShare) || 0,
      formatMetric: (value) => formatPercent(value),
    },
    {
      key: 'holiday',
      label: t('xDemand.sig.holiday'),
      note: t('xDemand.sig.holidayNote'),
      match: (row) => /holiday/i.test(row.reviewReason || ''),
      metricLabel: t('xDemand.sig.holidayMetric'),
      metric: () => 1,
      formatMetric: (value) => formatQty(value),
    },
    {
      key: 'no_recent_sales',
      label: t('xDemand.sig.noSales'),
      note: t('xDemand.sig.noSalesNote'),
      match: (row) => (
        (Number(row.recentQty) || 0) <= 0
        || /no recent/i.test(row.reviewReason || '')
      ),
      metricLabel: t('xDemand.sig.noSalesMetric'),
      metric: (row) => Number(row.forecastQty) || 0,
      formatMetric: (value) => formatQty(value),
    },
  ];
  const featureSignalRows = featureSignalDefinitions.map((definition) => {
    const rows = featureVisibleReviewQueueRows.filter(definition.match);
    const metricTotal = rows.reduce((sum, row) => sum + (Number(definition.metric(row)) || 0), 0);
    const forecastQty = rows.reduce((sum, row) => sum + (Number(row.forecastQty) || 0), 0);
    const recentQty = rows.reduce((sum, row) => sum + (Number(row.recentQty) || 0), 0);
    const highRows = rows.filter((row) => row.reviewPriority === 'High').length;
    return {
      ...definition,
      rows: rows.length,
      highRows,
      forecastQty,
      recentQty,
      metricValue: definition.key === 'no_recent_sales' || definition.key === 'holiday'
        ? metricTotal
        : rows.length
          ? metricTotal / rows.length
          : 0,
    };
  }).filter((row) => row.rows > 0);
  const featureCurrentRow = featureBacktestRows.find((row) => row.modelName === featureCurrentModelName) || null;
  const featureBestRow = featureBacktestRows[0] || null;
  const featureCandidateGap = featureCurrentRow && featureBestRow
    ? Number(featureBestRow.wape) - Number(featureCurrentRow.wape)
    : null;
  const featureHighRiskRows = Number(featureReviewPriorityCounts.High || 0);
  const featureTotalRiskRows = Number(featureReviewQueueRows.length || 0);
  const featureHighRiskShare = featureTotalRiskRows ? featureHighRiskRows / featureTotalRiskRows : 0;
  const featureGuardrailDecision = (() => {
    if (!featureBacktestRows.length || !featureCurrentRow || !featureBestRow) {
      return {
        label: 'Needs data',
        tone: 'medium',
        action: t('xDemand.txt.keepCurrentBaseline'),
        detail: t('xDemand.fg.needsDataDetail'),
      };
    }
    if (featureBestRow.modelName === featureCurrentRow.modelName) {
      return {
        label: 'Keep baseline',
        tone: 'medium',
        action: t('xDemand.fg.keepAction'),
        detail: t('xDemand.fg.keepDetail'),
      };
    }
    if (Number.isFinite(featureCandidateGap) && featureCandidateGap <= -0.02 && featureHighRiskShare <= 0.35) {
      return {
        label: 'Pilot candidate',
        tone: 'high',
        action: t('xDemand.fg.pilotAction'),
        detail: t('xDemand.fg.pilotDetail'),
      };
    }
    if (Number.isFinite(featureCandidateGap) && featureCandidateGap <= -0.005) {
      return {
        label: 'Review candidate',
        tone: 'medium',
        action: t('xDemand.fg.reviewAction'),
        detail: t('xDemand.fg.reviewDetail'),
      };
    }
    return {
      label: 'Hold',
      tone: 'low',
      action: t('xDemand.fg.holdAction'),
      detail: t('xDemand.fg.holdDetail'),
    };
  })();
  const featureGuardrailRows = [
    {
      label: t('xDemand.col.decision'),
      value: tv(featureGuardrailDecision.label),
      detail: featureGuardrailDecision.detail,
      tone: featureGuardrailDecision.tone,
    },
    {
      label: t('xDemand.fg.accuracy'),
      value: Number.isFinite(featureCandidateGap) ? pts((featureCandidateGap * 100).toFixed(2)) : '-',
      detail: t('xDemand.fg.accuracyDetail'),
      tone: Number.isFinite(featureCandidateGap) && featureCandidateGap <= -0.02 ? 'high' : Number.isFinite(featureCandidateGap) && featureCandidateGap <= -0.005 ? 'medium' : 'low',
    },
    {
      label: t('xDemand.fg.risk'),
      value: t('xDemand.fg.riskValue', { n: formatQty(featureHighRiskRows) }),
      detail: t('xDemand.fg.riskDetail', { share: formatPercent(featureHighRiskShare) }),
      tone: featureHighRiskShare <= 0.2 ? 'high' : featureHighRiskShare <= 0.4 ? 'medium' : 'low',
    },
    {
      label: t('xDemand.fg.next'),
      value: featureGuardrailDecision.action,
      detail: t('xDemand.fg.nextDetail', { n: formatQty(featureSignalRows.length) }),
      tone: featureGuardrailDecision.tone,
    },
  ];
  const featureActionRows = featureBranchChannelRows.map((row) => {
    const improvement = Number(row.improvementVsCurrent);
    const actualQty = Number(row.actualQty) || 0;
    let action = 'Watch';
    let tone = 'medium';
    let reason = t('xDemand.fa.watchReason');
    if (row.featureStatus === 'Feature helps' && Number.isFinite(improvement) && improvement >= 0.03 && actualQty >= 500) {
      action = 'Pilot candidate';
      tone = 'high';
      reason = t('xDemand.fa.pilotReason');
    } else if (row.featureStatus === 'Feature hurts' && Number.isFinite(improvement) && improvement <= -0.03 && actualQty >= 500) {
      action = 'Keep baseline';
      tone = 'low';
      reason = t('xDemand.fa.keepReason');
    } else if (actualQty < 500) {
      action = 'Low volume watch';
      tone = 'medium';
      reason = t('xDemand.fa.lowReason');
    }
    return {
      ...row,
      action,
      tone,
      actionReason: reason,
    };
  }).sort((a, b) => {
    const rank = { 'Pilot candidate': 0, 'Keep baseline': 1, Watch: 2, 'Low volume watch': 3 };
    return (rank[a.action] ?? 9) - (rank[b.action] ?? 9)
      || Math.abs(Number(b.improvementVsCurrent) || 0) - Math.abs(Number(a.improvementVsCurrent) || 0)
      || Number(b.actualQty || 0) - Number(a.actualQty || 0);
  });
  const featureActionCounts = featureActionRows.reduce((acc, row) => {
    acc[row.action] = (acc[row.action] || 0) + 1;
    return acc;
  }, {});
  const featurePilotRows = featureActionRows.filter((row) => row.action === 'Pilot candidate');
  const featureActionTotalImpact = featurePilotRows.reduce((sum, row) => sum + ((Number(row.currentWape) || 0) - (Number(row.featureWape) || 0)) * (Number(row.actualQty) || 0), 0);
  const phase7ChecklistRows = [
    {
      label: t('xDemand.p7.backtest'),
      done: featureBacktestRows.length > 0 && Number(featureBestRow?.weeks || 0) >= 8,
      detail: t('xDemand.p7.backtestDetail', { n: formatQty(featureBestRow?.weeks || 0) }),
    },
    {
      label: t('xDemand.p7.signals'),
      done: featureSignalRows.length >= 2,
      detail: t('xDemand.p7.signalsDetail', { n: formatQty(featureSignalRows.length) }),
    },
    {
      label: t('xDemand.p7.queue'),
      done: featureReviewQueueRows.length > 0 && featureExceptionRows.length > 0,
      detail: t('xDemand.p7.queueDetail', { risks: formatQty(featureReviewQueueRows.length), buckets: formatQty(featureExceptionRows.length) }),
    },
    {
      label: t('xDemand.p7.pilot'),
      done: featureActionRows.length > 0,
      detail: t('xDemand.p7.pilotDetail', { pilot: formatQty(featurePilotRows.length), keep: formatQty(featureActionCounts['Keep baseline'] || 0) }),
    },
    {
      label: t('xDemand.p7.guardrail'),
      done: !['Needs data', 'Hold'].includes(featureGuardrailDecision.label),
      detail: t('xDemand.p7.guardrailDetail', { label: tv(featureGuardrailDecision.label), action: featureGuardrailDecision.action }),
    },
  ];
  const phase7DoneCount = phase7ChecklistRows.filter((row) => row.done).length;
  const phase7UiStatus = (() => {
    if (phase7DoneCount >= phase7ChecklistRows.length && featureGuardrailDecision.label === 'Pilot candidate') {
      return {
        label: t('xDemand.p7.pilotReady'),
        tone: 'high',
        detail: t('xDemand.p7.pilotReadyDetail'),
      };
    }
    if (phase7DoneCount >= 4) {
      return {
        label: t('xDemand.p7.mvpReady'),
        tone: 'medium',
        detail: t('xDemand.p7.mvpReadyDetail'),
      };
    }
    return {
      label: t('xDemand.p7.needsWork'),
      tone: 'low',
      detail: t('xDemand.p7.needsWorkDetail'),
    };
  })();
  const phase8WorkflowRows = [
    {
      label: t('xDemand.p8.official'),
      value: t('xDemand.txt.keepCurrentBaseline'),
      detail: t('xDemand.p8.officialDetail'),
      tone: 'high',
    },
    {
      label: t('xDemand.p8.aiUsage'),
      value: t('xDemand.p8.aiUsageValue'),
      detail: t('xDemand.p8.aiUsageDetail'),
      tone: featurePilotRows.length ? 'medium' : 'high',
    },
    {
      label: t('xDemand.p8.weekly'),
      value: t('xDemand.p8.weeklyValue'),
      detail: t('xDemand.p8.weeklyDetail'),
      tone: 'high',
    },
    {
      label: t('xDemand.p8.failure'),
      value: t('xDemand.p8.failureValue'),
      detail: t('xDemand.p8.failureDetail'),
      tone: 'medium',
    },
  ];
  const runHistoryRuns = runHistory?.runs || [];
  const runHistoryComparison = runHistory?.comparison || {};
  const hasPreviousRun = Boolean(runHistoryComparison.previousRunId);
  const runHistoryBranchRows = hasPreviousRun ? (runHistory?.branchChannel || []) : [];
  const runHistorySkuRows = hasPreviousRun ? (runHistory?.skuMovers || []) : [];
  const latestRunId = runHistoryComparison.currentRunId || runHistoryRuns[0]?.forecastRunId;
  const latestRunTime = runHistoryComparison.currentGeneratedAt || runHistoryRuns[0]?.generatedAt;
  const previousRunId = runHistoryComparison.previousRunId || runHistoryRuns[1]?.forecastRunId;
  const previousRunTime = runHistoryComparison.previousGeneratedAt || runHistoryRuns[1]?.generatedAt;
  const runDeltaPct = Number(runHistoryComparison.deltaPct);
  const runDeltaQty = Number(runHistoryComparison.deltaQty);
  const runSkuDelta = Number(runHistoryComparison.currentSkuCount) - Number(runHistoryComparison.previousSkuCount);
  const branchSevereMoverCount = runHistoryBranchRows.filter((row) => Math.abs(Number(row.deltaPct)) >= 0.30 || Math.abs(Number(row.deltaQty)) >= 5000).length;
  const skuSevereMoverCount = runHistorySkuRows.filter((row) => Math.abs(Number(row.deltaPct)) >= 0.50 || Math.abs(Number(row.deltaQty)) >= 1000).length;
  const getRunMoverAction = (row) => {
    const currentQty = Number(row.currentForecastQty) || 0;
    const previousQty = Number(row.previousForecastQty) || 0;
    const deltaQty = Number(row.deltaQty) || 0;
    const deltaPct = Number(row.deltaPct);
    if (previousQty <= 0 && currentQty >= 500) {
      return {
        action: 'Review new demand',
        tone: 'low',
        reason: t('xDemand.mv.newReason'),
      };
    }
    if (currentQty <= 0 && previousQty >= 500) {
      return {
        action: 'Review dropped demand',
        tone: 'low',
        reason: t('xDemand.mv.droppedReason'),
      };
    }
    if (Math.abs(deltaQty) >= 1000 || Math.abs(deltaPct) >= 0.50) {
      return {
        action: 'Manual review',
        tone: 'low',
        reason: t('xDemand.mv.manualReason'),
      };
    }
    if (Math.abs(deltaQty) >= 500 || Math.abs(deltaPct) >= 0.30) {
      return {
        action: 'Watch',
        tone: 'medium',
        reason: t('xDemand.mv.watchReason'),
      };
    }
    return {
      action: 'Accept movement',
      tone: 'high',
      reason: t('xDemand.mv.acceptReason'),
    };
  };
  const phase8HoldSkuRows = runHistorySkuRows
    .map((row) => ({
      ...row,
      ...getRunMoverAction(row),
    }))
    .filter((row) => row.action !== 'Accept movement')
    .slice(0, 30);
  const phase8HoldActionCounts = phase8HoldSkuRows.reduce((acc, row) => {
    acc[row.action] = (acc[row.action] || 0) + 1;
    return acc;
  }, {});
  const phase8HoldGroupRows = Object.values(phase8HoldSkuRows.reduce((acc, row) => {
    const key = `${row.branch || '-'}|${row.channel || '-'}`;
    if (!acc[key]) {
      acc[key] = {
        branch: row.branch || '-',
        channel: row.channel || '-',
        reviewCount: 0,
        currentForecastQty: 0,
        previousForecastQty: 0,
        deltaQty: 0,
        largestSku: row.sku || '-',
        largestAbsDelta: 0,
      };
    }
    acc[key].reviewCount += 1;
    acc[key].currentForecastQty += Number(row.currentForecastQty) || 0;
    acc[key].previousForecastQty += Number(row.previousForecastQty) || 0;
    acc[key].deltaQty += Number(row.deltaQty) || 0;
    const absDelta = Math.abs(Number(row.deltaQty) || 0);
    if (absDelta > acc[key].largestAbsDelta) {
      acc[key].largestAbsDelta = absDelta;
      acc[key].largestSku = row.sku || '-';
    }
    return acc;
  }, {})).sort((a, b) => Math.abs(b.deltaQty) - Math.abs(a.deltaQty)).slice(0, 8);
  const phase8HoldReasonRows = [
    {
      label: t('xDemand.hr.total'),
      status: Math.abs(runDeltaPct) >= 0.15 || Math.abs(runDeltaQty) >= 25000 ? 'Hold' : (Math.abs(runDeltaPct) >= 0.07 || Math.abs(runDeltaQty) >= 10000 ? 'Review' : 'Pass'),
      detail: t('xDemand.hr.totalDetail', {
        pct: Number.isFinite(runDeltaPct) ? formatPercent(runDeltaPct) : '-',
        qty: Number.isFinite(runDeltaQty) ? formatQty(runDeltaQty) : '-',
      }),
    },
    {
      label: t('xDemand.hr.branch'),
      status: branchSevereMoverCount >= 5 ? 'Hold' : (branchSevereMoverCount > 0 ? 'Review' : 'Pass'),
      detail: t('xDemand.hr.branchDetail', { n: formatQty(branchSevereMoverCount) }),
    },
    {
      label: t('xDemand.hr.sku'),
      status: skuSevereMoverCount >= 20 ? 'Hold' : (skuSevereMoverCount >= 5 ? 'Review' : 'Pass'),
      detail: t('xDemand.hr.skuDetail', { n: formatQty(skuSevereMoverCount) }),
    },
  ];
  const phase8GuardrailDecision = (() => {
    if (!latestRunId) {
      return {
        label: 'No run loaded',
        tone: 'low',
        action: t('xDemand.g8.noRunAction'),
        detail: t('xDemand.g8.noRunDetail'),
      };
    }
    if (!hasPreviousRun) {
      return {
        label: 'Watch',
        tone: 'medium',
        action: t('xDemand.g8.firstAction'),
        detail: t('xDemand.g8.firstDetail'),
      };
    }
    if (
      Math.abs(runDeltaPct) >= 0.15
      || Math.abs(runDeltaQty) >= 25000
      || branchSevereMoverCount >= 5
      || skuSevereMoverCount >= 20
    ) {
      return {
        label: 'Hold',
        tone: 'low',
        action: t('xDemand.g8.holdAction'),
        detail: t('xDemand.g8.holdDetail', {
          pct: Number.isFinite(runDeltaPct) ? formatPercent(runDeltaPct) : '-',
          branch: formatQty(branchSevereMoverCount),
          sku: formatQty(skuSevereMoverCount),
        }),
      };
    }
    if (
      Math.abs(runDeltaPct) >= 0.07
      || Math.abs(runDeltaQty) >= 10000
      || branchSevereMoverCount > 0
      || skuSevereMoverCount >= 5
    ) {
      return {
        label: 'Review',
        tone: 'medium',
        action: t('xDemand.g8.reviewAction'),
        detail: t('xDemand.g8.reviewDetail', { pct: Number.isFinite(runDeltaPct) ? formatPercent(runDeltaPct) : '-' }),
      };
    }
    return {
      label: 'Pass',
      tone: 'high',
      action: t('xDemand.g8.passAction'),
      detail: t('xDemand.g8.passDetail'),
    };
  })();
  const runHistorySummaryRows = [
    {
      label: t('xDemand.rh.latest'),
      value: latestRunId ? String(latestRunId).slice(0, 18) : '-',
      detail: formatDateTime(latestRunTime),
    },
    {
      label: t('xDemand.rh.previous'),
      value: previousRunId ? String(previousRunId).slice(0, 18) : '-',
      detail: formatDateTime(previousRunTime),
    },
    {
      label: t('xDemand.rh.totalDelta'),
      value: Number.isFinite(runDeltaQty) ? formatQty(runDeltaQty) : '-',
      detail: Number.isFinite(runDeltaPct) ? formatPercent(runDeltaPct) : t('xDemand.rh.vsPrevious'),
    },
    {
      label: t('xDemand.rh.skuDelta'),
      value: Number.isFinite(runSkuDelta) ? formatQty(runSkuDelta) : '-',
      detail: t('xDemand.rh.skuDeltaDetail', { current: formatQty(runHistoryComparison.currentSkuCount), previous: formatQty(runHistoryComparison.previousSkuCount) }),
    },
  ];
  const phase8ControlRows = [
    {
      label: t('xDemand.pc.export'),
      status: 'Ready',
      tone: 'high',
      detail: t('xDemand.pc.exportDetail'),
    },
    {
      label: t('xDemand.pc.runId'),
      status: latestRunId ? 'Ready' : 'Missing',
      tone: latestRunId ? 'high' : 'low',
      detail: latestRunId
        ? t('xDemand.txt.latestRunGenerated', { id: String(latestRunId).slice(0, 18), time: formatDateTime(latestRunTime) })
        : t('xDemand.pc.runIdNone'),
    },
    {
      label: t('xDemand.pc.compare'),
      status: hasPreviousRun ? 'Ready' : 'Missing',
      tone: hasPreviousRun ? 'high' : 'low',
      detail: hasPreviousRun
        ? t('xDemand.pc.compareDetail', {
          pct: Number.isFinite(runDeltaPct) ? formatPercent(runDeltaPct) : '-',
          qty: Number.isFinite(runDeltaQty) ? formatQty(runDeltaQty) : '-',
        })
        : t('xDemand.pc.compareNone'),
    },
    {
      label: t('xDemand.pc.rollback'),
      status: phase8GuardrailDecision.label,
      tone: phase8GuardrailDecision.tone,
      detail: phase8GuardrailDecision.detail,
    },
  ];
  const phase8ReadyControls = phase8ControlRows.filter((row) => ['Ready', 'Policy ready'].includes(row.status)).length;
  const phase8WeeklyChecklistRows = [
    {
      label: t('xDemand.wc.sales'),
      status: filtersMeta?.meta?.maxHistoryDate ? 'Done' : 'Missing',
      tone: filtersMeta?.meta?.maxHistoryDate ? 'high' : 'low',
      detail: filtersMeta?.meta?.maxHistoryDate
        ? t('xDemand.wc.salesDetail', { date: String(filtersMeta.meta.maxHistoryDate).slice(0, 10) })
        : t('xDemand.wc.salesNone'),
    },
    {
      label: t('xDemand.wc.run'),
      status: latestRunId ? 'Done' : 'Missing',
      tone: latestRunId ? 'high' : 'low',
      detail: latestRunId
        ? t('xDemand.txt.latestRunGenerated', { id: String(latestRunId).slice(0, 12), time: formatDateTime(latestRunTime) })
        : t('xDemand.wc.runNone'),
    },
    {
      label: t('xDemand.wc.prev'),
      status: hasPreviousRun ? 'Done' : 'Waiting',
      tone: hasPreviousRun ? 'high' : 'medium',
      detail: hasPreviousRun
        ? t('xDemand.wc.prevDetail', { id: String(previousRunId).slice(0, 12) })
        : t('xDemand.wc.prevNone'),
    },
    {
      label: t('xDemand.wc.guardrail'),
      status: phase8GuardrailDecision.label,
      tone: phase8GuardrailDecision.tone,
      detail: phase8GuardrailDecision.detail,
    },
    {
      label: t('xDemand.wc.export'),
      status: phase8GuardrailDecision.label === 'Pass' ? 'Ready' : 'Review first',
      tone: phase8GuardrailDecision.label === 'Pass' ? 'high' : 'medium',
      detail: phase8GuardrailDecision.label === 'Pass'
        ? t('xDemand.wc.exportPass')
        : t('xDemand.wc.exportOther'),
    },
    {
      label: t('xDemand.wc.action'),
      status: phase8GuardrailDecision.label === 'Hold' ? 'Hold' : (phase8GuardrailDecision.label === 'Review' ? 'Review' : 'Proceed'),
      tone: phase8GuardrailDecision.tone,
      detail: phase8GuardrailDecision.label === 'Hold'
        ? t('xDemand.wc.actionHold')
        : phase8GuardrailDecision.label === 'Review'
          ? t('xDemand.wc.actionReview')
          : t('xDemand.wc.actionProceed'),
    },
  ];
  const bestModelName = modelComparisonRows[0]?.modelName;
  const bestWape = Number(modelComparisonRows[0]?.wape);
  const rollingRows = backtest?.rollingValidation || [];
  const rollingWinCounts = rollingRows.reduce((acc, row) => {
    const key = row.bestModel || '-';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const rollingWinnerEntry = Object.entries(rollingWinCounts)
    .sort((a, b) => b[1] - a[1] || modelLabel(a[0]).localeCompare(modelLabel(b[0])))[0];
  const rollingWinnerName = rollingWinnerEntry?.[0] || null;
  const rollingWinnerCount = rollingWinnerEntry?.[1] || 0;
  const lowestBiasRow = modelComparisonRows
    .filter((row) => Number.isFinite(Number(row.bias)))
    .slice()
    .sort((a, b) => Math.abs(Number(a.bias)) - Math.abs(Number(b.bias)))[0];
  const recommendedModelName = (() => {
    if (rollingRows.length && rollingWinnerCount >= Math.ceil(rollingRows.length / 2)) {
      return rollingWinnerName;
    }
    if (!modelComparisonRows.length) return null;
    if (!lowestBiasRow || !Number.isFinite(bestWape)) return bestModelName;
    const biasCandidateWape = Number(lowestBiasRow.wape);
    if (Number.isFinite(biasCandidateWape) && Math.abs(biasCandidateWape - bestWape) <= 0.03) {
      return lowestBiasRow.modelName;
    }
    return bestModelName;
  })();
  const recommendedRow = modelComparisonRows.find((row) => row.modelName === recommendedModelName) || modelComparisonRows[0];
  const modelComparisonByName = modelComparisonRows.reduce((acc, row) => {
    acc[row.modelName] = row;
    return acc;
  }, {});
  const currentModelRow = modelComparisonByName.hierarchical_branch_channel_recent12_sku_mix || null;
  const v4ModelRow = modelComparisonByName.candidate_v4_signal_blend || null;
  const v5ModelRow = modelComparisonByName.candidate_v5_seasonal_guardrail || null;
  const weeklyModelRows = backtest?.weeklyModelComparison || [];
  const modelComparisonByWeekRows = backtest?.modelComparisonByWeek || [];
  const weeklyTrackingRows = Object.values(modelComparisonByWeekRows.reduce((acc, row) => {
    const targetYear = row.targetYear || row.target_year;
    const targetWeek = row.targetWeek || row.target_week;
    const key = `${targetYear}-${targetWeek}`;
    if (!acc[key]) {
      acc[key] = {
        key,
        targetYear,
        targetWeek,
        label: weekLabel(row),
        actualQty: Number(row.actualQty) || 0,
        models: {},
      };
    }
    acc[key].actualQty = Number(row.actualQty) || acc[key].actualQty || 0;
    acc[key].models[row.modelName] = {
      forecastQty: Number(row.forecastQty) || 0,
      wape: Number(row.wape),
      bias: Number(row.bias),
    };
    return acc;
  }, {})).map((row) => {
    const matchingFeature = featureWeeklyChartRows.find((item) => (
      Number(item.targetYear) === Number(row.targetYear)
      && Number(item.targetWeek) === Number(row.targetWeek)
    ));
    if (matchingFeature && Number.isFinite(Number(matchingFeature.featureWapePct))) {
      row.models[featureCandidateModelName] = {
        forecastQty: Number(matchingFeature.featureForecastQty) || 0,
        wape: Number(matchingFeature.featureWapePct) / 100,
        bias: row.actualQty ? ((Number(matchingFeature.featureForecastQty) || 0) - row.actualQty) / Math.abs(row.actualQty) : null,
      };
    }
    const rankedModels = Object.entries(row.models)
      .filter(([, model]) => Number.isFinite(Number(model.wape)))
      .sort((a, b) => Number(a[1].wape) - Number(b[1].wape));
    const baseline = row.models.hierarchical_branch_channel_recent12_sku_mix || {};
    const ma4 = row.models.moving_average_4w || {};
    const ma12 = row.models.moving_average_12w || {};
    const feature = row.models[featureCandidateModelName] || {};
    const winnerName = rankedModels[0]?.[0] || '-';
    const winner = rankedModels[0]?.[1] || {};
    const baselineVsWinnerPts = Number.isFinite(Number(baseline.wape)) && Number.isFinite(Number(winner.wape))
      ? (Number(baseline.wape) - Number(winner.wape)) * 100
      : null;
    return {
      ...row,
      baseline,
      ma4,
      ma12,
      feature,
      winnerName,
      winner,
      baselineVsWinnerPts,
    };
  }).sort((a, b) => (
    Number(a.targetYear || 0) - Number(b.targetYear || 0)
    || Number(a.targetWeek || 0) - Number(b.targetWeek || 0)
  ));
  const weeklyTrackingRecentRows = weeklyTrackingRows.slice(-8);
  const weeklyTrackingWinnerCounts = weeklyTrackingRecentRows.reduce((acc, row) => {
    const key = row.winnerName || '-';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const weeklyTrackingTopWinner = Object.entries(weeklyTrackingWinnerCounts)
    .sort((a, b) => b[1] - a[1] || modelLabel(a[0]).localeCompare(modelLabel(b[0])))[0];
  const weeklyTrackingBaselineAvgWape = weeklyTrackingRecentRows.length
    ? weeklyTrackingRecentRows.reduce((sum, row) => sum + (Number(row.baseline?.wape) || 0), 0) / weeklyTrackingRecentRows.length
    : null;
  const weeklyTrackingBestAvgWape = weeklyTrackingRecentRows.length
    ? weeklyTrackingRecentRows.reduce((sum, row) => sum + (Number(row.winner?.wape) || 0), 0) / weeklyTrackingRecentRows.length
    : null;
  const weeklyTrackingLatest = weeklyTrackingRecentRows[weeklyTrackingRecentRows.length - 1] || null;
  const exportWeeklyModelTracking = () => {
    downloadCsv('demand_forecast_weekly_model_tracking.csv', [
      { label: t('xDemand.col.year'), value: (row) => row.targetYear },
      { label: t('xDemand.col.week'), value: (row) => row.label },
      { label: t('xDemand.col.actualQty'), value: (row) => Number(row.actualQty || 0).toFixed(3) },
      { label: t('xDemand.col.baselineForecast'), value: (row) => Number(row.baseline?.forecastQty || 0).toFixed(3) },
      { label: t('xDemand.col.baselineWape'), value: (row) => Number(row.baseline?.wape || 0).toFixed(6) },
      { label: t('xDemand.col.baselineBias'), value: (row) => Number(row.baseline?.bias || 0).toFixed(6) },
      { label: t('xDemand.col.featureForecast'), value: (row) => Number(row.feature?.forecastQty || 0).toFixed(3) },
      { label: t('xDemand.col.featureWape'), value: (row) => Number(row.feature?.wape || 0).toFixed(6) },
      { label: t('xDemand.col.featureBias'), value: (row) => Number(row.feature?.bias || 0).toFixed(6) },
      { label: t('xDemand.col.ma4Forecast'), value: (row) => Number(row.ma4?.forecastQty || 0).toFixed(3) },
      { label: 'MA4 WAPE', value: (row) => Number(row.ma4?.wape || 0).toFixed(6) },
      { label: t('xDemand.col.ma4Bias'), value: (row) => Number(row.ma4?.bias || 0).toFixed(6) },
      { label: t('xDemand.col.ma12Forecast'), value: (row) => Number(row.ma12?.forecastQty || 0).toFixed(3) },
      { label: 'MA12 WAPE', value: (row) => Number(row.ma12?.wape || 0).toFixed(6) },
      { label: t('xDemand.col.ma12Bias'), value: (row) => Number(row.ma12?.bias || 0).toFixed(6) },
      { label: t('xDemand.col.winner'), value: (row) => modelLabel(row.winnerName) },
      { label: t('xDemand.col.winnerForecast'), value: (row) => Number(row.winner?.forecastQty || 0).toFixed(3) },
      { label: t('xDemand.col.winnerWape'), value: (row) => Number(row.winner?.wape || 0).toFixed(6) },
      { label: t('xDemand.col.baselineGapVsWinnerPts'), value: (row) => Number(row.baselineVsWinnerPts || 0).toFixed(3) },
    ], weeklyTrackingRows);
  };
  const exportBacktestModels = () => {
    downloadCsv('demand_forecast_backtest_model_comparison.csv', [
      { label: t('xDemand.col.section'), value: (row) => row.section },
      { label: t('xDemand.col.model'), value: (row) => row.modelName },
      { label: t('xDemand.col.branch'), value: (row) => row.branch || '' },
      { label: t('xDemand.col.channel'), value: (row) => row.channel || '' },
      { label: t('xDemand.col.actualQty'), value: (row) => Number(row.actualQty || 0).toFixed(3) },
      { label: t('xDemand.col.forecastQty'), value: (row) => Number(row.forecastQty || row.recommendedForecastQty || 0).toFixed(3) },
      { label: 'WAPE', value: (row) => Number(row.wape ?? row.recommendedWape ?? 0).toFixed(6) },
      { label: t('xDemand.col.bias'), value: (row) => Number(row.bias ?? row.currentBias ?? 0).toFixed(6) },
      { label: t('xDemand.col.currentWape'), value: (row) => Number(row.currentWape || 0).toFixed(6) },
      { label: t('xDemand.col.seasonalWape'), value: (row) => Number(row.seasonalWape || 0).toFixed(6) },
      { label: 'V4 WAPE', value: (row) => Number(row.candidateV4Wape || 0).toFixed(6) },
      { label: 'V5 WAPE', value: (row) => Number(row.candidateV5Wape || 0).toFixed(6) },
      { label: t('xDemand.col.recommendedModel'), value: (row) => row.recommendedModel || row.modelName },
      { label: t('xDemand.col.improvementVsCurrent'), value: (row) => Number(row.improvementVsCurrent || 0).toFixed(6) },
      { label: t('xDemand.col.status'), value: (row) => row.recommendationStatus || '' },
    ], [
      ...modelComparisonRows.map((row) => ({ ...row, section: 'overall' })),
      ...branchChannelModelRows.map((row) => ({
        ...row,
        section: 'branch_channel',
        modelName: row.recommendedModel,
        wape: row.recommendedWape,
        bias: row.currentBias,
      })),
    ]);
  };
  const exportWeeklyBacktest = () => {
    downloadCsv('demand_forecast_weekly_model_comparison.csv', [
      { label: t('xDemand.col.year'), value: (row) => row.targetYear },
      { label: t('xDemand.col.week'), value: (row) => weekLabel(row) },
      { label: t('xDemand.col.actualQty'), value: (row) => Number(row.actualQty || 0).toFixed(3) },
      { label: t('xDemand.col.winner'), value: (row) => row.winningModel },
      { label: t('xDemand.col.winnerWape'), value: (row) => Number(row.winningWape || 0).toFixed(6) },
      { label: t('xDemand.col.hierForecast'), value: (row) => Number(row.hierarchicalForecastQty || 0).toFixed(3) },
      { label: t('xDemand.col.hierWape'), value: (row) => Number(row.hierarchicalWape || 0).toFixed(6) },
      { label: t('xDemand.col.seasonalForecast'), value: (row) => Number(row.seasonalForecastQty || 0).toFixed(3) },
      { label: t('xDemand.col.seasonalWape'), value: (row) => Number(row.seasonalWape || 0).toFixed(6) },
      { label: t('xDemand.col.v3Forecast'), value: (row) => Number(row.candidateV3ForecastQty || 0).toFixed(3) },
      { label: 'V3 WAPE', value: (row) => Number(row.candidateV3Wape || 0).toFixed(6) },
      { label: t('xDemand.col.v4Forecast'), value: (row) => Number(row.candidateV4ForecastQty || 0).toFixed(3) },
      { label: 'V4 WAPE', value: (row) => Number(row.candidateV4Wape || 0).toFixed(6) },
      { label: t('xDemand.col.v5Forecast'), value: (row) => Number(row.candidateV5ForecastQty || 0).toFixed(3) },
      { label: 'V5 WAPE', value: (row) => Number(row.candidateV5Wape || 0).toFixed(6) },
      { label: t('xDemand.col.ma12Forecast'), value: (row) => Number(row.movingAverage12ForecastQty || 0).toFixed(3) },
      { label: 'MA12 WAPE', value: (row) => Number(row.movingAverage12Wape || 0).toFixed(6) },
      { label: t('xDemand.col.naiveForecast'), value: (row) => Number(row.naiveForecastQty || 0).toFixed(3) },
      { label: t('xDemand.col.naiveWape'), value: (row) => Number(row.naiveWape || 0).toFixed(6) },
    ], weeklyModelRows);
  };
  const exportFeatureReviewQueue = () => {
    const safePriority = String(featureReviewPriorityFilter || 'All').toLowerCase().replace(/\s+/g, '_');
    const safeRisk = String(featureReviewRiskFilter || 'All').toLowerCase().replace(/[^a-z0-9]+/gi, '_').toLowerCase();
    downloadCsv(`demand_forecast_ai_review_queue_${safePriority}_${safeRisk}.csv`, [
      { label: t('xDemand.col.priority'), value: (row) => row.reviewPriority },
      { label: t('xDemand.col.risk'), value: (row) => row.reviewReason },
      { label: t('xDemand.col.action'), value: (row) => row.recommendedAction },
      { label: 'SKU', value: (row) => row.sku },
      { label: t('xDemand.col.branch'), value: (row) => row.branch },
      { label: t('xDemand.col.channel'), value: (row) => row.channel },
      { label: t('xDemand.col.collection'), value: (row) => row.productCollection || '' },
      { label: t('xDemand.col.category'), value: (row) => row.productCategory || '' },
      { label: t('xDemand.col.forecastQty'), value: (row) => Number(row.forecastQty || 0).toFixed(3) },
      { label: t('xDemand.col.lowerQty'), value: (row) => Number(row.lowerQty || 0).toFixed(3) },
      { label: t('xDemand.col.upperQty'), value: (row) => Number(row.upperQty || 0).toFixed(3) },
      { label: t('xDemand.col.recent4Qty'), value: (row) => Number(row.recentQty || 0).toFixed(3) },
      { label: t('xDemand.col.promoShare'), value: (row) => Number(row.promoQtyShare || 0).toFixed(6) },
      { label: t('xDemand.col.highDiscountShare'), value: (row) => Number(row.highDiscountQtyShare || 0).toFixed(6) },
      { label: t('xDemand.col.priceDropShare'), value: (row) => Number(row.priceDropQtyShare || 0).toFixed(6) },
      { label: t('xDemand.col.stockoutShare'), value: (row) => Number(row.stockoutQtyShare || 0).toFixed(6) },
      { label: t('xDemand.col.invToSales'), value: (row) => Number.isFinite(Number(row.inventoryToSalesRatio)) ? Number(row.inventoryToSalesRatio).toFixed(6) : '' },
    ], featureVisibleReviewQueueRows);
  };
  const exportFeatureExceptionBuckets = () => {
    downloadCsv(`demand_forecast_ai_exception_buckets_${filters.horizonWeeks}w.csv`, [
      { label: t('xDemand.col.risk'), value: (row) => row.risk },
      { label: t('xDemand.col.recommendedAction'), value: (row) => row.action },
      { label: t('xDemand.col.rows'), value: (row) => row.rows },
      { label: t('xDemand.col.highPriorityRows'), value: (row) => row.highRows },
      { label: t('xDemand.col.mediumPriorityRows'), value: (row) => row.mediumRows },
      { label: t('xDemand.col.forecastQty'), value: (row) => Number(row.forecastQty || 0).toFixed(3) },
      { label: t('xDemand.col.recent4Qty'), value: (row) => Number(row.recentQty || 0).toFixed(3) },
      { label: t('xDemand.col.lowerQty'), value: (row) => Number(row.lowerQty || 0).toFixed(3) },
      { label: t('xDemand.col.upperQty'), value: (row) => Number(row.upperQty || 0).toFixed(3) },
    ], featureExceptionRows);
  };
  const exportFeatureActionOutput = () => {
    downloadCsv(`demand_forecast_feature_action_output_${filters.horizonWeeks}w.csv`, [
      { label: t('xDemand.col.action'), value: (row) => row.action },
      { label: t('xDemand.col.branch'), value: (row) => row.branch },
      { label: t('xDemand.col.channel'), value: (row) => row.channel },
      { label: t('xDemand.col.status'), value: (row) => row.featureStatus },
      { label: t('xDemand.col.actualQty'), value: (row) => Number(row.actualQty || 0).toFixed(3) },
      { label: t('xDemand.col.currentWape'), value: (row) => Number(row.currentWape || 0).toFixed(6) },
      { label: t('xDemand.col.featureWape'), value: (row) => Number(row.featureWape || 0).toFixed(6) },
      { label: t('xDemand.col.improvementVsCurrent'), value: (row) => Number(row.improvementVsCurrent || 0).toFixed(6) },
      { label: t('xDemand.col.avgAdjustmentPct'), value: (row) => Number(row.avgAdjustmentPct || 0).toFixed(6) },
      { label: t('xDemand.col.reason'), value: (row) => row.actionReason || '' },
      { label: t('xDemand.col.signalReason'), value: (row) => row.adjustmentReason || '' },
    ], featureActionRows);
  };
  const exportPhase8HoldReview = () => {
    downloadCsv(`demand_forecast_phase8_hold_review_${filters.horizonWeeks}w.csv`, [
      { label: t('xDemand.col.guardrailDecision'), value: () => phase8GuardrailDecision.label },
      { label: t('xDemand.col.guardrailAction'), value: () => phase8GuardrailDecision.action },
      { label: t('xDemand.col.latestRunId'), value: () => latestRunId || '' },
      { label: t('xDemand.col.latestRunTime'), value: () => formatDateTime(latestRunTime) },
      { label: t('xDemand.col.previousRunId'), value: () => previousRunId || '' },
      { label: t('xDemand.col.previousRunTime'), value: () => formatDateTime(previousRunTime) },
      { label: t('xDemand.col.totalDeltaQty'), value: () => Number.isFinite(runDeltaQty) ? runDeltaQty.toFixed(3) : '' },
      { label: t('xDemand.col.totalDeltaPct'), value: () => Number.isFinite(runDeltaPct) ? runDeltaPct.toFixed(6) : '' },
      { label: 'SKU', value: (row) => row.sku || '' },
      { label: t('xDemand.col.branch'), value: (row) => row.branch || '' },
      { label: t('xDemand.col.channel'), value: (row) => row.channel || '' },
      { label: t('xDemand.col.collection'), value: (row) => row.productCollection || '' },
      { label: t('xDemand.col.category'), value: (row) => row.productCategory || '' },
      { label: t('xDemand.col.currentForecast'), value: (row) => Number(row.currentForecastQty || 0).toFixed(3) },
      { label: t('xDemand.col.previousForecast'), value: (row) => Number(row.previousForecastQty || 0).toFixed(3) },
      { label: t('xDemand.col.deltaQty'), value: (row) => Number(row.deltaQty || 0).toFixed(3) },
      { label: t('xDemand.col.deltaPctTxt'), value: (row) => Number.isFinite(Number(row.deltaPct)) ? Number(row.deltaPct).toFixed(6) : '' },
      { label: t('xDemand.col.action'), value: (row) => row.action || '' },
      { label: t('xDemand.col.reason'), value: (row) => row.reason || '' },
      { label: t('xDemand.col.plannerDecision'), value: () => '' },
      { label: t('xDemand.col.plannerComment'), value: () => '' },
    ], phase8HoldSkuRows);
  };
  const candidateV3Diagnostics = backtest?.candidateV3Diagnostics || [];
  const candidateV3WinWeeks = candidateV3Diagnostics.filter((row) => Number(row.v3VsCurrentWapeGap) < -0.005).length;
  const candidateV3CloseWeeks = candidateV3Diagnostics.filter((row) => Math.abs(Number(row.v3VsCurrentWapeGap)) <= 0.005).length;
  const candidateV3WorseWeeks = candidateV3Diagnostics.filter((row) => Number(row.v3VsCurrentWapeGap) > 0.005).length;
  const candidateV3AvgGap = candidateV3Diagnostics.length
    ? candidateV3Diagnostics.reduce((sum, row) => sum + (Number(row.v3VsCurrentWapeGap) || 0), 0) / candidateV3Diagnostics.length
    : null;
  const candidateV4WinWeeks = candidateV3Diagnostics.filter((row) => Number(row.v4VsCurrentWapeGap) < -0.005).length;
  const candidateV4WorseWeeks = candidateV3Diagnostics.filter((row) => Number(row.v4VsCurrentWapeGap) > 0.005).length;
  const candidateV4AvgGap = candidateV3Diagnostics.length
    ? candidateV3Diagnostics.reduce((sum, row) => sum + (Number(row.v4VsCurrentWapeGap) || 0), 0) / candidateV3Diagnostics.length
    : null;
  const candidateV5WinWeeks = candidateV3Diagnostics.filter((row) => Number(row.v5VsCurrentWapeGap) < -0.005).length;
  const candidateV5WorseWeeks = candidateV3Diagnostics.filter((row) => Number(row.v5VsCurrentWapeGap) > 0.005).length;
  const candidateV5AvgGap = candidateV3Diagnostics.length
    ? candidateV3Diagnostics.reduce((sum, row) => sum + (Number(row.v5VsCurrentWapeGap) || 0), 0) / candidateV3Diagnostics.length
    : null;
  const modelWinRows = backtest?.modelWinRate || [];
  const hybridSimulation = backtest?.hybridSimulation || null;
  const ruleHybridSimulation = backtest?.ruleHybridSimulation || null;
  const ruleHybridRows = backtest?.ruleHybridByWeek || [];
  const rollingV4Wins = rollingRows.filter((row) => row.bestModel === 'candidate_v4_signal_blend').length;
  const rollingV4AvgGap = rollingRows.length
    ? rollingRows.reduce((sum, row) => sum + (Number(row.candidateV4VsCurrentGap) || 0), 0) / rollingRows.length
    : null;
  const rollingV5Wins = rollingRows.filter((row) => row.bestModel === 'candidate_v5_seasonal_guardrail').length;
  const rollingV5AvgGap = rollingRows.length
    ? rollingRows.reduce((sum, row) => sum + (Number(row.candidateV5VsCurrentGap) || 0), 0) / rollingRows.length
    : null;
  const rollingRuleWins = rollingRows.filter((row) => row.bestModel === 'hybrid_rule_v2').length;
  const rollingAvgGap = rollingRows.length
    ? rollingRows.reduce((sum, row) => sum + (Number(row.ruleVsBestSingleGap) || 0), 0) / rollingRows.length
    : null;
  const modelRecommendationRows = [
    {
      modelName: 'hierarchical_branch_channel_recent12_sku_mix',
      label: t('xDemand.col.current'),
      useCase: t('xDemand.mr.currentUse'),
      action: t('xDemand.mr.currentAction'),
      row: currentModelRow,
      rollingWins: rollingRows.filter((row) => row.bestModel === 'hierarchical_branch_channel_recent12_sku_mix').length,
      rollingGap: 0,
      tone: 'medium',
    },
    {
      modelName: 'candidate_v4_signal_blend',
      label: 'V4',
      useCase: t('xDemand.mr.v4Use'),
      action: t('xDemand.mr.v4Action'),
      row: v4ModelRow,
      rollingWins: rollingV4Wins,
      rollingGap: rollingV4AvgGap,
      tone: Number(rollingV4AvgGap) < 0 ? 'high' : 'medium',
    },
    {
      modelName: 'candidate_v5_seasonal_guardrail',
      label: 'V5',
      useCase: t('xDemand.mr.v5Use'),
      action: t('xDemand.mr.v5Action'),
      row: v5ModelRow,
      rollingWins: rollingV5Wins,
      rollingGap: rollingV5AvgGap,
      tone: rollingV5Wins >= Math.ceil(Math.max(rollingRows.length, 1) / 2) ? 'high' : 'medium',
    },
  ];
  const v4LatestWapeGap = v4ModelRow && currentModelRow
    ? Number(v4ModelRow.wape) - Number(currentModelRow.wape)
    : null;
  const v5LatestWapeGap = v5ModelRow && currentModelRow
    ? Number(v5ModelRow.wape) - Number(currentModelRow.wape)
    : null;
  const recommendationPrimary = (() => {
    if (rollingRows.length && rollingV5Wins >= Math.ceil(rollingRows.length / 2) && Number(rollingV5AvgGap) < -0.02) {
      return {
        label: t('xDemand.rp.v5'),
        tone: 'high',
        detail: t('xDemand.rp.v5Detail'),
      };
    }
    if (Number.isFinite(v4LatestWapeGap) && v4LatestWapeGap < -0.05) {
      return {
        label: t('xDemand.rp.v4'),
        tone: 'medium',
        detail: t('xDemand.rp.v4Detail'),
      };
    }
    if (Number.isFinite(v5LatestWapeGap) && v5LatestWapeGap < -0.03) {
      return {
        label: t('xDemand.rp.v5Review'),
        tone: 'medium',
        detail: t('xDemand.rp.v5ReviewDetail'),
      };
    }
    return {
      label: t('xDemand.txt.keepCurrentOutput'),
      tone: 'low',
      detail: t('xDemand.rp.keepDetail'),
    };
  })();
  const hybridImprovement = recommendedRow && hybridSimulation
    ? Number(recommendedRow.wape) - Number(hybridSimulation.wape)
    : null;
  const ruleHybridImprovement = recommendedRow && ruleHybridSimulation
    ? Number(recommendedRow.wape) - Number(ruleHybridSimulation.wape)
    : null;
  const upperBoundGap = hybridSimulation && ruleHybridSimulation
    ? Number(ruleHybridSimulation.wape) - Number(hybridSimulation.wape)
    : null;
  const validationStatus = (() => {
    if (!ruleHybridSimulation || !hybridSimulation || !recommendedRow) {
      return {
        label: 'Needs data',
        tone: 'medium',
        detail: t('xDemand.vs.needsDataDetail'),
      };
    }
    if (rollingRows.length >= 3) {
      if (rollingRuleWins >= Math.ceil(rollingRows.length * 0.75) && Number(rollingAvgGap) <= 0) {
        return {
          label: 'Rolling stable',
          tone: 'high',
          detail: t('xDemand.vs.stableDetail'),
        };
      }
      return {
        label: 'Not stable yet',
        tone: 'low',
        detail: t('xDemand.vs.notStableDetail'),
      };
    }
    const weeks = Number(ruleHybridSimulation.weeks || 0);
    if (weeks < 12 && Number.isFinite(upperBoundGap) && Math.abs(upperBoundGap) <= 0.005) {
      return {
        label: 'High overfit risk',
        tone: 'low',
        detail: t('xDemand.vs.overfitDetail'),
      };
    }
    if (Number.isFinite(ruleHybridImprovement) && ruleHybridImprovement > 0.05) {
      return {
        label: 'Promising',
        tone: 'high',
        detail: t('xDemand.vs.promisingDetail'),
      };
    }
    return {
      label: 'Watch',
      tone: 'medium',
      detail: t('xDemand.vs.watchDetail'),
    };
  })();
  const promotionPolicyForSingleModel = (modelName, latestGap, rollingWins, rollingGap) => {
    const wins = Number(rollingWins || 0);
    const windows = Number(rollingRows.length || 0);
    const gap = Number(rollingGap);
    const latest = Number(latestGap);
    if (!Number.isFinite(latest) && !windows) {
      return {
        status: 'Needs data',
        tone: 'medium',
        action: t('xDemand.pp.waitBacktest'),
        reason: t('xDemand.pp.needsDataReason'),
      };
    }
    if (windows >= 3 && wins >= Math.ceil(windows * 0.75) && Number.isFinite(gap) && gap <= -0.02) {
      return {
        status: 'Promote review',
        tone: 'high',
        action: t('xDemand.pp.preparePilot'),
        reason: t('xDemand.pp.promoteReason', { model: modelLabel(modelName) }),
      };
    }
    if ((Number.isFinite(latest) && latest <= -0.03) || (windows >= 3 && wins >= Math.ceil(windows / 2))) {
      return {
        status: 'Pilot candidate',
        tone: 'medium',
        action: t('xDemand.pp.reviewImpact'),
        reason: t('xDemand.pp.pilotReason', { model: modelLabel(modelName) }),
      };
    }
    return {
      status: 'Watch only',
      tone: 'low',
      action: t('xDemand.pp.keepDiagnostic'),
      reason: t('xDemand.pp.watchReason', { model: modelLabel(modelName) }),
    };
  };
  const v4PromotionPolicy = promotionPolicyForSingleModel(
    'candidate_v4_signal_blend',
    v4LatestWapeGap,
    rollingV4Wins,
    rollingV4AvgGap
  );
  const v5PromotionPolicy = promotionPolicyForSingleModel(
    'candidate_v5_seasonal_guardrail',
    v5LatestWapeGap,
    rollingV5Wins,
    rollingV5AvgGap
  );
  const ruleHybridPromotionPolicy = (() => {
    if (!ruleHybridSimulation || !rollingRows.length) {
      return {
        status: 'Needs data',
        tone: 'medium',
        action: t('xDemand.pp.waitRolling'),
        reason: t('xDemand.pp.ruleNeedsData'),
      };
    }
    if (validationStatus.label === 'Rolling stable') {
      return {
        status: 'Promote review',
        tone: 'high',
        action: t('xDemand.pp.preparePilot'),
        reason: t('xDemand.pp.rulePromote'),
      };
    }
    if (validationStatus.label === 'Promising' || Number(ruleHybridImprovement) > 0.03) {
      return {
        status: 'Pilot candidate',
        tone: 'medium',
        action: t('xDemand.pp.extendWindow'),
        reason: t('xDemand.pp.rulePilot'),
      };
    }
    return {
      status: 'Hold',
      tone: 'low',
      action: t('xDemand.pp.keepOut'),
      reason: validationStatus.detail,
    };
  })();
  const promotionCandidateRows = [
    {
      modelName: 'hierarchical_branch_channel_recent12_sku_mix',
      label: t('xDemand.col.current'),
      status: 'Production baseline',
      tone: 'medium',
      action: t('xDemand.txt.keepCurrentOutput'),
      reason: t('xDemand.pp.currentReason'),
      latestWape: currentModelRow?.wape,
      latestBias: currentModelRow?.bias,
      rollingWins: rollingRows.filter((row) => row.bestModel === 'hierarchical_branch_channel_recent12_sku_mix').length,
      rollingGap: 0,
    },
    {
      modelName: 'candidate_v4_signal_blend',
      label: 'V4',
      ...v4PromotionPolicy,
      latestWape: v4ModelRow?.wape,
      latestBias: v4ModelRow?.bias,
      rollingWins: rollingV4Wins,
      rollingGap: rollingV4AvgGap,
    },
    {
      modelName: 'candidate_v5_seasonal_guardrail',
      label: 'V5',
      ...v5PromotionPolicy,
      latestWape: v5ModelRow?.wape,
      latestBias: v5ModelRow?.bias,
      rollingWins: rollingV5Wins,
      rollingGap: rollingV5AvgGap,
    },
    {
      modelName: 'hybrid_rule_v2',
      label: t('xDemand.txt.ruleHybrid'),
      ...ruleHybridPromotionPolicy,
      latestWape: ruleHybridSimulation?.wape,
      latestBias: ruleHybridSimulation?.bias,
      rollingWins: rollingRuleWins,
      rollingGap: rollingAvgGap,
    },
  ];
  const modelDescriptions = {
    hierarchical_branch_channel_recent12_sku_mix: t('xDemand.md.hier'),
    naive_1w: t('xDemand.md.naive1'),
    moving_average_4w: t('xDemand.md.ma4'),
    moving_average_12w: t('xDemand.md.ma12'),
    seasonal_naive_52w: t('xDemand.md.seasonal'),
    candidate_v3_adjusted: t('xDemand.md.v3'),
    candidate_v4_signal_blend: t('xDemand.md.v4'),
    candidate_v5_seasonal_guardrail: t('xDemand.md.v5'),
    hybrid_rule_v2: t('xDemand.md.rule'),
  };
  const policyDirection = rollingRows.length && recommendedModelName === 'seasonal_naive_52w'
    ? t('xDemand.pd.seasonal')
    : rollingRows.length && recommendedModelName === 'candidate_v4_signal_blend'
      ? t('xDemand.pd.v4')
      : rollingRows.length && recommendedModelName === 'candidate_v5_seasonal_guardrail'
        ? t('xDemand.pd.v5')
        : rollingRows.length && recommendedModelName === 'hybrid_rule_v2'
          ? t('xDemand.pd.hybrid')
          : t('xDemand.pd.keep');
  const candidateReviewCount = candidateBranchRows.filter((row) => row.diagnosticStatus === 'Review').length;
  const candidateWatchCount = candidateBranchRows.filter((row) => row.diagnosticStatus === 'Watch').length;
  const highSkuImpactCount = candidateSkuStatusCounts.Review || 0;
  const highPriorityReviewGroups = candidateReviewSummaryRows.filter((row) => row.priority === 'High').length;
  const mediumPriorityReviewGroups = candidateReviewSummaryRows.filter((row) => row.priority === 'Medium').length;
  const candidateChecklistRows = [
    {
      label: t('xDemand.ck.rolling'),
      detail: recommendedModelName === 'seasonal_naive_52w'
        ? t('xDemand.ck.rollingSeasonal', { n: formatQty(rollingRows.length) })
        : t('xDemand.ck.rollingOther', { model: modelLabel(recommendedModelName || '-') }),
      done: recommendedModelName === 'seasonal_naive_52w',
    },
    {
      label: t('xDemand.ck.branch'),
      detail: candidateReviewCount
        ? t('xDemand.ck.branchDetail', { n: formatQty(candidateReviewCount) })
        : t('xDemand.ck.branchNone'),
      done: candidateReviewCount === 0,
    },
    {
      label: t('xDemand.ck.highGroups'),
      detail: highPriorityReviewGroups
        ? t('xDemand.ck.highGroupsDetail', { high: formatQty(highPriorityReviewGroups), medium: formatQty(mediumPriorityReviewGroups) })
        : t('xDemand.ck.highGroupsNone', { medium: formatQty(mediumPriorityReviewGroups) }),
      done: highPriorityReviewGroups === 0,
    },
    {
      label: t('xDemand.ck.skuList'),
      detail: highSkuImpactCount
        ? t('xDemand.ck.skuListDetail', { n: formatQty(highSkuImpactCount) })
        : t('xDemand.ck.skuListNone'),
      done: highSkuImpactCount === 0,
    },
  ];
  const candidateChecklistDoneCount = candidateChecklistRows.filter((row) => row.done).length;
  const candidateReadyToPromote = candidateChecklistDoneCount === candidateChecklistRows.length;
  const candidateDecision = (() => {
    if (!candidateBranchRows.length || !candidateSkuRows.length || !rollingRows.length) {
      return {
        label: 'Needs data',
        tone: 'medium',
        detail: t('xDemand.cd.needsDataDetail'),
      };
    }
    if (recommendedModelName === 'seasonal_naive_52w' && candidateReviewCount <= 2 && highSkuImpactCount <= 10) {
      return {
        label: t('xDemand.cd.promote'),
        tone: 'high',
        detail: t('xDemand.cd.promoteDetail'),
      };
    }
    if (recommendedModelName === 'seasonal_naive_52w') {
      return {
        label: t('xDemand.cd.reviewBefore'),
        tone: 'medium',
        detail: t('xDemand.cd.reviewBeforeDetail'),
      };
    }
    return {
      label: t('xDemand.cd.doNot'),
      tone: 'low',
      detail: t('xDemand.cd.doNotDetail'),
    };
  })();
  const recommendedReviewRows = Number(recommendedOutputSummary.reviewRows || 0);
  const recommendedWatchRows = Number(recommendedOutputSummary.watchRows || 0);
  const recommendedKeepCurrentRows = Number(recommendedOutputSummary.keepCurrentRows || 0);
  const recommendedTotalRows = Number(recommendedOutputSummary.totalRows || recommendedOutputRows.length || 0);
  const recommendedP1Rows = Number(recommendedOutputPriorityCounts.P1 || 0);
  const recommendedP2Rows = Number(recommendedOutputPriorityCounts.P2 || 0);
  const recommendedAlignmentReviewRows = Number(recommendedOutputModelAlignmentCounts.Review || 0);
  const recommendedDeltaPctAbs = Math.abs(Number(recommendedOutputSummary.deltaPct) || 0);
  const recommendedDeltaQtyAbs = Math.abs(Number(recommendedOutputSummary.deltaQty) || 0);
  const recommendedReviewShare = recommendedTotalRows ? recommendedReviewRows / recommendedTotalRows : 0;
  const recommendedKeepShare = recommendedTotalRows ? recommendedKeepCurrentRows / recommendedTotalRows : 0;
  const recommendedGovernanceDecision = (() => {
    if (!recommendedTotalRows) {
      return {
        label: 'Needs data',
        tone: 'medium',
        detail: t('xDemand.gov.needsDataDetail'),
      };
    }
    if (recommendedP1Rows > 200 || recommendedAlignmentReviewRows > 300 || recommendedDeltaPctAbs >= 0.10) {
      return {
        label: t('xDemand.gov.reviewOnly'),
        tone: 'low',
        detail: t('xDemand.gov.reviewOnlyDetail'),
      };
    }
    if (recommendedP1Rows > 50 || recommendedReviewShare >= 0.15 || recommendedDeltaPctAbs >= 0.03) {
      return {
        label: t('xDemand.gov.pilot'),
        tone: 'medium',
        detail: t('xDemand.gov.pilotDetail'),
      };
    }
    return {
      label: t('xDemand.gov.ready'),
      tone: 'high',
      detail: t('xDemand.gov.readyDetail'),
    };
  })();
  const recommendedPolicyRows = [
    {
      label: t('xDemand.pol.guardrail'),
      value: tv(recommendedGovernanceDecision.label),
      detail: recommendedGovernanceDecision.detail,
      tone: recommendedGovernanceDecision.tone,
    },
    {
      label: t('xDemand.pol.pressure'),
      value: `${formatQty(recommendedP1Rows)} P1 / ${formatQty(recommendedP2Rows)} P2`,
      detail: t('xDemand.pol.pressureDetail', { share: formatPercent(recommendedReviewShare), watch: formatQty(recommendedWatchRows) }),
      tone: recommendedP1Rows > 200 ? 'low' : recommendedP1Rows > 50 ? 'medium' : 'high',
    },
    {
      label: t('xDemand.pol.agreement'),
      value: t('xDemand.pol.agreementValue', { n: formatQty(recommendedOutputModelAlignmentCounts.Aligned) }),
      detail: t('xDemand.pol.agreementDetail', { n: formatQty(recommendedAlignmentReviewRows) }),
      tone: recommendedAlignmentReviewRows > 300 ? 'low' : recommendedAlignmentReviewRows > 50 ? 'medium' : 'high',
    },
    {
      label: t('xDemand.pol.volume'),
      value: `${formatQty(recommendedOutputSummary.deltaQty)} / ${formatPercent(recommendedOutputSummary.deltaPct)}`,
      detail: t('xDemand.pol.volumeDetail', { share: formatPercent(recommendedKeepShare) }),
      tone: recommendedDeltaPctAbs >= 0.10 || recommendedDeltaQtyAbs >= 50000 ? 'low' : recommendedDeltaPctAbs >= 0.03 ? 'medium' : 'high',
    },
  ];
  const candidateReason = (row) => {
    const delta = Math.abs(Number(row.deltaPct) || 0);
    const ratio = Number(row.seasonalVsRecentRatio);
    if (row.diagnosticStatus === 'Review') {
      if (delta >= 0.5) return t('xDemand.cr.largeGap');
      if (Number.isFinite(ratio) && ratio >= 1.5) return t('xDemand.cr.priorAbove');
      if (Number.isFinite(ratio) && ratio <= 0.5) return t('xDemand.cr.priorBelow');
      return t('xDemand.cr.needsReview');
    }
    if (row.diagnosticStatus === 'Watch') return t('xDemand.cr.moderateGap');
    if (row.diagnosticStatus === 'OK') return t('xDemand.cr.broadlyAligned');
    return '-';
  };
  const backtestLevelDescriptions = {
    total: t('xDemand.lvd.total'),
    branch_channel: t('xDemand.lvd.branch_channel'),
    sku: t('xDemand.lvd.sku'),
  };

  return (
    <div className="df-page">
      <Header />
      <main className="df-shell">
        <section className="df-topbar">
          <div>
            <h1>{t('xDemand.top.title')}</h1>
            <p>{filtersMeta?.forecast?.source_max_week_end ? t('xDemand.top.actualsThrough', { date: String(filtersMeta.forecast.source_max_week_end).slice(0, 10) }) : t('xDemand.top.subtitle')}</p>
          </div>
          <div className="df-actions">
            <div className="df-env">{t('xDemand.top.env')}</div>
            <button type="button" onClick={refreshForecastData} disabled={refreshing || refreshingData || !filtersMeta} title={t('xDemand.top.refreshDataTitle')}>
              <RefreshCw size={16} className={refreshingData ? 'spin' : ''} />
              {t('xDemand.top.refreshData')}
            </button>
            <button type="button" onClick={() => loadForecast()} disabled={refreshing || refreshingData || !filtersMeta}>
              <RefreshCw size={16} className={refreshing ? 'spin' : ''} />
              {t('xDemand.top.refresh')}
            </button>
            <ModuleHelp steps={TOUR} tourKey="demandForecast" ready={!loading && !!filtersMeta} onBeforeTour={() => setActiveTab('forecast')} />
          </div>
        </section>

        <section className="df-filters">
          <label className="df-field">
            <span>{t('xDemand.f.horizon')}</span>
            <select
              value={filters.horizonWeeks}
              onChange={(event) => setFilters((current) => ({ ...current, horizonWeeks: Number(event.target.value) }))}
            >
              {(filtersMeta?.horizons || [4, 8, 12]).map((item) => (
                <option key={item} value={item}>{t('xDemand.f.weeksN', { n: item })}</option>
              ))}
            </select>
          </label>
          <MultiSelect label={t('xDemand.col.branch')} options={filtersMeta?.branches || []} value={filters.branches} onChange={setListFilter('branches')} />
          <MultiSelect label={t('xDemand.col.channel')} options={filtersMeta?.channels || []} value={filters.channels} onChange={setListFilter('channels')} />
          <MultiSelect label={t('xDemand.col.collection')} options={filtersMeta?.productCollections || []} value={filters.productCollections} onChange={setListFilter('productCollections')} />
          <MultiSelect label={t('xDemand.col.category')} options={filtersMeta?.productCategories || []} value={filters.productCategories} onChange={setListFilter('productCategories')} />
          <MultiSelect label="SKU" options={filtersMeta?.skus || []} value={filters.skus} onChange={setListFilter('skus')} searchable />
          <button type="button" className="df-clear" onClick={() => setFilters(FILTER_DEFAULTS)}>{t('xDemand.f.clear')}</button>
        </section>

        {error && <div className="df-alert">{error}</div>}
        {refreshMessage && !error && <div className="df-success">{refreshMessage}</div>}

        <section className="df-stats" aria-busy={loading || refreshing || refreshingData}>
          <Stat icon={CalendarRange} label={t('xDemand.stat.w1')} value={formatQty(summary?.total?.forecastH1Qty)} note="ERPQuantity" />
          <Stat icon={TrendingUp} label={t('xDemand.stat.w4')} value={formatQty(summary?.total?.forecast4wQty)} note={t('xDemand.stat.weeksAvailable', { n: forecastWeekCount })} />
          <Stat icon={Boxes} label={t('xDemand.stat.total')} value={formatQty(summary?.total?.forecastTotalQty)} note={t('xDemand.stat.skus', { n: formatQty(summary?.total?.skuCount) })} />
          <Stat icon={Activity} label={t('xDemand.stat.wape')} value={formatPercent(wapeRow?.wape)} note={wapeRow?.level ? levelLabel(wapeRow.level) : t('xDemand.stat.totalLevel')} />
        </section>

        <div className="df-tabs view-tabs" role="tablist">
          <button type="button" className={activeTab === 'forecast' ? 'active' : ''} onClick={() => setActiveTab('forecast')}><TabIcon k="forecast" />{t('xDemand.tab.forecast')}</button>
          <button type="button" className={activeTab === 'sku' ? 'active' : ''} onClick={() => setActiveTab('sku')}><TabIcon k="sku" />{t('xDemand.tab.sku')}</button>
          <button type="button" className={activeTab === 'backtest' ? 'active' : ''} onClick={() => setActiveTab('backtest')}><TabIcon k="backtest" />{t('xDemand.tab.backtest')}</button>
        </div>

        {activeTab === 'forecast' && (
          <section className="df-grid">
            <div className="df-panel df-full">
              <div className="df-panel-head">
                <span>{t('xDemand.fc.actualForecast')}</span>
                <em>{t('xDemand.fc.actualForecastNote', { actual: historyRows.length, forecast: trendRows.length })}</em>
              </div>
              <div className="df-chart">
                <ResponsiveContainer>
                  <LineChart data={combinedTrendRows} margin={{ top: 10, right: 18, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(value) => formatQty(value)} width={58} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(value) => formatQty(value)} />
                    <Line name={t('xDemand.col.upper')} type="monotone" dataKey="upperQty" stroke="#b9c2cf" strokeWidth={1} dot={false} />
                    <Line name={t('xDemand.col.lower')} type="monotone" dataKey="lowerQty" stroke="#b9c2cf" strokeWidth={1} dot={false} />
                    <Line name={t('xDemand.col.actual')} type="monotone" dataKey="actualQty" stroke="#4b5563" strokeWidth={2.5} dot={false} />
                    <Line name={t('xDemand.col.forecast')} type="monotone" dataKey="forecastQty" stroke="#156082" strokeWidth={3} dot={{ r: 3 }} />
                    <Line name={t('xDemand.cand.candidateV2')} type="monotone" dataKey="candidateQty" stroke="#c55a11" strokeWidth={2.5} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="df-panel df-full">
              <div className="df-panel-head">
                <span>{t('xDemand.cand.title')}</span>
                <em>{t('xDemand.cand.note')}</em>
              </div>
              <div className={`df-candidate-decision ${candidateDecision.tone}`}>
                <div>
                  <span>{t('xDemand.col.decision')}</span>
                  <strong>{tv(candidateDecision.label)}</strong>
                </div>
                <p>{candidateDecision.detail}</p>
                <div>
                  <span>{t('xDemand.cand.diagnostics')}</span>
                  <strong>{t('xDemand.cand.diagCounts', { review: formatQty(candidateReviewCount), watch: formatQty(candidateWatchCount) })}</strong>
                </div>
                <div>
                  <span>{t('xDemand.cand.highSkuImpact')}</span>
                  <strong>{formatQty(highSkuImpactCount)}</strong>
                </div>
              </div>
              <div className={`df-candidate-checklist ${candidateReadyToPromote ? 'ready' : 'not-ready'}`}>
                <div className="df-checklist-head">
                  <div>
                    <span>{t('xDemand.cand.promoteChecklist')}</span>
                    <strong>{candidateReadyToPromote ? t('xDemand.cand.readyToPromote') : t('xDemand.cand.notReady')}</strong>
                  </div>
                  <em>{t('xDemand.cand.passed', { done: formatQty(candidateChecklistDoneCount), total: formatQty(candidateChecklistRows.length) })}</em>
                </div>
                <div className="df-checklist-grid">
                  {candidateChecklistRows.map((row) => {
                    const Icon = row.done ? CheckCircle2 : AlertTriangle;
                    return (
                      <div className={`df-checklist-item ${row.done ? 'done' : 'open'}`} key={row.label}>
                        <Icon size={18} />
                        <div>
                          <strong>{row.label}</strong>
                          <span>{row.detail}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="df-candidate-grid">
                <div className="df-candidate-summary">
                  <div>
                    <span>{t('xDemand.cand.candidateTotal')}</span>
                    <strong>{formatQty(candidateTotal.candidateTotalQty)}</strong>
                    <em>Seasonal Naive 52W</em>
                  </div>
                  <div>
                    <span>{t('xDemand.cand.currentTotal')}</span>
                    <strong>{formatQty(candidateTotal.currentTotalQty)}</strong>
                    <em>{t('xDemand.cand.hierOutput')}</em>
                  </div>
                  <div>
                    <span>{t('xDemand.cand.difference')}</span>
                    <strong>{formatQty(candidateTotal.deltaQty)}</strong>
                    <em>{formatPercent(candidateTotal.deltaPct)}</em>
                  </div>
                  <p>{t('xDemand.cand.policyText')}</p>
                </div>
                <div>
                  <div className="df-candidate-tools">
                    {['All', 'Review', 'Watch', 'OK'].map((status) => (
                      <button
                        type="button"
                        key={status}
                        className={candidateStatusFilter === status ? 'active' : ''}
                        onClick={() => setCandidateStatusFilter(status)}
                      >
                        {tv(status)} {status === 'All' ? formatQty(candidateBranchRows.length) : formatQty(candidateStatusCounts[status] || 0)}
                      </button>
                    ))}
                  </div>
                  <div className="df-table-wrap df-candidate-table">
                    <table className="df-table">
                      <thead>
                        <tr>
                          <th>{t('xDemand.col.branch')}</th>
                          <th>{t('xDemand.col.channel')}</th>
                          <th className="num">{t('xDemand.col.candidate')}</th>
                          <th className="num">{t('xDemand.col.current')}</th>
                          <th className="num">{t('xDemand.col.delta')}</th>
                          <th className="num">{t('xDemand.col.deltaPct')}</th>
                          <th>{t('xDemand.col.status')}</th>
                          <th>{t('xDemand.col.reason')}</th>
                          <th className="num">{t('xDemand.col.recentRunRate')}</th>
                          <th className="num">{t('xDemand.col.seasonalRecent')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {candidateVisibleBranchRows.map((row) => (
                          <tr key={`${row.branch}-${row.channel}`}>
                            <td>{row.branch}</td>
                            <td>{row.channel}</td>
                            <td className="num">{formatQty(row.candidateQty)}</td>
                            <td className="num">{formatQty(row.forecastQty)}</td>
                            <td className="num">{formatQty(row.deltaQty)}</td>
                            <td className="num">{formatPercent(row.deltaPct)}</td>
                            <td><span className={`df-status ${String(row.diagnosticStatus || '').toLowerCase()}`}>{tv(row.diagnosticStatus) || '-'}</span></td>
                            <td className="df-reason-cell">{candidateReason(row)}</td>
                            <td className="num">{formatQty(row.recentRunRateQty)}</td>
                            <td className="num">{formatPercent(row.seasonalVsRecentRatio)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div className="df-panel df-full df-branch-panel">
              <div className="df-panel-head">
                <span>{t('xDemand.level.branch_channel')}</span>
                <em>{t('xDemand.bc.top10')}</em>
              </div>
              <div className="df-bars">
                {branchRows.slice(0, 10).map((row, index) => {
                  const max = Number(branchRows[0]?.forecastQty) || 1;
                  return (
                    <div className="df-bar-row" key={`${row.branch}-${row.channel}`}>
                      <div>
                        <strong>{row.branch}</strong>
                        <span>{row.channel}</span>
                      </div>
                      <div className="df-bar-track">
                        <span style={{ width: `${Math.max(4, (Number(row.forecastQty) / max) * 100)}%` }} />
                      </div>
                      <em>{formatQty(row.forecastQty)}</em>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="df-panel">
              <div className="df-panel-head">
                <span>{t('xDemand.conf.title')}</span>
                <em>{t('xDemand.conf.note')}</em>
              </div>
              <div className="df-confidence-list">
                {confidenceRows.map((row) => {
                  const share = confidenceTotalQty > 0 ? (Number(row.forecastQty) || 0) / confidenceTotalQty : 0;
                  return (
                    <div className={`df-confidence-row ${String(row.confidence || '').toLowerCase()}`} key={row.confidence}>
                      <div>
                        <strong>{tv(row.confidence)}</strong>
                        <span>{confidenceDescriptions[row.confidence] || t('xDemand.conf.bucket')}</span>
                      </div>
                      <div className="df-confidence-metrics">
                        <em>{t('xDemand.u.qty', { n: formatQty(row.forecastQty) })}</em>
                        <em>{t('xDemand.u.rows', { n: formatQty(row.rows) })}</em>
                        <b>{formatPercent(share)}</b>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {activeTab === 'sku' && (
          <section className="df-grid">
            <div className="df-panel df-full">
              <div className="df-panel-head">
                <span>{t('xDemand.sku.title')}</span>
                <em>{detailRows.length === detailTotalRows ? t('xDemand.u.rows', { n: formatQty(detailRows.length) }) : t('xDemand.sku.rowsOf', { shown: formatQty(detailRows.length), total: formatQty(detailTotalRows) })}</em>
              </div>
              <div className="df-table-wrap">
                <table className="df-table">
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>{t('xDemand.col.productName')}</th>
                      <th>{t('xDemand.col.branch')}</th>
                      <th>{t('xDemand.col.channel')}</th>
                      <th>{t('xDemand.col.collection')}</th>
                      <th>{t('xDemand.col.category')}</th>
                      <th className="num">{t('xDemand.col.forecast')}</th>
                      <th className="num">{t('xDemand.col.lower')}</th>
                      <th className="num">{t('xDemand.col.upper')}</th>
                      <th>{t('xDemand.col.confidence')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailRows.map((row) => (
                      <tr key={`${row.sku}-${row.branch}-${row.channel}`}>
                        <td>{row.sku}</td>
                        <td className="df-product-name" title={row.productDescription || row.frameModel || row.sku}>{row.productDescription || row.frameModel || row.sku}</td>
                        <td>{row.branch}</td>
                        <td>{row.channel}</td>
                        <td>{row.productCollection}</td>
                        <td>{row.productCategory}</td>
                        <td className="num">{formatQty(row.forecastQty)}</td>
                        <td className="num">{formatQty(row.lowerQty)}</td>
                        <td className="num">{formatQty(row.upperQty)}</td>
                        <td><span className={`df-pill ${String(row.confidence || '').toLowerCase()}`}>{tv(row.confidence) || '-'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="df-panel df-full">
              <div className="df-panel-head">
                <span>{t('xDemand.rec.title')}</span>
                <div className="df-panel-actions">
                  <em>{t('xDemand.rec.note')}</em>
                  <button type="button" onClick={exportPlanningOutput} disabled={!recommendedVisibleOutputRows.length}>
                    <Download size={14} />
                    {t('xDemand.rec.planningCsv')}
                  </button>
                  <button type="button" onClick={exportRecommendedOutput} disabled={!recommendedOutputRows.length}>
                    <Download size={14} />
                    {t('xDemand.btn.exportCsv')}
                  </button>
                </div>
              </div>
              <div className="df-v3-summary">
                <div>
                  <span>{t('xDemand.cand.currentTotal')}</span>
                  <strong>{formatQty(recommendedOutputSummary.currentTotalQty)}</strong>
                  <em>{t('xDemand.rec.productionBaseline')}</em>
                </div>
                <div>
                  <span>{t('xDemand.rec.recommendedTotal')}</span>
                  <strong>{formatQty(recommendedOutputSummary.recommendedTotalQty)}</strong>
                  <em>{t('xDemand.rec.reviewOutput')}</em>
                </div>
                <div>
                  <span>{t('xDemand.cand.difference')}</span>
                  <strong>{formatQty(recommendedOutputSummary.deltaQty)}</strong>
                  <em>{formatPercent(recommendedOutputSummary.deltaPct)}</em>
                </div>
                <div>
                  <span>{t('xDemand.rec.reviewWatch')}</span>
                  <strong>{formatQty(recommendedOutputSummary.reviewRows)} / {formatQty(recommendedOutputSummary.watchRows)}</strong>
                  <em>{t('xDemand.rec.keepCurrentN', { n: formatQty(recommendedOutputSummary.keepCurrentRows) })}</em>
                </div>
                <div>
                  <span>{t('xDemand.col.modelAlignment')}</span>
                  <strong>{formatQty(recommendedOutputModelAlignmentCounts.Aligned)} / {formatQty(recommendedOutputModelAlignmentCounts.Review)}</strong>
                  <em>{t('xDemand.rec.alignedReview')}</em>
                </div>
                <div>
                  <span>{t('xDemand.col.reviewPriority')}</span>
                  <strong>{formatQty(recommendedOutputPriorityCounts.P1)} / {formatQty(recommendedOutputPriorityCounts.P2)}</strong>
                  <em>{t('xDemand.rec.p1p2')}</em>
                </div>
              </div>
              <div className={`df-output-governance ${recommendedGovernanceDecision.tone}`}>
                <div className="df-output-governance-head">
                  <div>
                    <span>{t('xDemand.rec.decisionPolicy')}</span>
                    <strong>{tv(recommendedGovernanceDecision.label)}</strong>
                  </div>
                  <em>{t('xDemand.rec.unchanged')}</em>
                </div>
                <div className="df-output-policy-grid">
                  {recommendedPolicyRows.map((row) => (
                    <div className={`df-output-policy-card ${row.tone}`} key={row.label}>
                      <span>{row.label}</span>
                      <strong>{row.value}</strong>
                      <em>{row.detail}</em>
                    </div>
                  ))}
                </div>
              </div>
              <div className="df-candidate-tools">
                {['All', 'Review', 'Watch', 'Keep current'].map((status) => (
                  <button
                    type="button"
                    key={status}
                    className={recommendedOutputStatusFilter === status ? 'active' : ''}
                    onClick={() => setRecommendedOutputStatusFilter(status)}
                  >
                    {tv(status)} {status === 'All' ? formatQty(recommendedOutputRows.length) : formatQty(recommendedOutputStatusCounts[status] || 0)}
                  </button>
                ))}
              </div>
              <div className="df-candidate-tools df-direction-tools">
                {['All', 'Increase forecast', 'Decrease forecast', 'Keep current'].map((direction) => (
                  <button
                    type="button"
                    key={direction}
                    className={recommendedOutputDirectionFilter === direction ? 'active' : ''}
                    onClick={() => setRecommendedOutputDirectionFilter(direction)}
                  >
                    {direction === 'All' ? t('xDemand.rec.allDirection') : tv(direction)} {direction === 'All' ? formatQty(recommendedOutputRows.length) : formatQty(recommendedOutputDirectionCounts[direction] || 0)}
                  </button>
                ))}
              </div>
              <div className="df-candidate-tools df-alignment-tools">
                {['All', 'Aligned', 'Review'].map((alignment) => (
                  <button
                    type="button"
                    key={alignment}
                    className={recommendedOutputAlignmentFilter === alignment ? 'active' : ''}
                    onClick={() => setRecommendedOutputAlignmentFilter(alignment)}
                  >
                    {alignment === 'All' ? t('xDemand.rec.allAlignment') : tv(alignment)} {alignment === 'All' ? formatQty(recommendedOutputRows.length) : formatQty(recommendedOutputModelAlignmentCounts[alignment] || 0)}
                  </button>
                ))}
              </div>
              {(recommendedOutputGroupFilter || recommendedOutputReasonFilter) && (
                <div className="df-active-filters">
                  {recommendedOutputGroupFilter && (
                    <button type="button" onClick={() => setRecommendedOutputGroupFilter(null)}>
                      {t('xDemand.rec.filterGroup', { value: recommendedOutputGroupFilter.replace('|', ' / ') })}
                    </button>
                  )}
                  {recommendedOutputReasonFilter && (
                    <button type="button" onClick={() => setRecommendedOutputReasonFilter(null)}>
                      {t('xDemand.rec.filterReason', { value: tv(recommendedOutputReasonFilter) })}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setRecommendedOutputGroupFilter(null);
                      setRecommendedOutputReasonFilter(null);
                    }}
                  >
                    {t('xDemand.rec.clearDrill')}
                  </button>
                </div>
              )}
              <div className="df-review-summary df-priority-queue">
                <div className="df-review-summary-head">
                  <span>{t('xDemand.rec.queue')}</span>
                  <em>{t('xDemand.rec.queueNote', { n: formatQty(recommendedPriorityRows.length) })}</em>
                </div>
                <div className="df-priority-queue-grid">
                  {recommendedPriorityRows.map((row) => (
                    <div className="df-priority-row" key={`${row.sku}-${row.branch}-${row.channel}-priority`}>
                      <div className="df-priority-row-head">
                        <span className={`df-priority-badge ${String(row.reviewPriority || 'P3').toLowerCase()}`}>{row.reviewPriority}</span>
                        <strong title={row.productDescription || row.sku}>{row.productDescription || row.sku}</strong>
                        <em>{row.sku}</em>
                      </div>
                      <div className="df-priority-row-meta">
                        <span>{row.branch} / {row.channel}</span>
                        <span>{tv(row.outputStatus) || '-'}</span>
                        <span>{row.modelAligned ? t('xDemand.rec.modelAligned') : t('xDemand.rec.modelReview')}</span>
                      </div>
                      <div className="df-priority-row-metrics">
                        <div>
                          <span>{t('xDemand.col.current')}</span>
                          <strong>{formatQty(row.currentForecastQty)}</strong>
                        </div>
                        <div>
                          <span>{t('xDemand.col.recommended')}</span>
                          <strong>{formatQty(row.recommendedForecastQty)}</strong>
                        </div>
                        <div>
                          <span>{t('xDemand.col.delta')}</span>
                          <strong>{formatQty(row.deltaQty)}</strong>
                        </div>
                        <div>
                          <span>{t('xDemand.col.deltaPct')}</span>
                          <strong>{formatPercent(row.deltaPct)}</strong>
                        </div>
                      </div>
                      <p title={row.outputReason || '-'}>
                        {row.outputReason || '-'}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="df-review-summary df-recommended-summary">
                <div className="df-review-summary-head">
                  <span>{t('xDemand.rec.byGroup')}</span>
                  <em>{t('xDemand.rec.groupsN', { n: formatQty(recommendedOutputGroupRows.length) })}</em>
                </div>
                <div className="df-review-summary-grid">
                  {recommendedOutputGroupRows.slice(0, 8).map((row) => (
                    <button
                      type="button"
                      className={`df-review-card df-review-card-button ${recommendedOutputGroupFilter === `${row.branch}|${row.channel}` ? 'active' : ''}`}
                      key={`${row.branch}-${row.channel}-recommended-summary`}
                      onClick={() => {
                        const key = `${row.branch}|${row.channel}`;
                        setRecommendedOutputGroupFilter((current) => (current === key ? null : key));
                      }}
                    >
                      <div className="df-review-card-top">
                        <div>
                          <strong>{row.branch}</strong>
                          <span>{row.channel}</span>
                        </div>
                        <em className={`df-priority ${row.priority.toLowerCase()}`}>{tv(row.priority)}</em>
                      </div>
                      <div className="df-review-card-metrics">
                        <div>
                          <span>{t('xDemand.v.review')}</span>
                          <strong>{formatQty(row.reviewRows)}</strong>
                        </div>
                        <div>
                          <span>{t('xDemand.v.watch')}</span>
                          <strong>{formatQty(row.watchRows)}</strong>
                        </div>
                        <div>
                          <span>{t('xDemand.col.delta')}</span>
                          <strong>{formatQty(row.deltaQty)}</strong>
                        </div>
                        <div>
                          <span>{t('xDemand.col.deltaPct')}</span>
                          <strong>{formatPercent(row.deltaPct)}</strong>
                        </div>
                        <div>
                          <span>{t('xDemand.rec.increase')}</span>
                          <strong>{formatQty(row.increaseRows)}</strong>
                        </div>
                        <div>
                          <span>{t('xDemand.rec.decrease')}</span>
                          <strong>{formatQty(row.decreaseRows)}</strong>
                        </div>
                      </div>
                      <p>
                        {t('xDemand.rec.recVsCurrent', { rec: formatQty(row.recommendedQty), cur: formatQty(row.currentQty) })}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
              <div className="df-review-summary df-reason-summary">
                <div className="df-review-summary-head">
                  <span>{t('xDemand.rec.byReason')}</span>
                  <em>{t('xDemand.rec.reasonsN', { n: formatQty(recommendedReasonRows.length) })}</em>
                </div>
                <div className="df-review-summary-grid df-reason-grid">
                  {recommendedReasonRows.slice(0, 8).map((row) => (
                    <button
                      type="button"
                      className={`df-review-card df-review-card-button ${recommendedOutputReasonFilter === row.reason ? 'active' : ''}`}
                      key={`recommended-reason-${row.reason}`}
                      onClick={() => setRecommendedOutputReasonFilter((current) => (current === row.reason ? null : row.reason))}
                    >
                      <div className="df-review-card-top">
                        <div>
                          <strong title={tv(row.reason)}>{tv(row.reason)}</strong>
                          <span>{t('xDemand.u.rows', { n: formatQty(row.rows) })}</span>
                        </div>
                        <em className={`df-priority ${row.priority.toLowerCase()}`}>{tv(row.priority)}</em>
                      </div>
                      <div className="df-review-card-metrics">
                        <div>
                          <span>{t('xDemand.v.review')}</span>
                          <strong>{formatQty(row.reviewRows)}</strong>
                        </div>
                        <div>
                          <span>{t('xDemand.v.watch')}</span>
                          <strong>{formatQty(row.watchRows)}</strong>
                        </div>
                        <div>
                          <span>{t('xDemand.col.delta')}</span>
                          <strong>{formatQty(row.deltaQty)}</strong>
                        </div>
                        <div>
                          <span>{t('xDemand.col.deltaPct')}</span>
                          <strong>{formatPercent(row.deltaPct)}</strong>
                        </div>
                        <div>
                          <span>{t('xDemand.rec.increase')}</span>
                          <strong>{formatQty(row.increaseRows)}</strong>
                        </div>
                        <div>
                          <span>{t('xDemand.rec.decrease')}</span>
                          <strong>{formatQty(row.decreaseRows)}</strong>
                        </div>
                      </div>
                      <p>
                        {t('xDemand.rec.recVsCurrent', { rec: formatQty(row.recommendedQty), cur: formatQty(row.currentQty) })}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
              <div className="df-table-wrap df-recommended-output-table">
                <table className="df-table">
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>{t('xDemand.col.productName')}</th>
                      <th>{t('xDemand.col.branch')}</th>
                      <th>{t('xDemand.col.channel')}</th>
                      <th>{t('xDemand.col.collection')}</th>
                      <th>{t('xDemand.col.category')}</th>
                      <th className="num">{t('xDemand.col.current')}</th>
                      <th className="num">{t('xDemand.col.candidateV3')}</th>
                      <th className="num">{t('xDemand.col.recommended')}</th>
                      <th className="num">{t('xDemand.col.delta')}</th>
                      <th className="num">{t('xDemand.col.deltaPct')}</th>
                      <th>{t('xDemand.col.model')}</th>
                      <th>{t('xDemand.col.bcModelShort')}</th>
                      <th>{t('xDemand.col.alignment')}</th>
                      <th>{t('xDemand.col.priority')}</th>
                      <th>{t('xDemand.col.status')}</th>
                      <th>{t('xDemand.col.direction')}</th>
                      <th>{t('xDemand.col.reason')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recommendedVisibleOutputRows.map((row) => (
                      <tr key={`${row.sku}-${row.branch}-${row.channel}-recommended`}>
                        <td>{row.sku}</td>
                        <td className="df-product-name" title={row.productDescription || row.sku}>{row.productDescription || row.sku}</td>
                        <td>{row.branch}</td>
                        <td>{row.channel}</td>
                        <td>{row.productCollection}</td>
                        <td>{row.productCategory}</td>
                        <td className="num">{formatQty(row.currentForecastQty)}</td>
                        <td className="num">{formatQty(row.candidateV3ForecastQty)}</td>
                        <td className="num">{formatQty(row.recommendedForecastQty)}</td>
                        <td className="num">{formatQty(row.deltaQty)}</td>
                        <td className="num">{formatPercent(row.deltaPct)}</td>
                        <td className="df-model-cell" title={row.recommendedModel}>{modelLabel(row.recommendedModel)}</td>
                        <td className="df-model-cell" title={row.bcRecommendedModel || '-'}>{modelLabel(row.bcRecommendedModel)}</td>
                        <td>
                          <span className={`df-status ${row.modelAligned ? 'ok' : 'review'}`}>
                            {row.modelAligned ? t('xDemand.v.aligned') : t('xDemand.v.review')}
                          </span>
                        </td>
                        <td><span className={`df-priority-badge ${String(row.reviewPriority || 'P3').toLowerCase()}`}>{row.reviewPriority}</span></td>
                        <td><span className={`df-status ${String(row.outputStatus || '').toLowerCase().replace(' ', '-')}`}>{tv(row.outputStatus) || '-'}</span></td>
                        <td><span className={`df-direction ${String(row.outputDirection || '').toLowerCase().replaceAll(' ', '-')}`}>{tv(row.outputDirection) || '-'}</span></td>
                        <td className="df-reason-cell" title={row.outputReason || '-'}>{row.outputReason || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="df-panel df-full">
              <div className="df-panel-head">
                <span>{t('xDemand.csku.title')}</span>
                <div className="df-panel-actions">
                  <em>{t('xDemand.csku.note', { n: formatQty(candidateSkuRows.length) })}</em>
                  <button type="button" onClick={exportCandidateSkuReview} disabled={!candidateVisibleSkuRows.length}>
                    <Download size={14} />
                    {t('xDemand.btn.exportCsv')}
                  </button>
                </div>
              </div>
              <div className="df-candidate-tools">
                {['All', 'Review', 'Watch', 'OK'].map((status) => (
                  <button
                    type="button"
                    key={status}
                    className={candidateSkuStatusFilter === status ? 'active' : ''}
                    onClick={() => setCandidateSkuStatusFilter(status)}
                  >
                    {tv(status)} {status === 'All' ? formatQty(candidateSkuReviewRows.length) : formatQty(candidateSkuStatusCounts[status] || 0)}
                  </button>
                ))}
              </div>
              <div className="df-v3-summary">
                <div>
                  <span>{t('xDemand.cand.candidateV2')}</span>
                  <strong>{formatQty(candidateV3Summary.candidateV2TotalQty)}</strong>
                  <em>{t('xDemand.csku.seasonalityBaseline')}</em>
                </div>
                <div>
                  <span>{t('xDemand.col.candidateV3')}</span>
                  <strong>{formatQty(candidateV3Summary.candidateV3TotalQty)}</strong>
                  <em>{t('xDemand.csku.dataSignalAdjusted')}</em>
                </div>
                <div>
                  <span>{t('xDemand.csku.v3vsCurrent')}</span>
                  <strong>{formatQty(candidateV3Summary.candidateV3DeltaVsCurrentQty)}</strong>
                  <em>{formatPercent(candidateV3Summary.candidateV3DeltaVsCurrentPct)}</em>
                </div>
                <div>
                  <span>{t('xDemand.csku.v3vsV2')}</span>
                  <strong>{formatQty(candidateV3Summary.candidateV3DeltaVsV2Qty)}</strong>
                  <em>{formatPercent(candidateV3Summary.candidateV3DeltaVsV2Pct)}</em>
                </div>
              </div>
              <div className="df-review-summary">
                <div className="df-review-summary-head">
                  <span>{t('xDemand.rec.byGroup')}</span>
                  <em>{t('xDemand.csku.groupsWithReview', { n: formatQty(candidateReviewSummaryRows.length) })}</em>
                </div>
                <div className="df-review-summary-grid">
                  {candidateReviewSummaryRows.slice(0, 8).map((row) => (
                    <div className="df-review-card" key={`${row.branch}-${row.channel}`}>
                      <div className="df-review-card-top">
                        <div>
                          <strong>{row.branch}</strong>
                          <span>{row.channel}</span>
                        </div>
                        <em className={`df-priority ${row.priority.toLowerCase()}`}>{tv(row.priority)}</em>
                      </div>
                      <div className="df-review-card-metrics">
                        <div>
                          <span>{t('xDemand.v.review')}</span>
                          <strong>{formatQty(row.reviewSkuCount)}</strong>
                        </div>
                        <div>
                          <span>{t('xDemand.v.watch')}</span>
                          <strong>{formatQty(row.watchSkuCount)}</strong>
                        </div>
                        <div>
                          <span>{t('xDemand.col.delta')}</span>
                          <strong>{formatQty(row.deltaQty)}</strong>
                        </div>
                        <div>
                          <span>{t('xDemand.col.v3Delta')}</span>
                          <strong>{formatQty(row.v3DeltaQty)}</strong>
                        </div>
                      </div>
                      <p>
                        {t('xDemand.csku.cardText', { v2: formatQty(row.candidateQty), v3: formatQty(row.candidateV3Qty), cur: formatQty(row.currentQty) })}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="df-table-wrap df-candidate-sku-table">
                <table className="df-table">
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>{t('xDemand.col.productName')}</th>
                      <th>{t('xDemand.col.branch')}</th>
                      <th>{t('xDemand.col.channel')}</th>
                      <th>{t('xDemand.col.collection')}</th>
                      <th>{t('xDemand.col.category')}</th>
                      <th className="num">V2</th>
                      <th className="num">V3</th>
                      <th className="num">{t('xDemand.col.current')}</th>
                      <th className="num">{t('xDemand.col.v3Delta')}</th>
                      <th className="num">{t('xDemand.col.v3DeltaPct')}</th>
                      <th>{t('xDemand.col.status')}</th>
                      <th>{t('xDemand.col.reason')}</th>
                      <th>{t('xDemand.col.dataSignal')}</th>
                      <th>{t('xDemand.col.v3Rule')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidateVisibleSkuRows.map((row) => (
                      <tr key={`${row.sku}-${row.branch}-${row.channel}`}>
                        <td>{row.sku}</td>
                        <td className="df-product-name" title={row.productDescription || row.sku}>{row.productDescription || row.sku}</td>
                        <td>{row.branch}</td>
                        <td>{row.channel}</td>
                        <td>{row.productCollection}</td>
                        <td>{row.productCategory}</td>
                        <td className="num">{formatQty(row.candidateForecastQty)}</td>
                        <td className="num">{formatQty(row.candidateV3ForecastQty)}</td>
                        <td className="num">{formatQty(row.currentForecastQty)}</td>
                        <td className="num">{formatQty(row.v3DeltaVsCurrentQty)}</td>
                        <td className="num">{formatPercent(row.v3DeltaVsCurrentPct)}</td>
                        <td><span className={`df-status ${String(row.diagnosticStatus || '').toLowerCase()}`}>{tv(row.diagnosticStatus) || '-'}</span></td>
                        <td className="df-reason-cell">{row.diagnosticReason}</td>
                        <td className="df-signal-cell" title={[
                          t('xDemand.csku.sigRecent12', { n: formatQty(row.recent12ActualQty) }),
                          t('xDemand.csku.sigActiveWeeks', { n: formatQty(row.recent12ActiveWeeks) }),
                          t('xDemand.csku.sigPriorYear', { n: formatQty(row.priorYearSameHorizonQty) }),
                        ].join(' | ')}
                        >
                          {row.dataSignal || '-'}
                        </td>
                        <td className="df-signal-cell" title={row.candidateV3Rule || '-'}>
                          {row.candidateV3Rule || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {activeTab === 'backtest' && (
          <section className="df-grid">
            <div className="df-panel df-full df-backtest-guide">
              <div className="df-panel-head">
                <span>{t('xDemand.bt.summary')}</span>
                <em>{t('xDemand.bt.lastWeeks', { n: backtestWeeks.length || 8 })}</em>
              </div>
              <div className="df-model-note">
                <strong>{t('xDemand.bt.modelUsed')}</strong>
                <span title={backtestModelName}>{backtestModelLabel}</span>
              </div>
              <div className="df-guide-grid">
                <div>
                  <strong>WAPE</strong>
                  <span>{t('xDemand.bt.wapeText')}</span>
                </div>
                <div>
                  <strong>{t('xDemand.col.bias')}</strong>
                  <span>{t('xDemand.bt.biasText')}</span>
                </div>
                <div>
                  <strong>{t('xDemand.bt.level')}</strong>
                  <span>{t('xDemand.bt.levelText')}</span>
                </div>
              </div>
            </div>
            <div className="df-panel df-full">
              <div className="df-panel-head">
                <span>{t('xDemand.p8.title')}</span>
                <em>{t('xDemand.p8.note')}</em>
              </div>
              <div className="df-phase8-banner">
                <div>
                  <span>{t('xDemand.p8.finalLogic')}</span>
                  <strong>{t('xDemand.p8.finalLogicValue')}</strong>
                </div>
                <p>{t('xDemand.p8.bestOption')}</p>
              </div>
              <div className={`df-phase8-guardrail ${phase8GuardrailDecision.tone}`}>
                <div>
                  <span>{t('xDemand.p8.runGuardrail')}</span>
                  <strong>{tv(phase8GuardrailDecision.label)}</strong>
                  <em>{phase8GuardrailDecision.action}</em>
                </div>
                <p>{phase8GuardrailDecision.detail}</p>
                <div className="df-phase8-guardrail-metrics">
                  <div>
                    <span>{t('xDemand.rh.totalDelta')}</span>
                    <strong>{Number.isFinite(runDeltaPct) ? formatPercent(runDeltaPct) : '-'}</strong>
                  </div>
                  <div>
                    <span>{t('xDemand.p8.branchMovers')}</span>
                    <strong>{formatQty(branchSevereMoverCount)}</strong>
                  </div>
                  <div>
                    <span>{t('xDemand.hr.sku')}</span>
                    <strong>{formatQty(skuSevereMoverCount)}</strong>
                  </div>
                </div>
              </div>
              <div className="df-phase8-weekly-checklist">
                <div className="df-feature-mini-head">
                  <strong>{t('xDemand.p8.weeklyChecklist')}</strong>
                  <span>{t('xDemand.p8.weeklyChecklistNote')}</span>
                </div>
                <div className="df-phase8-check-grid">
                  {phase8WeeklyChecklistRows.map((row) => (
                    <div key={`phase8-weekly-${row.label}`} className={row.tone}>
                      <span>{row.label}</span>
                      <strong>{tv(row.status)}</strong>
                      <em>{row.detail}</em>
                    </div>
                  ))}
                </div>
              </div>
              <div className="df-phase8-hold-detail">
                <div className="df-feature-mini-head">
                  <strong>{t('xDemand.p8.holdDetail')}</strong>
                  <div className="df-panel-actions">
                    <span>{phase8GuardrailDecision.label === 'Hold' ? t('xDemand.p8.holdReviewThese') : t('xDemand.p8.guardrailDiagnostics')}</span>
                    <button type="button" onClick={exportPhase8HoldReview} disabled={!phase8HoldSkuRows.length}>
                      <Download size={14} />
                      {t('xDemand.p8.exportHold')}
                    </button>
                  </div>
                </div>
                <div className="df-phase8-reason-grid">
                  {phase8HoldReasonRows.map((row) => (
                    <div key={`phase8-hold-reason-${row.label}`} className={String(row.status || '').toLowerCase()}>
                      <span>{row.label}</span>
                      <strong>{tv(row.status)}</strong>
                      <em>{row.detail}</em>
                    </div>
                  ))}
                </div>
                <div className="df-phase8-action-grid">
                  {Object.entries(phase8HoldActionCounts).map(([action, count]) => (
                    <div key={`phase8-action-${action}`}>
                      <span>{tv(action)}</span>
                      <strong>{formatQty(count)}</strong>
                      <em>{t('xDemand.u.skuMovers')}</em>
                    </div>
                  ))}
                  {!phase8HoldSkuRows.length && (
                    <div>
                      <span>{t('xDemand.p8.noReviewMovers')}</span>
                      <strong>0</strong>
                      <em>{t('xDemand.p8.allInside')}</em>
                    </div>
                  )}
                </div>
                <div className="df-phase8-run-grid">
                  <div>
                    <div className="df-feature-mini-head">
                      <strong>{t('xDemand.p8.reviewGroups')}</strong>
                      <span>{t('xDemand.p8.byBranchChannel')}</span>
                    </div>
                    <div className="df-table-wrap df-phase8-mini-table">
                      <table className="df-table">
                        <thead>
                          <tr>
                            <th>{t('xDemand.col.branch')}</th>
                            <th>{t('xDemand.col.channel')}</th>
                            <th>{t('xDemand.col.skus')}</th>
                            <th>{t('xDemand.col.current')}</th>
                            <th>{t('xDemand.col.previous')}</th>
                            <th>{t('xDemand.col.delta')}</th>
                            <th>{t('xDemand.col.largestSku')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {phase8HoldGroupRows.map((row) => (
                            <tr key={`phase8-hold-group-${row.branch}-${row.channel}`}>
                              <td>{row.branch}</td>
                              <td>{row.channel}</td>
                              <td>{formatQty(row.reviewCount)}</td>
                              <td>{formatQty(row.currentForecastQty)}</td>
                              <td>{formatQty(row.previousForecastQty)}</td>
                              <td>{formatQty(row.deltaQty)}</td>
                              <td>{row.largestSku}</td>
                            </tr>
                          ))}
                          {!phase8HoldGroupRows.length && (
                            <tr><td colSpan="7">{t('xDemand.p8.noHoldGroups')}</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div>
                    <div className="df-feature-mini-head">
                      <strong>{t('xDemand.p8.topSkuActions')}</strong>
                      <span>{t('xDemand.p8.top12')}</span>
                    </div>
                    <div className="df-table-wrap df-phase8-mini-table">
                      <table className="df-table">
                        <thead>
                          <tr>
                            <th>SKU</th>
                            <th>{t('xDemand.col.location')}</th>
                            <th>{t('xDemand.col.current')}</th>
                            <th>{t('xDemand.col.previous')}</th>
                            <th>{t('xDemand.col.delta')}</th>
                            <th>{t('xDemand.col.action')}</th>
                            <th>{t('xDemand.col.reason')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {phase8HoldSkuRows.slice(0, 12).map((row) => (
                            <tr key={`phase8-hold-sku-${row.sku}-${row.branch}-${row.channel}`}>
                              <td>{row.sku || '-'}</td>
                              <td>{[row.branch, row.channel].filter(Boolean).join(' / ') || '-'}</td>
                              <td>{formatQty(row.currentForecastQty)}</td>
                              <td>{formatQty(row.previousForecastQty)}</td>
                              <td>{formatQty(row.deltaQty)}</td>
                              <td><span className={`df-model-pill ${row.tone}`}>{tv(row.action)}</span></td>
                              <td className="df-reason-cell" title={row.reason}>{row.reason}</td>
                            </tr>
                          ))}
                          {!phase8HoldSkuRows.length && (
                            <tr><td colSpan="8">{t('xDemand.p8.noSkuAction')}</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              <div className="df-phase8-grid">
                {phase8WorkflowRows.map((row) => (
                  <div key={`phase8-workflow-${row.label}`} className={row.tone}>
                    <span>{row.label}</span>
                    <strong>{row.value}</strong>
                    <em>{row.detail}</em>
                  </div>
                ))}
              </div>
              <div className="df-feature-mini-head">
                <strong>{t('xDemand.p8.controls')}</strong>
                <span>{t('xDemand.p8.controlsReady', { ready: formatQty(phase8ReadyControls), total: formatQty(phase8ControlRows.length) })}</span>
              </div>
              <div className="df-table-wrap df-phase8-control-table">
                <table className="df-table">
                  <thead>
                    <tr>
                      <th>{t('xDemand.col.control')}</th>
                      <th>{t('xDemand.col.status')}</th>
                      <th>{t('xDemand.col.detail')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {phase8ControlRows.map((row) => (
                      <tr key={`phase8-control-${row.label}`}>
                        <td>{row.label}</td>
                        <td><span className={`df-model-pill ${row.tone}`}>{tv(row.status)}</span></td>
                        <td className="df-reason-cell" title={row.detail}>{row.detail}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="df-feature-mini-head">
                <strong>{t('xDemand.p8.runHistory')}</strong>
                <span>{hasPreviousRun ? t('xDemand.p8.runHistoryCompared') : t('xDemand.p8.runHistoryWaiting')}</span>
              </div>
              <div className="df-phase8-run-summary">
                {runHistorySummaryRows.map((row) => (
                  <div key={`run-history-summary-${row.label}`}>
                    <span>{row.label}</span>
                    <strong title={row.value}>{row.value}</strong>
                    <em>{row.detail}</em>
                  </div>
                ))}
              </div>
              <div className="df-phase8-run-grid">
                <div>
                  <div className="df-feature-mini-head">
                    <strong>{t('xDemand.p8.recentRuns')}</strong>
                    <span>{t('xDemand.p8.latest5')}</span>
                  </div>
                  <div className="df-table-wrap df-phase8-mini-table">
                    <table className="df-table">
                      <thead>
                        <tr>
                          <th>{t('xDemand.col.generated')}</th>
                          <th>{t('xDemand.col.runId')}</th>
                          <th>{t('xDemand.col.forecast')}</th>
                          <th>{t('xDemand.col.skus')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {runHistoryRuns.slice(0, 5).map((row) => (
                          <tr key={`run-history-${row.forecastRunId}`}>
                            <td>{formatDateTime(row.generatedAt)}</td>
                            <td title={row.forecastRunId}>{String(row.forecastRunId || '-').slice(0, 12)}</td>
                            <td>{formatQty(row.forecastQty)}</td>
                            <td>{formatQty(row.skuCount)}</td>
                          </tr>
                        ))}
                        {!runHistoryRuns.length && (
                          <tr><td colSpan="4">{t('xDemand.p8.noRunHistory')}</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div>
                  <div className="df-feature-mini-head">
                    <strong>{t('xDemand.p8.bcMovement')}</strong>
                    <span>{t('xDemand.p8.top10Abs')}</span>
                  </div>
                  <div className="df-table-wrap df-phase8-mini-table">
                    <table className="df-table">
                      <thead>
                        <tr>
                          <th>{t('xDemand.col.branch')}</th>
                          <th>{t('xDemand.col.channel')}</th>
                          <th>{t('xDemand.col.current')}</th>
                          <th>{t('xDemand.col.previous')}</th>
                          <th>{t('xDemand.col.delta')}</th>
                          <th>{t('xDemand.col.deltaPct')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {runHistoryBranchRows.slice(0, 10).map((row) => (
                          <tr key={`run-bc-${row.branch}-${row.channel}`}>
                            <td>{row.branch || '-'}</td>
                            <td>{row.channel || '-'}</td>
                            <td>{formatQty(row.currentForecastQty)}</td>
                            <td>{formatQty(row.previousForecastQty)}</td>
                            <td>{formatQty(row.deltaQty)}</td>
                            <td>{formatPercent(row.deltaPct)}</td>
                          </tr>
                        ))}
                        {!runHistoryBranchRows.length && (
                          <tr><td colSpan="6">{t('xDemand.p8.noPrevMovement')}</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div className="df-feature-mini-head">
                <strong>{t('xDemand.p8.skuMovement')}</strong>
                <span>{t('xDemand.p8.top15Abs')}</span>
              </div>
              <div className="df-table-wrap df-phase8-sku-table">
                <table className="df-table">
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>{t('xDemand.col.branch')}</th>
                      <th>{t('xDemand.col.channel')}</th>
                      <th>{t('xDemand.col.collection')}</th>
                      <th>{t('xDemand.col.category')}</th>
                      <th>{t('xDemand.col.current')}</th>
                      <th>{t('xDemand.col.previous')}</th>
                      <th>{t('xDemand.col.delta')}</th>
                      <th>{t('xDemand.col.deltaPct')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runHistorySkuRows.slice(0, 15).map((row) => (
                      <tr key={`run-sku-${row.sku}-${row.branch}-${row.channel}`}>
                        <td>{row.sku || '-'}</td>
                        <td>{row.branch || '-'}</td>
                        <td>{row.channel || '-'}</td>
                        <td>{row.productCollection || '-'}</td>
                        <td>{row.productCategory || '-'}</td>
                        <td>{formatQty(row.currentForecastQty)}</td>
                        <td>{formatQty(row.previousForecastQty)}</td>
                        <td>{formatQty(row.deltaQty)}</td>
                        <td>{formatPercent(row.deltaPct)}</td>
                      </tr>
                    ))}
                    {!runHistorySkuRows.length && (
                      <tr><td colSpan="9">{t('xDemand.p8.noSkuMovement')}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="df-panel df-full">
              <div className="df-panel-head">
                <span>{t('xDemand.mc.title')}</span>
                <div className="df-panel-actions">
                  <em>{t('xDemand.mc.note')}</em>
                  <button type="button" onClick={exportBacktestModels} disabled={!modelComparisonRows.length && !branchChannelModelRows.length}>
                    <Download size={14} />
                    {t('xDemand.mc.exportModels')}
                  </button>
                </div>
              </div>
              {recommendedRow && (
                <div className="df-recommendation">
                  <div>
                    <strong>{t('xDemand.mc.recommendedModel')}</strong>
                    <span>{modelLabel(recommendedRow.modelName)}</span>
                  </div>
                  <p>
                    {rollingRows.length
                      ? t('xDemand.mc.selectedRolling', {
                        model: modelLabel(recommendedModelName),
                        wins: formatQty(rollingWinnerCount),
                        windows: formatQty(rollingRows.length),
                        policy: policyDirection,
                      })
                      : t('xDemand.mc.selectedWape', { wape: formatPercent(recommendedRow.wape), bias: formatPercent(recommendedRow.bias) })}
                  </p>
                </div>
              )}
              <div className="df-policy-summary">
                <div>
                  <span>{t('xDemand.mc.currentOutput')}</span>
                  <strong>Hierarchical recent12 SKU mix</strong>
                </div>
                <div>
                  <span>{t('xDemand.mc.rollingWinner')}</span>
                  <strong>{rollingWinnerName ? modelLabel(rollingWinnerName) : '-'}</strong>
                </div>
                <div>
                  <span>{t('xDemand.mc.hybridStatus')}</span>
                  <strong>{tv(validationStatus.label)}</strong>
                </div>
                <div>
                  <span>{t('xDemand.mc.nextDirection')}</span>
                  <strong>{policyDirection}</strong>
                </div>
              </div>
              <div className={`df-model-recommendation ${recommendationPrimary.tone}`}>
                <div>
                  <span>{t('xDemand.col.recommendation')}</span>
                  <strong>{recommendationPrimary.label}</strong>
                  <em>{recommendationPrimary.detail}</em>
                </div>
                <div>
                  <span>{t('xDemand.mc.latestV4Gap')}</span>
                  <strong>{Number.isFinite(v4LatestWapeGap) ? pts((v4LatestWapeGap * 100).toFixed(1)) : '-'}</strong>
                  <em>{t('xDemand.mc.v4MinusCurrent')}</em>
                </div>
                <div>
                  <span>{t('xDemand.mc.latestV5Gap')}</span>
                  <strong>{Number.isFinite(v5LatestWapeGap) ? pts((v5LatestWapeGap * 100).toFixed(1)) : '-'}</strong>
                  <em>{t('xDemand.mc.v5MinusCurrent')}</em>
                </div>
              </div>
              <div className="df-promotion-panel">
                <div className="df-promotion-head">
                  <div>
                    <span>{t('xDemand.mc.promoCandidate')}</span>
                    <strong>
                      {t('xDemand.mc.path', {
                        label: promotionCandidateRows.find((row) => row.status === 'Promote review')?.label
                          || promotionCandidateRows.find((row) => row.status === 'Pilot candidate')?.label
                          || t('xDemand.col.current'),
                      })}
                    </strong>
                  </div>
                  <em>{t('xDemand.mc.promoNote')}</em>
                </div>
                <div className="df-table-wrap df-promotion-table">
                  <table className="df-table">
                    <thead>
                      <tr>
                        <th>{t('xDemand.col.model')}</th>
                        <th>{t('xDemand.col.status')}</th>
                        <th>{t('xDemand.col.action')}</th>
                        <th className="num">{t('xDemand.col.latestWape')}</th>
                        <th className="num">{t('xDemand.col.latestBias')}</th>
                        <th className="num">{t('xDemand.col.rollingWins')}</th>
                        <th className="num">{t('xDemand.col.rollingGap')}</th>
                        <th>{t('xDemand.col.reason')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {promotionCandidateRows.map((row) => (
                        <tr key={`promotion-${row.modelName}`}>
                          <td><span className={`df-model-pill ${row.tone}`}>{row.label}</span></td>
                          <td><span className={`df-status ${row.tone === 'high' ? 'ok' : row.tone === 'medium' ? 'watch' : 'review'}`}>{tv(row.status)}</span></td>
                          <td>{row.action}</td>
                          <td className="num">{formatPercent(row.latestWape)}</td>
                          <td className="num">{formatPercent(row.latestBias)}</td>
                          <td className="num">{formatQty(row.rollingWins)} / {formatQty(rollingRows.length)}</td>
                          <td className="num">{Number.isFinite(Number(row.rollingGap)) ? pts((Number(row.rollingGap) * 100).toFixed(1)) : '-'}</td>
                          <td className="df-reason-cell" title={row.reason}>{row.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {!!featureBacktestRows.length && (
                <div className="df-promotion-panel">
                  <div className="df-promotion-head">
                    <div>
                      <span>{t('xDemand.feat.candidate')}</span>
                      <strong>{featureBestRow ? t('xDemand.mc.path', { label: modelLabel(featureBestRow.modelName) }) : '-'}</strong>
                    </div>
                    <em>{t('xDemand.feat.candidateNote')}</em>
                  </div>
                  <div className="df-policy-summary">
                    <div>
                      <span>{t('xDemand.feat.bestWape')}</span>
                      <strong>{formatPercent(featureBestRow?.wape)}</strong>
                    </div>
                    <div>
                      <span>{t('xDemand.col.currentWape')}</span>
                      <strong>{formatPercent(featureCurrentRow?.wape)}</strong>
                    </div>
                    <div>
                      <span>{t('xDemand.feat.gapVsCurrent')}</span>
                      <strong>{Number.isFinite(featureCandidateGap) ? pts((featureCandidateGap * 100).toFixed(2)) : '-'}</strong>
                    </div>
                    <div>
                      <span>{t('xDemand.feat.backtestWeeks')}</span>
                      <strong>{formatQty(featureBestRow?.weeks)}</strong>
                    </div>
                  </div>
                  <div className={`df-phase-readiness ${phase7UiStatus.tone}`}>
                    <div className="df-phase-readiness-head">
                      <div>
                        <span>{t('xDemand.p7.readiness')}</span>
                        <strong>{phase7UiStatus.label}</strong>
                      </div>
                      <em>{t('xDemand.p7.checksPassed', { done: formatQty(phase7DoneCount), total: formatQty(phase7ChecklistRows.length) })}</em>
                    </div>
                    <p>{phase7UiStatus.detail}</p>
                    <div className="df-phase-checklist">
                      {phase7ChecklistRows.map((row) => {
                        const Icon = row.done ? CheckCircle2 : AlertTriangle;
                        return (
                          <div key={`phase7-check-${row.label}`} className={row.done ? 'done' : 'open'}>
                            <Icon size={17} />
                            <div>
                              <strong>{row.label}</strong>
                              <span>{row.detail}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className={`df-feature-guardrail ${featureGuardrailDecision.tone}`}>
                    <div className="df-feature-guardrail-head">
                      <div>
                        <span>{t('xDemand.fg.title')}</span>
                        <strong>{tv(featureGuardrailDecision.label)}</strong>
                      </div>
                      <em>{featureGuardrailDecision.action}</em>
                    </div>
                    <div className="df-feature-guardrail-grid">
                      {featureGuardrailRows.map((row) => (
                        <div key={`feature-guardrail-${row.label}`} className={row.tone}>
                          <span>{row.label}</span>
                          <strong>{row.value}</strong>
                          <em>{row.detail}</em>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="df-feature-subsection">
                    <div className="df-feature-subsection-head">
                      <strong>{t('xDemand.feat.modelSummary')}</strong>
                      <span>{t('xDemand.feat.modelSummaryNote')}</span>
                    </div>
                    <div className="df-table-wrap df-promotion-table">
                      <table className="df-table">
                        <thead>
                          <tr>
                            <th>{t('xDemand.col.model')}</th>
                            <th className="num">{t('xDemand.col.actual')}</th>
                            <th className="num">{t('xDemand.col.forecast')}</th>
                            <th className="num">WAPE</th>
                            <th className="num">{t('xDemand.col.bias')}</th>
                            <th className="num">{t('xDemand.col.absError')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {featureBacktestRows.map((row) => (
                            <tr key={`feature-backtest-${row.modelName}`}>
                              <td className="df-model-cell" title={row.modelName}>{modelLabel(row.modelName)}</td>
                              <td className="num">{formatQty(row.actualQty)}</td>
                              <td className="num">{formatQty(row.forecastQty)}</td>
                              <td className="num">{formatPercent(row.wape)}</td>
                              <td className="num">{formatPercent(row.bias)}</td>
                              <td className="num">{formatQty(row.absError)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {!!featureSignalRows.length && (
                    <div className="df-feature-subsection">
                      <div className="df-feature-subsection-head">
                        <div>
                          <strong>{t('xDemand.feat.signalCoverage')}</strong>
                          <span>{t('xDemand.feat.signalCoverageNote')}</span>
                        </div>
                      </div>
                      <div className="df-feature-signal-grid">
                        {featureSignalRows.map((row) => (
                          <div key={`feature-signal-${row.key}`}>
                            <span>{row.label}</span>
                            <strong>{t('xDemand.u.rows', { n: formatQty(row.rows) })}</strong>
                            <em>{t('xDemand.feat.highPriorityN', { n: formatQty(row.highRows) })}</em>
                            <p>{row.note}</p>
                          </div>
                        ))}
                      </div>
                      <div className="df-table-wrap df-feature-signal-table">
                        <table className="df-table">
                          <thead>
                            <tr>
                              <th>{t('xDemand.col.signal')}</th>
                              <th className="num">{t('xDemand.col.rows')}</th>
                              <th className="num">{t('xDemand.col.highPriority')}</th>
                              <th className="num">{t('xDemand.col.forecast')}</th>
                              <th className="num">{t('xDemand.col.recent4w')}</th>
                              <th className="num">{t('xDemand.col.metric')}</th>
                              <th>{t('xDemand.col.metricMeaning')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {featureSignalRows.map((row) => (
                              <tr key={`feature-signal-row-${row.key}`}>
                                <td>{row.label}</td>
                                <td className="num">{formatQty(row.rows)}</td>
                                <td className="num">{formatQty(row.highRows)}</td>
                                <td className="num">{formatQty(row.forecastQty)}</td>
                                <td className="num">{formatQty(row.recentQty)}</td>
                                <td className="num">{row.formatMetric(row.metricValue)}</td>
                                <td className="df-reason-cell" title={row.metricLabel}>{row.metricLabel}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {!!featureWeeklyChartRows.length && (
                    <div className="df-feature-subsection">
                      <div className="df-feature-subsection-head">
                        <div>
                          <strong>{t('xDemand.feat.trend')}</strong>
                          <span>{t('xDemand.feat.trendNote', { model: modelLabel(featureCandidateModelName) })}</span>
                        </div>
                      </div>
                      <div className="df-feature-chart">
                        <ResponsiveContainer>
                          <ComposedChart data={featureWeeklyChartRows} margin={{ top: 10, right: 18, bottom: 0, left: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                            <YAxis yAxisId="wape" tickFormatter={(value) => `${Number(value).toFixed(0)}%`} width={58} tick={{ fontSize: 11 }} />
                            <YAxis yAxisId="adj" orientation="right" tickFormatter={(value) => `${Number(value).toFixed(0)}%`} width={48} tick={{ fontSize: 11 }} />
                            <Tooltip
                              formatter={(value, name, item) => {
                                // Told apart by the series, not by its name: the names are translated.
                                if (['adjustmentPct', 'currentWapePct', 'featureWapePct'].includes(item?.dataKey)) return `${Number(value).toFixed(1)}%`;
                                return formatQty(value);
                              }}
                              labelFormatter={(label) => t('xDemand.feat.weekLabel', { label })}
                            />
                            <Legend />
                            <Bar yAxisId="adj" name={t('xDemand.feat.adjustment')} dataKey="adjustmentPct" fill="#d18b12" radius={[4, 4, 0, 0]} />
                            <Line yAxisId="wape" name={t('xDemand.col.currentWape')} type="monotone" dataKey="currentWapePct" stroke="#4b5563" strokeWidth={2.5} dot={{ r: 3 }} />
                            <Line yAxisId="wape" name={t('xDemand.col.candidateWape')} type="monotone" dataKey="featureWapePct" stroke="#156082" strokeWidth={2.5} dot={{ r: 3 }} />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}
                  {!!featureWeeklyVerdictRows.length && (
                    <div className="df-feature-subsection">
                      <div className="df-feature-subsection-head">
                        <div>
                          <strong>{t('xDemand.feat.verdict')}</strong>
                          <span>{t('xDemand.feat.verdictNote')}</span>
                        </div>
                      </div>
                      <div className="df-feature-verdict-summary">
                        <div className="ok">
                          <span>{t('xDemand.v.helps')}</span>
                          <strong>{formatQty(featureWeeklyVerdictCounts.Helps || 0)}</strong>
                          <em>{t('xDemand.feat.candLower')}</em>
                        </div>
                        <div className="review">
                          <span>{t('xDemand.v.hurts')}</span>
                          <strong>{formatQty(featureWeeklyVerdictCounts.Hurts || 0)}</strong>
                          <em>{t('xDemand.feat.candHigher')}</em>
                        </div>
                        <div className="watch">
                          <span>{t('xDemand.feat.avgImprovement')}</span>
                          <strong>{Number.isFinite(featureWeeklyAvgImprovement) ? pts(featureWeeklyAvgImprovement.toFixed(1)) : '-'}</strong>
                          <em>{t('xDemand.feat.currentMinusCand')}</em>
                        </div>
                        <div>
                          <span>{t('xDemand.feat.bestWorst')}</span>
                          <strong>{featureWeeklyBestWeek ? `${featureWeeklyBestWeek.label} / ${featureWeeklyWorstWeek?.label || '-'}` : '-'}</strong>
                          <em>{featureWeeklyBestWeek ? `${pts(featureWeeklyBestWeek.improvementPts.toFixed(1))} / ${pts(featureWeeklyWorstWeek.improvementPts.toFixed(1))}` : '-'}</em>
                        </div>
                      </div>
                      <div className="df-table-wrap df-feature-verdict-table">
                        <table className="df-table">
                          <thead>
                            <tr>
                              <th>{t('xDemand.col.week')}</th>
                              <th>{t('xDemand.col.verdict')}</th>
                              <th className="num">{t('xDemand.col.currentWape')}</th>
                              <th className="num">{t('xDemand.col.candidateWape')}</th>
                              <th className="num">{t('xDemand.col.improvement')}</th>
                              <th className="num">{t('xDemand.col.adjustment')}</th>
                              <th>{t('xDemand.col.reason')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {featureWeeklyVerdictRows.map((row) => {
                              const verdictClass = row.verdict === 'Helps'
                                ? 'ok'
                                : row.verdict === 'Hurts'
                                  ? 'review'
                                  : 'watch';
                              return (
                                <tr key={`feature-verdict-${row.key}`}>
                                  <td>{row.label}</td>
                                  <td><span className={`df-status ${verdictClass}`}>{tv(row.verdict)}</span></td>
                                  <td className="num">{`${Number(row.currentWapePct).toFixed(1)}%`}</td>
                                  <td className="num">{`${Number(row.featureWapePct).toFixed(1)}%`}</td>
                                  <td className="num">{pts(row.improvementPts.toFixed(1))}</td>
                                  <td className="num">{`${Number(row.adjustmentPct || 0).toFixed(1)}%`}</td>
                                  <td className="df-reason-cell" title={row.adjustmentReason || '-'}>{row.adjustmentReason || '-'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {!!featureBacktestByWeekRows.length && (
                    <div className="df-feature-subsection">
                      <div className="df-feature-subsection-head">
                        <strong>{t('xDemand.feat.weeklyDetail')}</strong>
                        <span>{t('xDemand.feat.weeklyDetailNote')}</span>
                      </div>
                      <div className="df-table-wrap df-promotion-table">
                        <table className="df-table">
                          <thead>
                            <tr>
                              <th>{t('xDemand.col.week')}</th>
                              <th>{t('xDemand.col.model')}</th>
                              <th className="num">{t('xDemand.col.actual')}</th>
                              <th className="num">{t('xDemand.col.forecast')}</th>
                              <th className="num">WAPE</th>
                              <th className="num">{t('xDemand.col.adjustment')}</th>
                              <th>{t('xDemand.col.reason')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {featureBacktestByWeekRows.slice(0, 36).map((row) => (
                              <tr key={`feature-week-${row.targetYear}-${row.targetWeek}-${row.modelName}`}>
                                <td>{weekLabel(row)}</td>
                                <td className="df-model-cell" title={row.modelName}>{modelLabel(row.modelName)}</td>
                                <td className="num">{formatQty(row.actualQty)}</td>
                                <td className="num">{formatQty(row.forecastQty)}</td>
                                <td className="num">{formatPercent(row.wape)}</td>
                                <td className="num">{formatPercent(row.totalAdjustmentPct)}</td>
                                <td className="df-reason-cell" title={row.adjustmentReason || '-'}>{row.adjustmentReason || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {!!featureActionRows.length && (
                    <div className="df-feature-subsection">
                      <div className="df-feature-subsection-head">
                        <div>
                          <strong>{t('xDemand.feat.actionable')}</strong>
                          <span>{t('xDemand.feat.actionableNote')}</span>
                        </div>
                        <button type="button" onClick={exportFeatureActionOutput} disabled={!featureActionRows.length}>
                          <Download size={14} />
                          {t('xDemand.btn.exportCsv')}
                        </button>
                      </div>
                      <div className="df-feature-action-summary">
                        <div className="high">
                          <span>{t('xDemand.feat.pilotCandidates')}</span>
                          <strong>{formatQty(featureActionCounts['Pilot candidate'] || 0)}</strong>
                          <em>{t('xDemand.feat.pilotCandNote')}</em>
                        </div>
                        <div className="low">
                          <span>{t('xDemand.feat.keepBaseline')}</span>
                          <strong>{formatQty(featureActionCounts['Keep baseline'] || 0)}</strong>
                          <em>{t('xDemand.feat.worsens')}</em>
                        </div>
                        <div className="medium">
                          <span>{t('xDemand.feat.watchLow')}</span>
                          <strong>{formatQty((featureActionCounts.Watch || 0) + (featureActionCounts['Low volume watch'] || 0))}</strong>
                          <em>{t('xDemand.feat.insufficient')}</em>
                        </div>
                        <div>
                          <span>{t('xDemand.feat.weightedGain')}</span>
                          <strong>{Number.isFinite(featureActionTotalImpact) ? formatQty(featureActionTotalImpact) : '-'}</strong>
                          <em>{t('xDemand.feat.weightedGainNote')}</em>
                        </div>
                      </div>
                      {!!featurePilotRows.length && (
                        <div className="df-feature-mini-head">
                          <strong>{t('xDemand.feat.topPilots')}</strong>
                          <span>{t('xDemand.feat.bcGroupsN', { n: formatQty(featurePilotRows.length) })}</span>
                        </div>
                      )}
                      <div className="df-table-wrap df-feature-action-table">
                        <table className="df-table">
                          <thead>
                            <tr>
                              <th>{t('xDemand.col.action')}</th>
                              <th>{t('xDemand.col.branch')}</th>
                              <th>{t('xDemand.col.channel')}</th>
                              <th className="num">{t('xDemand.col.actual')}</th>
                              <th className="num">{t('xDemand.col.currentWape')}</th>
                              <th className="num">{t('xDemand.col.featureWape')}</th>
                              <th className="num">{t('xDemand.col.gain')}</th>
                              <th className="num">{t('xDemand.col.avgAdj')}</th>
                              <th>{t('xDemand.col.reason')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {featureActionRows.slice(0, 20).map((row) => (
                              <tr key={`feature-action-${row.branch}-${row.channel}`}>
                                <td><span className={`df-model-pill ${row.tone}`}>{tv(row.action)}</span></td>
                                <td>{row.branch}</td>
                                <td>{row.channel}</td>
                                <td className="num">{formatQty(row.actualQty)}</td>
                                <td className="num">{formatPercent(row.currentWape)}</td>
                                <td className="num">{formatPercent(row.featureWape)}</td>
                                <td className="num">{Number.isFinite(Number(row.improvementVsCurrent)) ? pts((Number(row.improvementVsCurrent) * 100).toFixed(1)) : '-'}</td>
                                <td className="num">{formatPercent(row.avgAdjustmentPct)}</td>
                                <td className="df-reason-cell" title={row.actionReason}>{row.actionReason}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {!!featureBranchChannelRows.length && (
                    <div className="df-feature-subsection">
                      <div className="df-feature-subsection-head">
                        <strong>{t('xDemand.feat.bcImpact')}</strong>
                        <span>{t('xDemand.feat.bcImpactNote')}</span>
                      </div>
                      <div className="df-table-wrap df-bc-model-table">
                        <table className="df-table">
                          <thead>
                            <tr>
                              <th>{t('xDemand.col.branch')}</th>
                              <th>{t('xDemand.col.channel')}</th>
                              <th>{t('xDemand.col.status')}</th>
                              <th className="num">{t('xDemand.col.actual')}</th>
                              <th className="num">{t('xDemand.col.currentWape')}</th>
                              <th className="num">{t('xDemand.col.featureWape')}</th>
                              <th className="num">{t('xDemand.col.improvement')}</th>
                              <th className="num">{t('xDemand.col.avgAdj')}</th>
                              <th>{t('xDemand.col.signalReason')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {featureBranchChannelRows.map((row) => {
                              const status = row.featureStatus || '-';
                              const statusClass = status === 'Feature helps'
                                ? 'ok'
                                : status === 'Feature hurts'
                                  ? 'review'
                                  : status === 'Low volume'
                                    ? 'watch'
                                    : 'close';
                              return (
                                <tr key={`feature-bc-${row.branch}-${row.channel}`}>
                                  <td>{row.branch}</td>
                                  <td>{row.channel}</td>
                                  <td><span className={`df-status ${statusClass}`}>{tv(status)}</span></td>
                                  <td className="num">{formatQty(row.actualQty)}</td>
                                  <td className="num">{formatPercent(row.currentWape)}</td>
                                  <td className="num">{formatPercent(row.featureWape)}</td>
                                  <td className="num">{Number.isFinite(Number(row.improvementVsCurrent)) ? pts((Number(row.improvementVsCurrent) * 100).toFixed(1)) : '-'}</td>
                                  <td className="num">{formatPercent(row.avgAdjustmentPct)}</td>
                                  <td className="df-reason-cell" title={row.adjustmentReason || '-'}>{row.adjustmentReason || '-'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {!!featureReviewQueueRows.length && (
                    <div className="df-feature-subsection">
                      <div className="df-feature-subsection-head">
                        <div>
                          <strong>{t('xDemand.feat.reviewQueue')}</strong>
                          <span>{t('xDemand.feat.reviewQueueNote', { shown: formatQty(featureVisibleReviewQueueRows.length), total: formatQty(featureReviewQueueRows.length) })}</span>
                        </div>
                        <button type="button" onClick={exportFeatureReviewQueue} disabled={!featureVisibleReviewQueueRows.length}>
                          <Download size={14} />
                          {t('xDemand.btn.exportCsv')}
                        </button>
                      </div>
                      <div className="df-feature-review-summary">
                        <div>
                          <span>{t('xDemand.feat.totalRisks')}</span>
                          <strong>{formatQty(featureReviewQueueRows.length)}</strong>
                        </div>
                        <div>
                          <span>{t('xDemand.col.highPriority')}</span>
                          <strong>{formatQty(featureReviewPriorityCounts.High || 0)}</strong>
                        </div>
                        <div>
                          <span>{t('xDemand.feat.mediumPriority')}</span>
                          <strong>{formatQty(featureReviewPriorityCounts.Medium || 0)}</strong>
                        </div>
                        <div>
                          <span>{t('xDemand.feat.topRisk')}</span>
                          <strong title={featureReviewTopRisk?.[0] || '-'}>{featureReviewTopRisk?.[0] || '-'}</strong>
                          <em>{t('xDemand.u.rows', { n: formatQty(featureReviewTopRisk?.[1] || 0) })}</em>
                        </div>
                      </div>
                      <div className="df-candidate-tools df-feature-review-tools">
                        {['All', 'High', 'Medium', 'Low'].map((priority) => (
                          <button
                            key={`feature-priority-${priority}`}
                            type="button"
                            className={featureReviewPriorityFilter === priority ? 'active' : ''}
                            onClick={() => setFeatureReviewPriorityFilter(priority)}
                          >
                            {tv(priority)} {priority === 'All' ? formatQty(featureReviewQueueRows.length) : formatQty(featureReviewPriorityCounts[priority] || 0)}
                          </button>
                        ))}
                      </div>
                      <div className="df-candidate-tools df-feature-review-tools">
                        {featureReviewRiskOptions.map((risk) => (
                          <button
                            key={`feature-risk-${risk}`}
                            type="button"
                            className={featureReviewRiskFilter === risk ? 'active' : ''}
                            onClick={() => setFeatureReviewRiskFilter(risk)}
                            title={risk}
                          >
                            {risk === 'All' ? t('xDemand.feat.allRisk') : risk} {risk === 'All' ? formatQty(featureReviewQueueRows.length) : formatQty(featureReviewRiskCounts[risk] || 0)}
                          </button>
                        ))}
                      </div>
                      {!!featureExceptionRows.length && (
                        <div className="df-feature-exceptions">
                          <div className="df-feature-subsection-head">
                            <div>
                              <strong>{t('xDemand.feat.exceptions')}</strong>
                              <span>{t('xDemand.feat.exceptionsNote')}</span>
                            </div>
                            <button type="button" onClick={exportFeatureExceptionBuckets} disabled={!featureExceptionRows.length}>
                              <Download size={14} />
                              {t('xDemand.btn.exportCsv')}
                            </button>
                          </div>
                          <div className="df-feature-exception-summary">
                            <div className="review">
                              <span>{t('xDemand.feat.exceptionBuckets')}</span>
                              <strong>{formatQty(featureExceptionRows.length)}</strong>
                              <em>{t('xDemand.feat.skuRowsN', { n: formatQty(featureVisibleReviewQueueRows.length) })}</em>
                            </div>
                            <div className="watch">
                              <span>{t('xDemand.feat.forecastAtRisk')}</span>
                              <strong>{formatQty(featureExceptionForecastQty)}</strong>
                              <em>{t('xDemand.feat.currentFilter')}</em>
                            </div>
                            <div className="review">
                              <span>{t('xDemand.feat.highPriorityForecast')}</span>
                              <strong>{formatQty(featureExceptionHighForecastQty)}</strong>
                              <em>{t('xDemand.feat.bucketHasHigh')}</em>
                            </div>
                            <div>
                              <span>{t('xDemand.feat.topException')}</span>
                              <strong title={tv(featureExceptionTop?.risk) || '-'}>{tv(featureExceptionTop?.risk) || '-'}</strong>
                              <em>{t('xDemand.feat.forecastQtyN', { n: formatQty(featureExceptionTop?.forecastQty || 0) })}</em>
                            </div>
                          </div>
                          <div className="df-table-wrap df-feature-exception-table">
                            <table className="df-table">
                              <thead>
                                <tr>
                                  <th>{t('xDemand.col.risk')}</th>
                                  <th>{t('xDemand.col.recommendedAction')}</th>
                                  <th className="num">{t('xDemand.col.rows')}</th>
                                  <th className="num">{t('xDemand.v.high')}</th>
                                  <th className="num">{t('xDemand.v.medium')}</th>
                                  <th className="num">{t('xDemand.col.forecast')}</th>
                                  <th className="num">{t('xDemand.col.recent4w')}</th>
                                  <th className="num">{t('xDemand.col.lower')}</th>
                                  <th className="num">{t('xDemand.col.upper')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {featureExceptionRows.slice(0, 12).map((row) => (
                                  <tr key={`feature-exception-${row.risk}-${row.action}`}>
                                    <td className="df-reason-cell" title={tv(row.risk)}>{tv(row.risk)}</td>
                                    <td className="df-reason-cell" title={tv(row.action)}>{tv(row.action)}</td>
                                    <td className="num">{formatQty(row.rows)}</td>
                                    <td className="num">{formatQty(row.highRows)}</td>
                                    <td className="num">{formatQty(row.mediumRows)}</td>
                                    <td className="num">{formatQty(row.forecastQty)}</td>
                                    <td className="num">{formatQty(row.recentQty)}</td>
                                    <td className="num">{formatQty(row.lowerQty)}</td>
                                    <td className="num">{formatQty(row.upperQty)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                      {!!featureReviewBranchRows.length && (
                        <div className="df-feature-review-branch">
                          <div className="df-feature-mini-head">
                            <strong>{t('xDemand.feat.riskByBc')}</strong>
                            <span>{t('xDemand.feat.top10Filter')}</span>
                          </div>
                          <div className="df-table-wrap df-feature-review-branch-table">
                            <table className="df-table">
                              <thead>
                                <tr>
                                  <th>{t('xDemand.col.branch')}</th>
                                  <th>{t('xDemand.col.channel')}</th>
                                  <th className="num">{t('xDemand.col.rows')}</th>
                                  <th className="num">{t('xDemand.v.high')}</th>
                                  <th className="num">{t('xDemand.v.medium')}</th>
                                  <th className="num">{t('xDemand.col.forecast')}</th>
                                  <th className="num">{t('xDemand.col.recent4w')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {featureReviewBranchRows.slice(0, 10).map((row) => (
                                  <tr key={`feature-review-branch-${row.branch}-${row.channel}`}>
                                    <td>{row.branch}</td>
                                    <td>{row.channel}</td>
                                    <td className="num">{formatQty(row.rows)}</td>
                                    <td className="num">{formatQty(row.highRows)}</td>
                                    <td className="num">{formatQty(row.mediumRows)}</td>
                                    <td className="num">{formatQty(row.forecastQty)}</td>
                                    <td className="num">{formatQty(row.recentQty)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                      <div className="df-table-wrap df-bc-model-table">
                        <table className="df-table">
                          <thead>
                            <tr>
                              <th>{t('xDemand.col.priority')}</th>
                              <th>SKU</th>
                              <th>{t('xDemand.col.branch')}</th>
                              <th>{t('xDemand.col.channel')}</th>
                              <th>{t('xDemand.col.collection')}</th>
                              <th>{t('xDemand.col.category')}</th>
                              <th className="num">{t('xDemand.col.forecast')}</th>
                              <th className="num">{t('xDemand.col.recent4w')}</th>
                              <th className="num">{t('xDemand.col.promo')}</th>
                              <th className="num">{t('xDemand.col.stockout')}</th>
                              <th className="num">{t('xDemand.col.invSales')}</th>
                              <th>{t('xDemand.col.risk')}</th>
                              <th>{t('xDemand.col.action')}</th>
                            </tr>
                        </thead>
                        <tbody>
                          {featureVisibleReviewQueueRows.map((row) => {
                            const priority = row.reviewPriority || '-';
                            const priorityClass = priority === 'High'
                              ? 'review'
                                : priority === 'Medium'
                                  ? 'watch'
                                  : 'close';
                              return (
                                <tr key={`feature-review-${row.sku}-${row.branch}-${row.channel}`}>
                                  <td><span className={`df-status ${priorityClass}`}>{tv(priority)}</span></td>
                                  <td>{row.sku}</td>
                                  <td>{row.branch}</td>
                                  <td>{row.channel}</td>
                                  <td>{row.productCollection || '-'}</td>
                                  <td>{row.productCategory || '-'}</td>
                                  <td className="num">{formatQty(row.forecastQty)}</td>
                                  <td className="num">{formatQty(row.recentQty)}</td>
                                  <td className="num">{formatPercent(row.promoQtyShare)}</td>
                                  <td className="num">{formatPercent(row.stockoutQtyShare)}</td>
                                  <td className="num">{Number.isFinite(Number(row.inventoryToSalesRatio)) ? Number(row.inventoryToSalesRatio).toFixed(1) : '-'}</td>
                                  <td className="df-reason-cell" title={row.reviewReason || '-'}>{row.reviewReason || '-'}</td>
                                  <td className="df-reason-cell" title={row.recommendedAction || '-'}>{row.recommendedAction || '-'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="df-table-wrap df-recommendation-table">
                <table className="df-table">
                  <thead>
                    <tr>
                      <th>{t('xDemand.col.option')}</th>
                      <th>{t('xDemand.col.whenToUse')}</th>
                      <th>{t('xDemand.col.action')}</th>
                      <th className="num">{t('xDemand.col.latestWape')}</th>
                      <th className="num">{t('xDemand.col.latestBias')}</th>
                      <th className="num">{t('xDemand.col.rollingWins')}</th>
                      <th className="num">{t('xDemand.col.rollingGap')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modelRecommendationRows.map((row) => (
                      <tr key={row.modelName}>
                        <td>
                          <span className={`df-model-pill ${row.tone}`}>{row.label}</span>
                        </td>
                        <td>{row.useCase}</td>
                        <td>{row.action}</td>
                        <td className="num">{formatPercent(row.row?.wape)}</td>
                        <td className="num">{formatPercent(row.row?.bias)}</td>
                        <td className="num">{formatQty(row.rollingWins)} / {formatQty(rollingRows.length)}</td>
                        <td className="num">{Number.isFinite(Number(row.rollingGap)) ? pts((Number(row.rollingGap) * 100).toFixed(1)) : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!!branchChannelModelRows.length && (
                <div className="df-table-wrap df-bc-model-table">
                  <table className="df-table">
                    <thead>
                      <tr>
                        <th>{t('xDemand.col.branch')}</th>
                        <th>{t('xDemand.col.channel')}</th>
                        <th>{t('xDemand.col.recommendation')}</th>
                        <th className="num">{t('xDemand.col.actual')}</th>
                        <th className="num">{t('xDemand.col.currentWape')}</th>
                        <th className="num">{t('xDemand.col.seasonalWape')}</th>
                        <th className="num">V4 WAPE</th>
                        <th className="num">V5 WAPE</th>
                        <th className="num">{t('xDemand.col.bestWape')}</th>
                        <th className="num">{t('xDemand.col.improvement')}</th>
                        <th>{t('xDemand.col.status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {branchChannelModelRows.map((row) => {
                        const status = row.recommendationStatus || '-';
                        const statusClass = status === 'Strong candidate'
                          ? 'ok'
                          : status === 'Low volume'
                            ? 'review'
                          : status === 'Watch'
                            ? 'watch'
                            : 'close';
                        return (
                          <tr key={`${row.branch}-${row.channel}`}>
                            <td>{row.branch}</td>
                            <td>{row.channel}</td>
                            <td className="df-model-cell" title={row.recommendedModel}>{modelLabel(row.recommendedModel)}</td>
                            <td className="num">{formatQty(row.actualQty)}</td>
                            <td className="num">{formatPercent(row.currentWape)}</td>
                            <td className="num">{formatPercent(row.seasonalWape)}</td>
                            <td className="num">{formatPercent(row.candidateV4Wape)}</td>
                            <td className="num">{formatPercent(row.candidateV5Wape)}</td>
                            <td className="num">{formatPercent(row.recommendedWape)}</td>
                            <td className="num">{Number.isFinite(Number(row.improvementVsCurrent)) ? pts((Number(row.improvementVsCurrent) * 100).toFixed(1)) : '-'}</td>
                            <td>
                              <span
                                className={`df-status ${statusClass}`}
                                title={status === 'Low volume' ? t('xDemand.mc.lowVolumeTitle') : tv(status)}
                              >
                                {tv(status)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="df-model-comparison">
                {modelComparisonRows.map((row, index) => (
                  <div className={row.modelName === recommendedModelName ? 'best' : ''} key={row.modelName}>
                    <div className="df-rank">{index + 1}</div>
                    <div className="df-model-summary">
                      <strong>{modelLabel(row.modelName)}</strong>
                      <span>{modelDescriptions[row.modelName] || t('xDemand.md.benchmark')}</span>
                    </div>
                    <div>
                      <span>WAPE</span>
                      <strong>{formatPercent(row.wape)}</strong>
                    </div>
                    <div>
                      <span>{t('xDemand.col.bias')}</span>
                      <strong>{formatPercent(row.bias)}</strong>
                    </div>
                    <div>
                      <span>{t('xDemand.col.forecast')}</span>
                      <strong>{formatQty(row.forecastQty)}</strong>
                    </div>
                    <div>
                      <span>{t('xDemand.col.actual')}</span>
                      <strong>{formatQty(row.actualQty)}</strong>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="df-panel df-full">
              <div className="df-panel-head">
                <span>{t('xDemand.wt.title')}</span>
                <div className="df-panel-actions">
                  <em>{t('xDemand.wt.note')}</em>
                  <button type="button" onClick={exportWeeklyModelTracking} disabled={!weeklyTrackingRows.length}>
                    <Download size={14} />
                    {t('xDemand.wt.export')}
                  </button>
                </div>
              </div>
              <div className="df-tracking-summary">
                <div>
                  <span>{t('xDemand.wt.latestWeek')}</span>
                  <strong>{weeklyTrackingLatest ? weeklyTrackingLatest.label : '-'}</strong>
                  <em>{weeklyTrackingLatest ? t('xDemand.wt.actualQtyN', { n: formatQty(weeklyTrackingLatest.actualQty) }) : t('xDemand.wt.noRows')}</em>
                </div>
                <div>
                  <span>{t('xDemand.wt.baselineAvg')}</span>
                  <strong>{formatPercent(weeklyTrackingBaselineAvgWape)}</strong>
                  <em>{t('xDemand.wt.lastTracked', { n: formatQty(weeklyTrackingRecentRows.length) })}</em>
                </div>
                <div>
                  <span>{t('xDemand.wt.bestAvg')}</span>
                  <strong>{formatPercent(weeklyTrackingBestAvgWape)}</strong>
                  <em>{t('xDemand.wt.bestAvgNote')}</em>
                </div>
                <div>
                  <span>{t('xDemand.wt.topWinner')}</span>
                  <strong title={weeklyTrackingTopWinner?.[0] || ''}>{modelLabel(weeklyTrackingTopWinner?.[0] || '-')}</strong>
                  <em>{t('xDemand.wt.winsN', { n: formatQty(weeklyTrackingTopWinner?.[1] || 0) })}</em>
                </div>
              </div>
              <div className="df-table-wrap df-weekly-tracking-table">
                <table className="df-table">
                  <thead>
                    <tr>
                      <th>{t('xDemand.col.week')}</th>
                      <th className="num">{t('xDemand.col.actual')}</th>
                      <th className="num">{t('xDemand.col.baselineForecast')}</th>
                      <th className="num">{t('xDemand.col.baselineWape')}</th>
                      <th className="num">{t('xDemand.col.baselineBias')}</th>
                      <th className="num">{t('xDemand.col.aiForecast')}</th>
                      <th className="num">AI WAPE</th>
                      <th className="num">{t('xDemand.col.ma4Forecast')}</th>
                      <th className="num">MA4 WAPE</th>
                      <th className="num">{t('xDemand.col.ma12Forecast')}</th>
                      <th className="num">MA12 WAPE</th>
                      <th>{t('xDemand.col.winner')}</th>
                      <th className="num">{t('xDemand.col.winnerWape')}</th>
                      <th className="num">{t('xDemand.col.baselineGap')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weeklyTrackingRecentRows.map((row) => (
                      <tr key={`weekly-tracking-${row.key}`}>
                        <td>{row.label}</td>
                        <td className="num">{formatQty(row.actualQty)}</td>
                        <td className="num">{formatQty(row.baseline?.forecastQty)}</td>
                        <td className="num">{formatPercent(row.baseline?.wape)}</td>
                        <td className="num">{formatPercent(row.baseline?.bias)}</td>
                        <td className="num">{formatQty(row.feature?.forecastQty)}</td>
                        <td className="num">{formatPercent(row.feature?.wape)}</td>
                        <td className="num">{formatQty(row.ma4?.forecastQty)}</td>
                        <td className="num">{formatPercent(row.ma4?.wape)}</td>
                        <td className="num">{formatQty(row.ma12?.forecastQty)}</td>
                        <td className="num">{formatPercent(row.ma12?.wape)}</td>
                        <td className="df-model-cell" title={row.winnerName}>{modelLabel(row.winnerName)}</td>
                        <td className="num">{formatPercent(row.winner?.wape)}</td>
                        <td className="num">{Number.isFinite(Number(row.baselineVsWinnerPts)) ? pts(Number(row.baselineVsWinnerPts).toFixed(1)) : '-'}</td>
                      </tr>
                    ))}
                    {!weeklyTrackingRecentRows.length && (
                      <tr>
                        <td colSpan="14" className="df-empty">{t('xDemand.wt.empty')}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="df-panel df-full">
              <div className="df-panel-head">
                <span>{t('xDemand.wmc.title')}</span>
                <div className="df-panel-actions">
                  <em>{t('xDemand.wmc.note')}</em>
                  <button type="button" onClick={exportWeeklyBacktest} disabled={!weeklyModelRows.length}>
                    <Download size={14} />
                    {t('xDemand.wmc.export')}
                  </button>
                </div>
              </div>
              <div className="df-table-wrap df-weekly-model-table">
                <table className="df-table">
                  <thead>
                    <tr>
                      <th>{t('xDemand.col.week')}</th>
                      <th className="num">{t('xDemand.col.actual')}</th>
                      <th>{t('xDemand.col.winner')}</th>
                      <th className="num">{t('xDemand.col.winnerWape')}</th>
                      <th className="num">{t('xDemand.col.hierForecast')}</th>
                      <th className="num">{t('xDemand.col.hierWape')}</th>
                      <th className="num">{t('xDemand.col.seasonalForecast')}</th>
                      <th className="num">{t('xDemand.col.seasonalWape')}</th>
                      <th className="num">{t('xDemand.col.v3Forecast')}</th>
                      <th className="num">V3 WAPE</th>
                      <th className="num">{t('xDemand.col.v4Forecast')}</th>
                      <th className="num">V4 WAPE</th>
                      <th className="num">{t('xDemand.col.v5Forecast')}</th>
                      <th className="num">V5 WAPE</th>
                      <th className="num">{t('xDemand.col.ma12Forecast')}</th>
                      <th className="num">MA12 WAPE</th>
                      <th className="num">{t('xDemand.col.naiveForecast')}</th>
                      <th className="num">{t('xDemand.col.naiveWape')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weeklyModelRows.map((row) => (
                      <tr key={`${row.targetYear}-${row.targetWeek}`}>
                        <td>{weekLabel(row)}</td>
                        <td className="num">{formatQty(row.actualQty)}</td>
                        <td className="df-model-cell" title={row.winningModel}>{modelLabel(row.winningModel)}</td>
                        <td className="num">{formatPercent(row.winningWape)}</td>
                        <td className="num">{formatQty(row.hierarchicalForecastQty)}</td>
                        <td className="num">{formatPercent(row.hierarchicalWape)}</td>
                        <td className="num">{formatQty(row.seasonalForecastQty)}</td>
                        <td className="num">{formatPercent(row.seasonalWape)}</td>
                        <td className="num">{formatQty(row.candidateV3ForecastQty)}</td>
                        <td className="num">{formatPercent(row.candidateV3Wape)}</td>
                        <td className="num">{formatQty(row.candidateV4ForecastQty)}</td>
                        <td className="num">{formatPercent(row.candidateV4Wape)}</td>
                        <td className="num">{formatQty(row.candidateV5ForecastQty)}</td>
                        <td className="num">{formatPercent(row.candidateV5Wape)}</td>
                        <td className="num">{formatQty(row.movingAverage12ForecastQty)}</td>
                        <td className="num">{formatPercent(row.movingAverage12Wape)}</td>
                        <td className="num">{formatQty(row.naiveForecastQty)}</td>
                        <td className="num">{formatPercent(row.naiveWape)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="df-panel df-full">
              <div className="df-panel-head">
                <span>{t('xDemand.diag.title')}</span>
                <em>{t('xDemand.diag.note')}</em>
              </div>
              <div className="df-diagnostic-summary">
                <div>
                  <span>{t('xDemand.v.v3Beats')}</span>
                  <strong>{formatQty(candidateV3WinWeeks)}</strong>
                  <em>{t('xDemand.u.weeks')}</em>
                </div>
                <div>
                  <span>{t('xDemand.v.close')}</span>
                  <strong>{formatQty(candidateV3CloseWeeks)}</strong>
                  <em>{t('xDemand.diag.within')}</em>
                </div>
                <div>
                  <span>{t('xDemand.diag.v3Worse')}</span>
                  <strong>{formatQty(candidateV3WorseWeeks)}</strong>
                  <em>{t('xDemand.u.weeks')}</em>
                </div>
                <div>
                  <span>{t('xDemand.diag.avgGap')}</span>
                  <strong>{formatPercent(candidateV3AvgGap)}</strong>
                  <em>{t('xDemand.diag.v3Minus')}</em>
                </div>
                <div>
                  <span>{t('xDemand.diag.v4Beats')}</span>
                  <strong>{formatQty(candidateV4WinWeeks)}</strong>
                  <em>{t('xDemand.u.weeks')}</em>
                </div>
                <div>
                  <span>{t('xDemand.diag.v4Worse')}</span>
                  <strong>{formatQty(candidateV4WorseWeeks)}</strong>
                  <em>{t('xDemand.u.weeks')}</em>
                </div>
                <div>
                  <span>{t('xDemand.diag.v4AvgGap')}</span>
                  <strong>{formatPercent(candidateV4AvgGap)}</strong>
                  <em>{t('xDemand.diag.v4Minus')}</em>
                </div>
                <div>
                  <span>{t('xDemand.diag.v5Beats')}</span>
                  <strong>{formatQty(candidateV5WinWeeks)}</strong>
                  <em>{t('xDemand.u.weeks')}</em>
                </div>
                <div>
                  <span>{t('xDemand.diag.v5Worse')}</span>
                  <strong>{formatQty(candidateV5WorseWeeks)}</strong>
                  <em>{t('xDemand.u.weeks')}</em>
                </div>
                <div>
                  <span>{t('xDemand.diag.v5AvgGap')}</span>
                  <strong>{formatPercent(candidateV5AvgGap)}</strong>
                  <em>{t('xDemand.diag.v5Minus')}</em>
                </div>
              </div>
              <div className="df-table-wrap df-v3-diagnostic-table">
                <table className="df-table">
                  <thead>
                    <tr>
                      <th>{t('xDemand.col.week')}</th>
                      <th className="num">{t('xDemand.col.actual')}</th>
                      <th className="num">{t('xDemand.col.currentForecast')}</th>
                      <th className="num">{t('xDemand.col.currentWape')}</th>
                      <th className="num">{t('xDemand.col.v3Forecast')}</th>
                      <th className="num">V3 WAPE</th>
                      <th className="num">{t('xDemand.col.gap')}</th>
                      <th className="num">{t('xDemand.col.v4Forecast')}</th>
                      <th className="num">V4 WAPE</th>
                      <th className="num">{t('xDemand.col.v4Gap')}</th>
                      <th className="num">{t('xDemand.col.v5Forecast')}</th>
                      <th className="num">V5 WAPE</th>
                      <th className="num">{t('xDemand.col.v5Gap')}</th>
                      <th>{t('xDemand.col.outcome')}</th>
                      <th>{t('xDemand.col.diagnosis')}</th>
                      <th>{t('xDemand.col.v4Diagnosis')}</th>
                      <th>{t('xDemand.col.v5Diagnosis')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidateV3Diagnostics.map((row) => {
                      const gap = Number(row.v3VsCurrentWapeGap);
                      const tone = Number.isFinite(gap) && gap < -0.005
                        ? 'ok'
                        : Number.isFinite(gap) && gap > 0.005
                          ? 'review'
                          : 'watch';
                      const v4Gap = Number(row.v4VsCurrentWapeGap);
                      const v4Tone = Number.isFinite(v4Gap) && v4Gap < -0.005
                        ? 'ok'
                        : Number.isFinite(v4Gap) && v4Gap > 0.005
                          ? 'review'
                          : 'watch';
                      const v5Gap = Number(row.v5VsCurrentWapeGap);
                      const v5Tone = Number.isFinite(v5Gap) && v5Gap < -0.005
                        ? 'ok'
                        : Number.isFinite(v5Gap) && v5Gap > 0.005
                          ? 'review'
                          : 'watch';
                      return (
                        <tr key={`${row.targetYear}-${row.targetWeek}`}>
                          <td>{weekLabel(row)}</td>
                          <td className="num">{formatQty(row.actualQty)}</td>
                          <td className="num">{formatQty(row.currentForecastQty)}</td>
                          <td className="num">{formatPercent(row.currentWape)}</td>
                          <td className="num">{formatQty(row.candidateV3ForecastQty)}</td>
                          <td className="num">{formatPercent(row.candidateV3Wape)}</td>
                          <td className={`num df-gap-${tone}`}>{formatPercent(row.v3VsCurrentWapeGap)}</td>
                          <td className="num">{formatQty(row.candidateV4ForecastQty)}</td>
                          <td className="num">{formatPercent(row.candidateV4Wape)}</td>
                          <td className={`num df-gap-${v4Tone}`}>{formatPercent(row.v4VsCurrentWapeGap)}</td>
                          <td className="num">{formatQty(row.candidateV5ForecastQty)}</td>
                          <td className="num">{formatPercent(row.candidateV5Wape)}</td>
                          <td className={`num df-gap-${v5Tone}`}>{formatPercent(row.v5VsCurrentWapeGap)}</td>
                          <td><span className={`df-status ${tone}`}>{tv(row.v3Outcome) || '-'}</span></td>
                          <td className="df-diagnosis-cell">{row.diagnosis || '-'}</td>
                          <td className="df-diagnosis-cell">{row.v4Diagnosis || '-'}</td>
                          <td className="df-diagnosis-cell">{row.v5Diagnosis || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="df-panel df-full">
              <div className="df-panel-head">
                <span>{t('xDemand.hyb.title')}</span>
                <em>{t('xDemand.hyb.note')}</em>
              </div>
              <div className="df-hybrid-grid">
                <div className="df-hybrid-summary">
                  <span>{t('xDemand.hyb.wape')}</span>
                  <strong>{formatPercent(hybridSimulation?.wape)}</strong>
                  <em>
                    {Number.isFinite(hybridImprovement)
                      ? t('xDemand.hyb.better', { n: (hybridImprovement * 100).toFixed(1) })
                      : t('xDemand.hyb.check')}
                  </em>
                  <p>{t('xDemand.hyb.text')}</p>
                </div>
                <div className="df-win-rate">
                  {modelWinRows.map((row) => {
                    const totalWeeks = Number(hybridSimulation?.weeks || backtestWeeks.length || 1);
                    const share = totalWeeks > 0 ? Number(row.winWeeks || 0) / totalWeeks : 0;
                    return (
                      <div key={row.modelName}>
                        <div>
                          <strong>{modelLabel(row.modelName)}</strong>
                          <span>{t('xDemand.hyb.winningWeeks', { n: formatQty(row.winWeeks) })}</span>
                        </div>
                        <div className="df-bar-track">
                          <span style={{ width: `${Math.max(4, share * 100)}%` }} />
                        </div>
                        <em>{formatPercent(share)}</em>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="df-panel df-full">
              <div className="df-panel-head">
                <span>{t('xDemand.rule.title')}</span>
                <em>{t('xDemand.rule.note')}</em>
              </div>
              <div className={`df-validation-card ${validationStatus.tone}`}>
                <div>
                  <span>{t('xDemand.rule.validationStatus')}</span>
                  <strong>{tv(validationStatus.label)}</strong>
                </div>
                <p>{validationStatus.detail}</p>
                <div>
                  <span>{t('xDemand.rule.upperGap')}</span>
                  <strong>{Number.isFinite(upperBoundGap) ? pts((upperBoundGap * 100).toFixed(1)) : '-'}</strong>
                </div>
              </div>
              <div className="df-rule-hybrid">
                <div className="df-rule-summary">
                  <div>
                    <span>{t('xDemand.col.ruleWape')}</span>
                    <strong>{formatPercent(ruleHybridSimulation?.wape)}</strong>
                    <em>
                      {Number.isFinite(ruleHybridImprovement)
                        ? t('xDemand.rule.vsRecommended', { n: (ruleHybridImprovement * 100).toFixed(1) })
                        : t('xDemand.rule.check')}
                    </em>
                  </div>
                  <div>
                    <span>{t('xDemand.col.ruleBias')}</span>
                    <strong>{formatPercent(ruleHybridSimulation?.bias)}</strong>
                    <em>{t('xDemand.rule.modelsUsed', { n: formatQty(ruleHybridSimulation?.modelsUsed) })}</em>
                  </div>
                  <p>{t('xDemand.rule.text')}</p>
                </div>
                <div className="df-table-wrap df-rule-table">
                  <table className="df-table">
                    <thead>
                      <tr>
                        <th>{t('xDemand.col.week')}</th>
                        <th className="num">{t('xDemand.col.actual')}</th>
                        <th>{t('xDemand.col.chosenModel')}</th>
                        <th className="num">{t('xDemand.col.forecast')}</th>
                        <th className="num">WAPE</th>
                        <th className="num">{t('xDemand.col.bias')}</th>
                        <th className="num">{t('xDemand.col.recentCv4')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ruleHybridRows.map((row) => (
                        <tr key={`${row.targetYear}-${row.targetWeek}`}>
                          <td>{weekLabel(row)}</td>
                          <td className="num">{formatQty(row.actualQty)}</td>
                          <td className="df-model-cell" title={row.chosenModel}>{modelLabel(row.chosenModel)}</td>
                          <td className="num">{formatQty(row.forecastQty)}</td>
                          <td className="num">{formatPercent(row.wape)}</td>
                          <td className="num">{formatPercent(row.bias)}</td>
                          <td className="num">{formatPercent(row.recentCv4)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="df-panel df-full">
              <div className="df-panel-head">
                <span>{t('xDemand.roll.title')}</span>
                <em>
                  {t('xDemand.roll.note', {
                    v4: formatQty(rollingV4Wins),
                    v5: formatQty(rollingV5Wins),
                    rule: formatQty(rollingRuleWins),
                    n: formatQty(rollingRows.length),
                  })}
                </em>
              </div>
              <div className="df-diagnostic-summary">
                <div>
                  <span>{t('xDemand.roll.v4Wins')}</span>
                  <strong>{formatQty(rollingV4Wins)}</strong>
                  <em>{t('xDemand.u.ofWindows', { n: formatQty(rollingRows.length) })}</em>
                </div>
                <div>
                  <span>{t('xDemand.diag.v4AvgGap')}</span>
                  <strong>{formatPercent(rollingV4AvgGap)}</strong>
                  <em>{t('xDemand.diag.v4Minus')}</em>
                </div>
                <div>
                  <span>{t('xDemand.roll.v5Wins')}</span>
                  <strong>{formatQty(rollingV5Wins)}</strong>
                  <em>{t('xDemand.u.ofWindows', { n: formatQty(rollingRows.length) })}</em>
                </div>
                <div>
                  <span>{t('xDemand.diag.v5AvgGap')}</span>
                  <strong>{formatPercent(rollingV5AvgGap)}</strong>
                  <em>{t('xDemand.diag.v5Minus')}</em>
                </div>
                <div>
                  <span>{t('xDemand.roll.ruleWins')}</span>
                  <strong>{formatQty(rollingRuleWins)}</strong>
                  <em>{t('xDemand.u.ofWindows', { n: formatQty(rollingRows.length) })}</em>
                </div>
                <div>
                  <span>{t('xDemand.roll.ruleAvgGap')}</span>
                  <strong>{Number.isFinite(rollingAvgGap) ? pts((rollingAvgGap * 100).toFixed(1)) : '-'}</strong>
                  <em>{t('xDemand.roll.ruleMinusBest')}</em>
                </div>
              </div>
              <div className="df-table-wrap df-rolling-table">
                <table className="df-table">
                  <thead>
                    <tr>
                      <th>{t('xDemand.col.window')}</th>
                      <th>{t('xDemand.col.bestModel')}</th>
                      <th className="num">{t('xDemand.col.ruleWape')}</th>
                      <th className="num">{t('xDemand.col.bestSingleWape')}</th>
                      <th className="num">{t('xDemand.col.gap')}</th>
                      <th className="num">{t('xDemand.col.ruleBias')}</th>
                      <th className="num">{t('xDemand.col.hierWape')}</th>
                      <th className="num">{t('xDemand.col.seasonalWape')}</th>
                      <th className="num">V4 WAPE</th>
                      <th className="num">{t('xDemand.col.v4VsCurrent')}</th>
                      <th className="num">V5 WAPE</th>
                      <th className="num">{t('xDemand.col.v5VsCurrent')}</th>
                      <th className="num">MA12 WAPE</th>
                      <th className="num">{t('xDemand.col.naiveWape')}</th>
                      <th>{t('xDemand.col.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rollingRows.map((row) => {
                      const gap = Number(row.ruleVsBestSingleGap);
                      const status = row.bestModel === 'hybrid_rule_v2'
                        ? 'Rule wins'
                        : gap <= 0.03
                          ? 'Close'
                          : 'Review';
                      return (
                        <tr key={row.windowSeq}>
                          <td>{row.windowLabel}</td>
                          <td className="df-model-cell" title={row.bestModel}>{modelLabel(row.bestModel)}</td>
                          <td className="num">{formatPercent(row.ruleWape)}</td>
                          <td className="num">{formatPercent(row.bestSingleWape)}</td>
                          <td className="num">{Number.isFinite(gap) ? pts((gap * 100).toFixed(1)) : '-'}</td>
                          <td className="num">{formatPercent(row.ruleBias)}</td>
                          <td className="num">{formatPercent(row.hierarchicalWape)}</td>
                          <td className="num">{formatPercent(row.seasonalWape)}</td>
                          <td className="num">{formatPercent(row.candidateV4Wape)}</td>
                          <td className="num">{Number.isFinite(Number(row.candidateV4VsCurrentGap)) ? pts((Number(row.candidateV4VsCurrentGap) * 100).toFixed(1)) : '-'}</td>
                          <td className="num">{formatPercent(row.candidateV5Wape)}</td>
                          <td className="num">{Number.isFinite(Number(row.candidateV5VsCurrentGap)) ? pts((Number(row.candidateV5VsCurrentGap) * 100).toFixed(1)) : '-'}</td>
                          <td className="num">{formatPercent(row.movingAverage12Wape)}</td>
                          <td className="num">{formatPercent(row.naiveWape)}</td>
                          <td><span className={`df-status ${status.toLowerCase().replace(' ', '-')}`}>{tv(status)}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="df-panel">
              <div className="df-panel-head">
                <span>{t('xDemand.acc.title')}</span>
                <em>{t('xDemand.acc.note')}</em>
              </div>
              <div className="df-accuracy">
                {backtestSummary.map((row) => (
                  <div key={row.level}>
                    <span>{levelLabel(row.level)}</span>
                    <strong>{formatPercent(row.wape)}</strong>
                    <em>{t('xDemand.acc.bias', { v: formatPercent(row.bias) })}</em>
                    <p>{backtestLevelDescriptions[row.level] || t('xDemand.lvd.other')}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="df-panel df-wide">
              <div className="df-panel-head">
                <span>{t('xDemand.wavf.title')}</span>
                <em>{t('xDemand.wavf.note')}</em>
              </div>
              <div className="df-chart df-backtest-chart">
                <ResponsiveContainer>
                  <ComposedChart data={backtestWeeks} margin={{ top: 10, right: 18, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="qty" tickFormatter={(value) => formatQty(value)} width={58} tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="pct" orientation="right" tickFormatter={(value) => `${Number(value).toFixed(0)}%`} width={48} tick={{ fontSize: 11 }} />
                    <Tooltip
                      formatter={(value, name) => {
                        if (name === 'WAPE') return `${Number(value).toFixed(1)}%`;
                        return formatQty(value);
                      }}
                    />
                    <Legend verticalAlign="top" height={24} />
                    <Bar yAxisId="qty" name={t('xDemand.col.actual')} dataKey="actualQty" fill="#4b5563" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="qty" name={t('xDemand.col.forecast')} dataKey="forecastQty" fill="#156082" radius={[4, 4, 0, 0]} />
                    <Line yAxisId="pct" name="WAPE" dataKey="wapePct" stroke="#c55a11" strokeWidth={2.5} dot={{ r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="df-panel df-full">
              <div className="df-panel-head">
                <span>{t('xDemand.feat.weeklyDetail')}</span>
                <em>{t('xDemand.wbd.note')}</em>
              </div>
              <div className="df-table-wrap df-backtest-table">
                <table className="df-table">
                  <thead>
                    <tr>
                      <th>{t('xDemand.col.model')}</th>
                      <th>{t('xDemand.col.year')}</th>
                      <th>{t('xDemand.col.week')}</th>
                      <th className="num">{t('xDemand.col.actualErp')}</th>
                      <th className="num">{t('xDemand.col.forecastErp')}</th>
                      <th className="num">{t('xDemand.col.absError')}</th>
                      <th className="num">WAPE</th>
                      <th className="num">{t('xDemand.col.bias')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {backtestWeeks.map((row) => (
                      <tr key={`${row.targetYear}-${row.targetWeek}`}>
                        <td className="df-model-cell" title={row.modelName || backtestModelName}>
                          {modelLabel(row.modelName || backtestModelName)}
                        </td>
                        <td>{row.targetYear}</td>
                        <td>{row.label}</td>
                        <td className="num">{formatQty(row.actualQty)}</td>
                        <td className="num">{formatQty(row.forecastQty)}</td>
                        <td className="num">{formatQty(row.absError)}</td>
                        <td className="num">{formatPercent(row.wape)}</td>
                        <td className="num">{formatPercent(row.bias)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {(loading || refreshing) && <div className="df-loading"><BarChart3 size={18} /> {t('xDemand.top.loading')}</div>}
      </main>
    </div>
  );
};

export default DemandForecastDashboard;
